/**
 * Internationalization (i18n) module for the Accessibility Menu Widget.
 *
 * Provides translation strings for all UI labels, helper functions for
 * language detection, RTL support, and a registration mechanism for adding
 * new languages at runtime.
 *
 * @module i18n
 */

// ---------------------------------------------------------------------------
// Translation registry
// ---------------------------------------------------------------------------

/**
 * Built-in translations keyed by BCP-47 language code.
 * Each language object maps a translation key to a localised string.
 *
 * @type {Record<string, Record<string, string>>}
 */
const translations = {
  en: {
    nativeName: 'English',
    menuTitle: 'Accessibility Menu',
    highContrast: 'High Contrast',
    darkMode: 'Dark Mode',
    fontSize: 'Font Size',
    increaseFontSize: 'Increase Font Size',
    decreaseFontSize: 'Decrease Font Size',
    resetFontSize: 'Reset Font Size',
    dyslexiaFont: 'Dyslexia Friendly Font',
    underlineLinks: 'Underline Links',
    focusOutline: 'Focus Outline',
    hideImages: 'Hide Images',
    textSpacing: 'Text Spacing',
    pauseAnimations: 'Pause Animations',
    largeCursor: 'Large Cursor',
    highlightHeadings: 'Highlight Headings',
    invertColors: 'Invert Colors',
    readingGuide: 'Reading Guide',
    textToSpeech: 'Text to Speech',
    focusMode: 'Focus Mode',
    lineHeight: 'Line Height',
    letterSpacing: 'Letter Spacing',
    wordSpacing: 'Word Spacing',
    visual: 'Visual',
    content: 'Content',
    navigation: 'Navigation',
    increaseLineHeight: 'Increase Line Height',
    decreaseLineHeight: 'Decrease Line Height',
    increaseLetterSpacing: 'Increase Letter Spacing',
    decreaseLetterSpacing: 'Decrease Letter Spacing',
    increaseWordSpacing: 'Increase Word Spacing',
    decreaseWordSpacing: 'Decrease Word Spacing',
    deuteranopia: 'Deuteranopia (Green-Blind)',
    protanopia: 'Protanopia (Red-Blind)',
    tritanopia: 'Tritanopia (Blue-Blind)',
    reducedTransparency: 'Reduced Transparency',
    sensoryFriendly: 'Sensory Friendly',
    readableFont: 'Readable Font',
    saturation: 'Saturation',
    increaseSaturation: 'Increase Saturation',
    decreaseSaturation: 'Decrease Saturation',
    brightness: 'Brightness',
    increaseBrightness: 'Increase Brightness',
    decreaseBrightness: 'Decrease Brightness',
    suppressNotifications: 'Suppress Notifications',
    highlightHover: 'Highlight on Hover',
    ttsPause: 'Pause Reading',
    ttsResume: 'Resume Reading',
    ttsSpeed: 'Reading Speed',
    ttsSlower: 'Slower',
    ttsFaster: 'Faster',
    devAuditBadge: 'Dev Audit',
    devAuditNoIssues: 'No alt text issues found',
    devAuditIssues: 'alt text issues found',
    quickStart: 'Quick Start',
    presetLowVision: 'Low Vision',
    presetDyslexia: 'Dyslexia Friendly',
    presetAdhd: 'ADHD / Cognitive',
    presetMotor: 'Motor / Keyboard',
    presetMigraine: 'Migraine Safe',
    zoomLockWarning: '\u26A0\uFE0F This page has disabled pinch-to-zoom. Users who rely on browser zoom may have difficulty accessing content.',
    position: 'Widget Position',
    topLeft: 'Top Left',
    topRight: 'Top Right',
    bottomLeft: 'Bottom Left',
    bottomRight: 'Bottom Right',
    profiles: 'Profiles',
    profileNamePlaceholder: 'Profile name\u2026',
    saveProfile: 'Save',
    loadProfile: 'Load',
    deleteProfile: 'Delete',
    noProfiles: 'No saved profiles',
    settingsActive: 'settings active',
    resetConfirmation: 'All accessibility settings have been reset',
    featureEnabled: 'enabled',
    featureDisabled: 'disabled',
    tooltipMessage: 'Customize your accessibility settings',
    resetAll: 'Reset All',
    closeMenu: 'Close Menu',
    language: 'Language',
    disclaimer:
      'This widget does not guarantee full accessibility compliance.',
    accessibilityStatementLink: 'Accessibility Statement',
  },

  he: {
    nativeName: '\u05E2\u05D1\u05E8\u05D9\u05EA',
    menuTitle: '\u05EA\u05E4\u05E8\u05D9\u05D8 \u05E0\u05D2\u05D9\u05E9\u05D5\u05EA',
    highContrast: '\u05E0\u05D9\u05D2\u05D5\u05D3\u05D9\u05D5\u05EA \u05D2\u05D1\u05D5\u05D4\u05D4',
    darkMode: '\u05DE\u05E6\u05D1 \u05DB\u05D4\u05D4',
    fontSize: '\u05D2\u05D5\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF',
    increaseFontSize: '\u05D4\u05D2\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF',
    decreaseFontSize: '\u05D4\u05E7\u05D8\u05DF \u05D2\u05D5\u05E4\u05DF',
    resetFontSize: '\u05D0\u05E4\u05E1 \u05D2\u05D5\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF',
    dyslexiaFont: '\u05D2\u05D5\u05E4\u05DF \u05D9\u05D3\u05D9\u05D3\u05D5\u05EA\u05D9 \u05DC\u05D3\u05D9\u05E1\u05DC\u05E7\u05E6\u05D9\u05D4',
    underlineLinks: '\u05E7\u05D5 \u05EA\u05D7\u05EA\u05D5\u05DF \u05DC\u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD',
    focusOutline: '\u05DE\u05E1\u05D2\u05E8\u05EA \u05DE\u05D9\u05E7\u05D5\u05D3',
    hideImages: '\u05D4\u05E1\u05EA\u05E8 \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA',
    textSpacing: '\u05E8\u05D9\u05D5\u05D5\u05D7 \u05D8\u05E7\u05E1\u05D8',
    pauseAnimations: '\u05D4\u05E9\u05D4\u05D4 \u05D0\u05E0\u05D9\u05DE\u05E6\u05D9\u05D5\u05EA',
    largeCursor: '\u05E1\u05DE\u05DF \u05D2\u05D3\u05D5\u05DC',
    highlightHeadings: '\u05D4\u05D3\u05D2\u05E9 \u05DB\u05D5\u05EA\u05E8\u05D5\u05EA',
    invertColors: '\u05D4\u05D9\u05E4\u05D5\u05DA \u05E6\u05D1\u05E2\u05D9\u05DD',
    readingGuide: '\u05DE\u05D3\u05E8\u05D9\u05DA \u05E7\u05E8\u05D9\u05D0\u05D4',
    textToSpeech: '\u05D4\u05E7\u05E8\u05D0\u05D4 \u05D1\u05E7\u05D5\u05DC',
    ttsPause: '\u05E2\u05E6\u05D5\u05E8 \u05E7\u05E8\u05D9\u05D0\u05D4',
    ttsResume: '\u05D4\u05DE\u05E9\u05DA \u05E7\u05E8\u05D9\u05D0\u05D4',
    ttsSpeed: '\u05DE\u05D4\u05D9\u05E8\u05D5\u05EA \u05E7\u05E8\u05D9\u05D0\u05D4',
    ttsSlower: '\u05D0\u05D9\u05D8\u05D9 \u05D9\u05D5\u05EA\u05E8',
    ttsFaster: '\u05DE\u05D4\u05D9\u05E8 \u05D9\u05D5\u05EA\u05E8',
    devAuditBadge: '\u05D1\u05D3\u05D9\u05E7\u05EA \u05E0\u05D2\u05D9\u05E9\u05D5\u05EA',
    devAuditNoIssues: '\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05D1\u05E2\u05D9\u05D5\u05EA alt',
    devAuditIssues: '\u05D1\u05E2\u05D9\u05D5\u05EA alt \u05E0\u05DE\u05E6\u05D0\u05D5',
    focusMode: 'מצב פוקוס',
    lineHeight: 'גובה שורה',
    letterSpacing: 'מרווח אותיות',
    wordSpacing: 'מרווח מילים',
    visual: 'חזותי',
    content: 'תוכן',
    navigation: 'ניווט',
    increaseLineHeight: 'הגדל גובה שורה',
    decreaseLineHeight: 'הקטן גובה שורה',
    increaseLetterSpacing: 'הגדל מרווח אותיות',
    decreaseLetterSpacing: 'הקטן מרווח אותיות',
    increaseWordSpacing: 'הגדל מרווח מילים',
    decreaseWordSpacing: 'הקטן מרווח מילים',
    deuteranopia: '\u05D3\u05D9\u05D9\u05D8\u05E8\u05E0\u05D5\u05E4\u05D9\u05D4 (\u05E2\u05D9\u05D5\u05D5\u05E8 \u05D9\u05E8\u05D5\u05E7)',
    protanopia: '\u05E4\u05E8\u05D5\u05D8\u05E0\u05D5\u05E4\u05D9\u05D4 (\u05E2\u05D9\u05D5\u05D5\u05E8 \u05D0\u05D3\u05D5\u05DD)',
    tritanopia: '\u05D8\u05E8\u05D9\u05D8\u05E0\u05D5\u05E4\u05D9\u05D4 (\u05E2\u05D9\u05D5\u05D5\u05E8 \u05DB\u05D7\u05D5\u05DC)',
    reducedTransparency: '\u05E9\u05E7\u05D9\u05E4\u05D5\u05EA \u05DE\u05D5\u05E4\u05D7\u05EA\u05EA',
    sensoryFriendly: '\u05D9\u05D3\u05D9\u05D3\u05D5\u05EA\u05D9 \u05DC\u05D7\u05D5\u05E9\u05D9\u05DD',
    readableFont: '\u05D2\u05D5\u05E4\u05DF \u05E7\u05E8\u05D9\u05D0',
    saturation: '\u05E8\u05D5\u05D5\u05D9\u05D9\u05D4',
    increaseSaturation: '\u05D4\u05D2\u05D1\u05E8 \u05E8\u05D5\u05D5\u05D9\u05D9\u05D4',
    decreaseSaturation: '\u05D4\u05E4\u05D7\u05EA \u05E8\u05D5\u05D5\u05D9\u05D9\u05D4',
    brightness: '\u05D1\u05D4\u05D9\u05E8\u05D5\u05EA',
    increaseBrightness: '\u05D4\u05D2\u05D1\u05E8 \u05D1\u05D4\u05D9\u05E8\u05D5\u05EA',
    decreaseBrightness: '\u05D4\u05E4\u05D7\u05EA \u05D1\u05D4\u05D9\u05E8\u05D5\u05EA',
    suppressNotifications: '\u05D3\u05D9\u05DB\u05D5\u05D9 \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA',
    highlightHover: '\u05D4\u05D3\u05D2\u05E9\u05EA \u05E2\u05D9\u05D5\u05E3',
    quickStart: '\u05D4\u05EA\u05D7\u05DC\u05D4 \u05DE\u05D4\u05D9\u05E8\u05D4',
    presetLowVision: '\u05E8\u05D0\u05D9\u05D9\u05D4 \u05D7\u05DC\u05E9\u05D4',
    presetDyslexia: '\u05D9\u05D3\u05D9\u05D3\u05D5\u05EA\u05D9 \u05DC\u05D3\u05D9\u05E1\u05DC\u05E7\u05E6\u05D9\u05D4',
    presetAdhd: '\u05D4\u05E4\u05E8\u05E2\u05EA \u05E7\u05E9\u05D1 / \u05E7\u05D5\u05D2\u05E0\u05D9\u05D8\u05D9\u05D1\u05D9',
    presetMotor: '\u05DE\u05D5\u05D8\u05D5\u05E8\u05D9 / \u05DE\u05E7\u05DC\u05D3\u05EA',
    presetMigraine: '\u05D1\u05D8\u05D5\u05D7 \u05DC\u05DE\u05D9\u05D2\u05E8\u05E0\u05D4',
    zoomLockWarning: '\u26A0\uFE0F \u05D3\u05E3 \u05D6\u05D4 \u05D1\u05D9\u05D8\u05DC \u05D0\u05EA \u05D4\u05D6\u05D5\u05DD. \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD \u05D4\u05E1\u05D5\u05DE\u05DB\u05D9\u05DD \u05E2\u05DC \u05D4\u05D2\u05D3\u05DC\u05EA \u05D3\u05E4\u05D3\u05E4\u05DF \u05E2\u05E9\u05D5\u05D9\u05D9\u05DD \u05DC\u05D4\u05EA\u05E7\u05E9\u05D5\u05EA.',
    position: '\u05DE\u05D9\u05E7\u05D5\u05DD \u05D4\u05EA\u05E4\u05E8\u05D9\u05D8',
    topLeft: '\u05E9\u05DE\u05D0\u05DC \u05E2\u05DC\u05D9\u05D5\u05DF',
    topRight: '\u05D9\u05DE\u05D9\u05DF \u05E2\u05DC\u05D9\u05D5\u05DF',
    bottomLeft: '\u05E9\u05DE\u05D0\u05DC \u05EA\u05D7\u05EA\u05D5\u05DF',
    bottomRight: '\u05D9\u05DE\u05D9\u05DF \u05EA\u05D7\u05EA\u05D5\u05DF',
    profiles: '\u05E4\u05E8\u05D5\u05E4\u05D9\u05DC\u05D9\u05DD',
    profileNamePlaceholder: '\u05E9\u05DD \u05E4\u05E8\u05D5\u05E4\u05D9\u05DC\u2026',
    saveProfile: '\u05E9\u05DE\u05D5\u05E8',
    loadProfile: '\u05D8\u05E2\u05DF',
    deleteProfile: '\u05DE\u05D7\u05E7',
    noProfiles: '\u05D0\u05D9\u05DF \u05E4\u05E8\u05D5\u05E4\u05D9\u05DC\u05D9\u05DD \u05E9\u05DE\u05D5\u05E8\u05D9\u05DD',
    settingsActive: '\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA',
    resetConfirmation: '\u05DB\u05DC \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05D4\u05E0\u05D2\u05D9\u05E9\u05D5\u05EA \u05D0\u05D5\u05E4\u05E1\u05D5',
    featureEnabled: '\u05DE\u05D5\u05E4\u05E2\u05DC',
    featureDisabled: '\u05DE\u05D5\u05E9\u05D1\u05EA',
    tooltipMessage: '\u05D4\u05EA\u05D0\u05DD \u05D0\u05D9\u05E9\u05D9\u05EA \u05D0\u05EA \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05D4\u05E0\u05D2\u05D9\u05E9\u05D5\u05EA \u05E9\u05DC\u05DA',
    resetAll: '\u05D0\u05E4\u05E1 \u05D4\u05DB\u05DC',
    closeMenu: '\u05E1\u05D2\u05D5\u05E8 \u05EA\u05E4\u05E8\u05D9\u05D8',
    language: '\u05E9\u05E4\u05D4',
    disclaimer:
      '\u05EA\u05D5\u05E1\u05E3 \u05D6\u05D4 \u05D0\u05D9\u05E0\u05D5 \u05DE\u05D1\u05D8\u05D9\u05D7 \u05E2\u05DE\u05D9\u05D3\u05D4 \u05DE\u05DC\u05D0\u05D4 \u05D1\u05EA\u05E7\u05E0\u05D9 \u05E0\u05D2\u05D9\u05E9\u05D5\u05EA.',
    accessibilityStatementLink: '\u05D4\u05E6\u05D4\u05E8\u05EA \u05E0\u05D2\u05D9\u05E9\u05D5\u05EA',
  },

  zh: {
    nativeName: '\u4E2D\u6587',
    menuTitle: '\u65E0\u969C\u788D\u83DC\u5355',
    highContrast: '\u9AD8\u5BF9\u6BD4\u5EA6',
    darkMode: '\u6DF1\u8272\u6A21\u5F0F',
    fontSize: '\u5B57\u4F53\u5927\u5C0F',
    increaseFontSize: '\u589E\u5927\u5B57\u4F53',
    decreaseFontSize: '\u51CF\u5C0F\u5B57\u4F53',
    resetFontSize: '\u91CD\u7F6E\u5B57\u4F53\u5927\u5C0F',
    dyslexiaFont: '\u9605\u8BFB\u969C\u788D\u53CB\u597D\u5B57\u4F53',
    underlineLinks: '\u94FE\u63A5\u4E0B\u5212\u7EBF',
    focusOutline: '\u7126\u70B9\u8F6E\u5ED3',
    hideImages: '\u9690\u85CF\u56FE\u7247',
    textSpacing: '\u6587\u5B57\u95F4\u8DDD',
    pauseAnimations: '\u6682\u505C\u52A8\u753B',
    largeCursor: '\u5927\u5149\u6807',
    highlightHeadings: '\u9AD8\u4EAE\u6807\u9898',
    invertColors: '\u53CD\u8F6C\u989C\u8272',
    readingGuide: '\u9605\u8BFB\u5F15\u5BFC',
    textToSpeech: '\u6587\u5B57\u8F6C\u8BED\u97F3',
    focusMode: '专注模式',
    lineHeight: '行高',
    letterSpacing: '字间距',
    wordSpacing: '字词间距',
    visual: '视觉',
    content: '内容',
    navigation: '导航',
    deuteranopia: '绿色盲',
    protanopia: '红色盲',
    tritanopia: '蓝色盲',
    highlightHover: '悬停高亮',
    ttsPause: '暂停服读',
    ttsResume: '继续服读',
    ttsSpeed: '服读速度',
    ttsSlower: '更慢',
    ttsFaster: '更快',
    quickStart: '快速开始',
    presetLowVision: '弱视',
    presetDyslexia: '阅读障碍友好',
    presetAdhd: '注意力缺陷/认知',
    presetMotor: '运动/键盘',
    presetMigraine: '偶头痛安全',
    settingsActive: '设置激活中',
    resetConfirmation: '所有无障碍设置已重置',
    featureEnabled: '已启用',
    featureDisabled: '已禁用',
    tooltipMessage: '自定义您的无障碍设置',
    increaseLineHeight: "增加行高",
    decreaseLineHeight: "减少行高",
    increaseLetterSpacing: "增加字间距",
    decreaseLetterSpacing: "减少字间距",
    increaseWordSpacing: "增加字词间距",
    decreaseWordSpacing: "减少字词间距",
    reducedTransparency: "降低透明度",
    sensoryFriendly: "感官友好",
    readableFont: "可读字体",
    saturation: "饱和度",
    increaseSaturation: "增加饱和度",
    decreaseSaturation: "降低饱和度",
    brightness: "亮度",
    increaseBrightness: "增加亮度",
    decreaseBrightness: "降低亮度",
    suppressNotifications: "抑制通知",
    devAuditBadge: "开发审计",
    devAuditNoIssues: "未发现 alt 文本问题",
    devAuditIssues: "个 alt 文本问题",
    zoomLockWarning: "⚠️ 此页面已禁用缩放手势。依赖浏览器缩放的用户可能难以访问内容。",
    position: "小组件位置",
    topLeft: "左上",
    topRight: "右上",
    bottomLeft: "左下",
    bottomRight: "右下",
    profiles: "配置文件",
    profileNamePlaceholder: "配置文件名称…",
    saveProfile: "保存",
    loadProfile: "加载",
    deleteProfile: "删除",
    noProfiles: "没有已保存的配置文件",
    accessibilityStatementLink: "无障碍声明",
    resetAll: '\u91CD\u7F6E\u5168\u90E8',
    closeMenu: '\u5173\u95ED\u83DC\u5355',
    language: '\u8BED\u8A00',
    disclaimer:
      '\u6B64\u5C0F\u7EC4\u4EF6\u4E0D\u4FDD\u8BC1\u5B8C\u5168\u7B26\u5408\u65E0\u969C\u788D\u6807\u51C6\u3002',
  },

  es: {
    nativeName: 'Espa\u00F1ol',
    menuTitle: 'Men\u00FA de accesibilidad',
    highContrast: 'Alto contraste',
    darkMode: 'Modo oscuro',
    fontSize: 'Tama\u00F1o de fuente',
    increaseFontSize: 'Aumentar fuente',
    decreaseFontSize: 'Reducir fuente',
    resetFontSize: 'Restablecer fuente',
    dyslexiaFont: 'Fuente para dislexia',
    underlineLinks: 'Subrayar enlaces',
    focusOutline: 'Contorno de foco',
    hideImages: 'Ocultar im\u00E1genes',
    textSpacing: 'Espaciado de texto',
    pauseAnimations: 'Pausar animaciones',
    largeCursor: 'Cursor grande',
    highlightHeadings: 'Resaltar encabezados',
    invertColors: 'Invertir colores',
    readingGuide: 'Gu\u00EDa de lectura',
    textToSpeech: 'Texto a voz',
    focusMode: 'Modo de enfoque',
    lineHeight: 'Altura de línea',
    letterSpacing: 'Espacio entre letras',
    wordSpacing: 'Espacio entre palabras',
    visual: 'Visual',
    content: 'Contenido',
    navigation: 'Navegación',
    deuteranopia: 'Deuteranopia (ceguera verde)',
    protanopia: 'Protanopia (ceguera roja)',
    tritanopia: 'Tritanopia (ceguera azul)',
    highlightHover: 'Resaltar al pasar',
    ttsPause: 'Pausar lectura',
    ttsResume: 'Reanudar lectura',
    ttsSpeed: 'Velocidad de lectura',
    ttsSlower: 'Más lento',
    ttsFaster: 'Más rápido',
    quickStart: 'Inicio rápido',
    presetLowVision: 'Visión reducida',
    presetDyslexia: 'Amigable con la dislexia',
    presetAdhd: 'TDAH / Cognitivo',
    presetMotor: 'Motor / Teclado',
    presetMigraine: 'Seguro para migraña',
    settingsActive: 'configuraciones activas',
    resetConfirmation: 'Todas las configuraciones de accesibilidad han sido restablecidas',
    featureEnabled: 'activado',
    featureDisabled: 'desactivado',
    tooltipMessage: 'Personaliza tu configuración de accesibilidad',
    increaseLineHeight: "Aumentar altura de línea",
    decreaseLineHeight: "Reducir altura de línea",
    increaseLetterSpacing: "Aumentar espacio entre letras",
    decreaseLetterSpacing: "Reducir espacio entre letras",
    increaseWordSpacing: "Aumentar espacio entre palabras",
    decreaseWordSpacing: "Reducir espacio entre palabras",
    reducedTransparency: "Transparencia reducida",
    sensoryFriendly: "Amigable con los sentidos",
    readableFont: "Fuente legible",
    saturation: "Saturación",
    increaseSaturation: "Aumentar saturación",
    decreaseSaturation: "Reducir saturación",
    brightness: "Brillo",
    increaseBrightness: "Aumentar brillo",
    decreaseBrightness: "Reducir brillo",
    suppressNotifications: "Suprimir notificaciones",
    devAuditBadge: "Auditoría de desarrollo",
    devAuditNoIssues: "No se encontraron problemas de texto alternativo",
    devAuditIssues: "problemas de texto alternativo encontrados",
    zoomLockWarning: "⚠️ Esta página ha desactivado el gesto de zoom. Los usuarios que dependen del zoom del navegador pueden tener dificultades para acceder al contenido.",
    position: "Posición del widget",
    topLeft: "Arriba izquierda",
    topRight: "Arriba derecha",
    bottomLeft: "Abajo izquierda",
    bottomRight: "Abajo derecha",
    profiles: "Perfiles",
    profileNamePlaceholder: "Nombre del perfil…",
    saveProfile: "Guardar",
    loadProfile: "Cargar",
    deleteProfile: "Eliminar",
    noProfiles: "Sin perfiles guardados",
    accessibilityStatementLink: "Declaración de accesibilidad",
    resetAll: 'Restablecer todo',
    closeMenu: 'Cerrar men\u00FA',
    language: 'Idioma',
    disclaimer:
      'Este widget no garantiza plena conformidad con la accesibilidad.',
  },

  ar: {
    nativeName: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',
    menuTitle: '\u0642\u0627\u0626\u0645\u0629 \u0625\u0645\u0643\u0627\u0646\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644',
    highContrast: '\u062A\u0628\u0627\u064A\u0646 \u0639\u0627\u0644\u0650',
    darkMode: '\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u062F\u0627\u0643\u0646',
    fontSize: '\u062D\u062C\u0645 \u0627\u0644\u062E\u0637',
    increaseFontSize: '\u062A\u0643\u0628\u064A\u0631 \u0627\u0644\u062E\u0637',
    decreaseFontSize: '\u062A\u0635\u063A\u064A\u0631 \u0627\u0644\u062E\u0637',
    resetFontSize: '\u0625\u0639\u0627\u062F\u0629 \u0636\u0628\u0637 \u062D\u062C\u0645 \u0627\u0644\u062E\u0637',
    dyslexiaFont: '\u062E\u0637 \u0635\u062F\u064A\u0642 \u0644\u0639\u0633\u0631 \u0627\u0644\u0642\u0631\u0627\u0621\u0629',
    underlineLinks: '\u062A\u0633\u0637\u064A\u0631 \u0627\u0644\u0631\u0648\u0627\u0628\u0637',
    focusOutline: '\u0625\u0637\u0627\u0631 \u0627\u0644\u062A\u0631\u0643\u064A\u0632',
    hideImages: '\u0625\u062E\u0641\u0627\u0621 \u0627\u0644\u0635\u0648\u0631',
    textSpacing: '\u062A\u0628\u0627\u0639\u062F \u0627\u0644\u0646\u0635',
    pauseAnimations: '\u0625\u064A\u0642\u0627\u0641 \u0627\u0644\u062D\u0631\u0643\u0627\u062A',
    largeCursor: '\u0645\u0624\u0634\u0631 \u0643\u0628\u064A\u0631',
    highlightHeadings: '\u062A\u0645\u064A\u064A\u0632 \u0627\u0644\u0639\u0646\u0627\u0648\u064A\u0646',
    invertColors: '\u0639\u0643\u0633 \u0627\u0644\u0623\u0644\u0648\u0627\u0646',
    readingGuide: '\u062F\u0644\u064A\u0644 \u0627\u0644\u0642\u0631\u0627\u0621\u0629',
    textToSpeech: '\u062A\u062D\u0648\u064A\u0644 \u0627\u0644\u0646\u0635 \u0625\u0644\u0649 \u0643\u0644\u0627\u0645',
    focusMode: 'وضع التركيز',
    lineHeight: 'ارتفاع الخط',
    letterSpacing: 'تباعد الحروف',
    wordSpacing: 'تباعد الكلمات',
    visual: 'مرئي',
    content: 'محتوى',
    navigation: 'تنقل',
    deuteranopia: 'عمى الأخضر',
    protanopia: 'عمى الأحمر',
    tritanopia: 'عمى الأزرق',
    highlightHover: 'تمييز عند التحويم',
    ttsPause: 'إيقاف القراءة',
    ttsResume: 'استئناف القراءة',
    ttsSpeed: 'سرعة القراءة',
    ttsSlower: 'أبطأ',
    ttsFaster: 'أسرع',
    quickStart: 'بداية سريعة',
    presetLowVision: 'ضعف البصر',
    presetDyslexia: 'مناسب لعسر القراءة',
    presetAdhd: 'اضطراب الانتباه / الإدراكي',
    presetMotor: 'حركي / لوحة المفاتيح',
    presetMigraine: 'آمن للصداع النصفي',
    settingsActive: 'إعدادات نشطة',
    resetConfirmation: 'تم إعادة تعيين جميع إعدادات إمكانية الوصول',
    featureEnabled: 'مفعّل',
    featureDisabled: 'معطّل',
    tooltipMessage: 'خصص إعدادات إمكانية الوصول الخاصة بك',
    increaseLineHeight: "زيادة ارتفاع الخط",
    decreaseLineHeight: "تقليل ارتفاع الخط",
    increaseLetterSpacing: "زيادة تباعد الحروف",
    decreaseLetterSpacing: "تقليل تباعد الحروف",
    increaseWordSpacing: "زيادة تباعد الكلمات",
    decreaseWordSpacing: "تقليل تباعد الكلمات",
    reducedTransparency: "شفافية مخفضة",
    sensoryFriendly: "صديق للحواس",
    readableFont: "خط مقروء",
    saturation: "التشبع",
    increaseSaturation: "زيادة التشبع",
    decreaseSaturation: "تقليل التشبع",
    brightness: "السطوع",
    increaseBrightness: "زيادة السطوع",
    decreaseBrightness: "تقليل السطوع",
    suppressNotifications: "كتم الإشعارات",
    devAuditBadge: "تدقيق المطور",
    devAuditNoIssues: "لم يتم العثور على مشاكل في النص البديل",
    devAuditIssues: "مشاكل في النص البديل",
    zoomLockWarning: "⚠️ هذه الصفحة تعطّل إيماءة التكبير. قد يجد المستخدمون الذين يعتمدون على تكبير المتصفح صعوبة في الوصول إلى المحتوى.",
    position: "موضع الأداة",
    topLeft: "أعلى اليسار",
    topRight: "أعلى اليمين",
    bottomLeft: "أسفل اليسار",
    bottomRight: "أسفل اليمين",
    profiles: "الملفات الشخصية",
    profileNamePlaceholder: "اسم الملف الشخصي…",
    saveProfile: "حفظ",
    loadProfile: "تحميل",
    deleteProfile: "حذف",
    noProfiles: "لا توجد ملفات شخصية محفوظة",
    accessibilityStatementLink: "بيان إمكانية الوصول",
    resetAll: '\u0625\u0639\u0627\u062F\u0629 \u0636\u0628\u0637 \u0627\u0644\u0643\u0644',
    closeMenu: '\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0642\u0627\u0626\u0645\u0629',
    language: '\u0627\u0644\u0644\u063A\u0629',
    disclaimer:
      '\u0647\u0630\u0647 \u0627\u0644\u0623\u062F\u0627\u0629 \u0644\u0627 \u062A\u0636\u0645\u0646 \u0627\u0644\u0627\u0645\u062A\u062B\u0627\u0644 \u0627\u0644\u0643\u0627\u0645\u0644 \u0644\u0645\u0639\u0627\u064A\u064A\u0631 \u0625\u0645\u0643\u0627\u0646\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644.',
  },

  pt: {
    nativeName: 'Portugu\u00EAs',
    menuTitle: 'Menu de acessibilidade',
    highContrast: 'Alto contraste',
    darkMode: 'Modo escuro',
    fontSize: 'Tamanho da fonte',
    increaseFontSize: 'Aumentar fonte',
    decreaseFontSize: 'Diminuir fonte',
    resetFontSize: 'Redefinir fonte',
    dyslexiaFont: 'Fonte para dislexia',
    underlineLinks: 'Sublinhar links',
    focusOutline: 'Contorno de foco',
    hideImages: 'Ocultar imagens',
    textSpacing: 'Espa\u00E7amento de texto',
    pauseAnimations: 'Pausar anima\u00E7\u00F5es',
    largeCursor: 'Cursor grande',
    highlightHeadings: 'Destacar cabe\u00E7alhos',
    invertColors: 'Inverter cores',
    readingGuide: 'Guia de leitura',
    textToSpeech: 'Texto para fala',
    focusMode: 'Modo de foco',
    lineHeight: 'Altura da linha',
    letterSpacing: 'Espaçamento de letras',
    wordSpacing: 'Espaçamento de palavras',
    visual: 'Visual',
    content: 'Conteúdo',
    navigation: 'Navegação',
    deuteranopia: 'Deuteranopia (cegueira verde)',
    protanopia: 'Protanopia (cegueira vermelha)',
    tritanopia: 'Tritanopia (cegueira azul)',
    highlightHover: 'Destacar ao passar',
    ttsPause: 'Pausar leitura',
    ttsResume: 'Retomar leitura',
    ttsSpeed: 'Velocidade de leitura',
    ttsSlower: 'Mais devagar',
    ttsFaster: 'Mais rápido',
    quickStart: 'Início rápido',
    presetLowVision: 'Baixa visão',
    presetDyslexia: 'Amigável para dislexia',
    presetAdhd: 'TDAH / Cognitivo',
    presetMotor: 'Motor / Teclado',
    presetMigraine: 'Seguro para enxaqueca',
    settingsActive: 'configurações ativas',
    resetConfirmation: 'Todas as configurações de acessibilidade foram redefinidas',
    featureEnabled: 'ativado',
    featureDisabled: 'desativado',
    tooltipMessage: 'Personalize suas configurações de acessibilidade',
    increaseLineHeight: "Aumentar altura da linha",
    decreaseLineHeight: "Diminuir altura da linha",
    increaseLetterSpacing: "Aumentar espaçamento de letras",
    decreaseLetterSpacing: "Diminuir espaçamento de letras",
    increaseWordSpacing: "Aumentar espaçamento de palavras",
    decreaseWordSpacing: "Diminuir espaçamento de palavras",
    reducedTransparency: "Transparência reduzida",
    sensoryFriendly: "Amigável aos sentidos",
    readableFont: "Fonte legível",
    saturation: "Saturação",
    increaseSaturation: "Aumentar saturação",
    decreaseSaturation: "Diminuir saturação",
    brightness: "Brilho",
    increaseBrightness: "Aumentar brilho",
    decreaseBrightness: "Diminuir brilho",
    suppressNotifications: "Suprimir notificações",
    devAuditBadge: "Auditoria de desenvolvimento",
    devAuditNoIssues: "Nenhum problema de texto alternativo encontrado",
    devAuditIssues: "problemas de texto alternativo encontrados",
    zoomLockWarning: "⚠️ Esta página desativou o gesto de zoom. Usuários que dependem do zoom do navegador podem ter dificuldade em acessar o conteúdo.",
    position: "Posição do widget",
    topLeft: "Canto superior esquerdo",
    topRight: "Canto superior direito",
    bottomLeft: "Canto inferior esquerdo",
    bottomRight: "Canto inferior direito",
    profiles: "Perfis",
    profileNamePlaceholder: "Nome do perfil…",
    saveProfile: "Salvar",
    loadProfile: "Carregar",
    deleteProfile: "Excluir",
    noProfiles: "Nenhum perfil salvo",
    accessibilityStatementLink: "Declaração de acessibilidade",
    resetAll: 'Redefinir tudo',
    closeMenu: 'Fechar menu',
    language: 'Idioma',
    disclaimer:
      'Este widget n\u00E3o garante conformidade total com acessibilidade.',
  },

  fr: {
    nativeName: 'Fran\u00E7ais',
    menuTitle: 'Menu d\u2019accessibilit\u00E9',
    highContrast: 'Contraste \u00E9lev\u00E9',
    darkMode: 'Mode sombre',
    fontSize: 'Taille de police',
    increaseFontSize: 'Augmenter la police',
    decreaseFontSize: 'R\u00E9duire la police',
    resetFontSize: 'R\u00E9initialiser la police',
    dyslexiaFont: 'Police pour dyslexiques',
    underlineLinks: 'Souligner les liens',
    focusOutline: 'Contour de focus',
    hideImages: 'Masquer les images',
    textSpacing: 'Espacement du texte',
    pauseAnimations: 'Mettre en pause les animations',
    largeCursor: 'Grand curseur',
    highlightHeadings: 'Surligner les titres',
    invertColors: 'Inverser les couleurs',
    readingGuide: 'Guide de lecture',
    textToSpeech: 'Synth\u00E8se vocale',
    focusMode: 'Mode focus',
    lineHeight: 'Interligne',
    letterSpacing: 'Espacement des lettres',
    wordSpacing: 'Espacement des mots',
    visual: 'Visuel',
    content: 'Contenu',
    navigation: 'Navigation',
    deuteranopia: 'Dcutéranopie (daltonisme vert)',
    protanopia: 'Protanopie (daltonisme rouge)',
    tritanopia: 'Tritanopie (daltonisme bleu)',
    highlightHover: 'Surligner au survol',
    ttsPause: 'Pause lecture',
    ttsResume: 'Reprendre la lecture',
    ttsSpeed: 'Vitesse de lecture',
    ttsSlower: 'Plus lent',
    ttsFaster: 'Plus rapide',
    quickStart: 'Démarrage rapide',
    presetLowVision: 'Basse vision',
    presetDyslexia: 'Adapté à la dyslexie',
    presetAdhd: 'TDAH / Cognitif',
    presetMotor: 'Moteur / Clavier',
    presetMigraine: 'Adapté aux migraines',
    settingsActive: 'paramètres actifs',
    resetConfirmation: 'Tous les paramètres d\'accessibilité ont été réinitialisés',
    featureEnabled: 'activé',
    featureDisabled: 'désactivé',
    tooltipMessage: 'Personnalisez vos paramètres d\'accessibilité',
    increaseLineHeight: "Augmenter la hauteur de ligne",
    decreaseLineHeight: "Réduire la hauteur de ligne",
    increaseLetterSpacing: "Augmenter l'espacement des lettres",
    decreaseLetterSpacing: "Réduire l'espacement des lettres",
    increaseWordSpacing: "Augmenter l'espacement des mots",
    decreaseWordSpacing: "Réduire l'espacement des mots",
    reducedTransparency: "Transparence réduite",
    sensoryFriendly: "Adapté aux personnes sensibles",
    readableFont: "Police lisible",
    saturation: "Saturation",
    increaseSaturation: "Augmenter la saturation",
    decreaseSaturation: "Réduire la saturation",
    brightness: "Luminosité",
    increaseBrightness: "Augmenter la luminosité",
    decreaseBrightness: "Réduire la luminosité",
    suppressNotifications: "Supprimer les notifications",
    devAuditBadge: "Audit de développement",
    devAuditNoIssues: "Aucun problème de texte alternatif trouvé",
    devAuditIssues: "problèmes de texte alternatif trouvés",
    zoomLockWarning: "⚠️ Cette page a désactivé le zoom par pincement. Les utilisateurs qui dépendent du zoom du navigateur peuvent avoir du mal à accéder au contenu.",
    position: "Position du widget",
    topLeft: "Haut gauche",
    topRight: "Haut droite",
    bottomLeft: "Bas gauche",
    bottomRight: "Bas droite",
    profiles: "Profils",
    profileNamePlaceholder: "Nom du profil…",
    saveProfile: "Enregistrer",
    loadProfile: "Charger",
    deleteProfile: "Supprimer",
    noProfiles: "Aucun profil enregistré",
    accessibilityStatementLink: "Déclaration d'accessibilité",
    resetAll: 'R\u00E9initialiser tout',
    closeMenu: 'Fermer le menu',
    language: 'Langue',
    disclaimer:
      'Ce widget ne garantit pas une conformit\u00E9 totale en mati\u00E8re d\u2019accessibilit\u00E9.',
  },

  de: {
    nativeName: 'Deutsch',
    menuTitle: 'Barrierefreiheitsmen\u00FC',
    highContrast: 'Hoher Kontrast',
    darkMode: 'Dunkelmodus',
    fontSize: 'Schriftgr\u00F6\u00DFe',
    increaseFontSize: 'Schrift vergr\u00F6\u00DFern',
    decreaseFontSize: 'Schrift verkleinern',
    resetFontSize: 'Schriftgr\u00F6\u00DFe zur\u00FCcksetzen',
    dyslexiaFont: 'Schrift f\u00FCr Legasthenie',
    underlineLinks: 'Links unterstreichen',
    focusOutline: 'Fokusumrandung',
    hideImages: 'Bilder ausblenden',
    textSpacing: 'Textabstand',
    pauseAnimations: 'Animationen anhalten',
    largeCursor: 'Gro\u00DFer Cursor',
    highlightHeadings: '\u00DCberschriften hervorheben',
    invertColors: 'Farben invertieren',
    readingGuide: 'Lesehilfe',
    textToSpeech: 'Text zu Sprache',
    focusMode: 'Fokusmodus',
    lineHeight: 'Zeilenabstand',
    letterSpacing: 'Buchstabenabstand',
    wordSpacing: 'Wortabstand',
    visual: 'Visuell',
    content: 'Inhalt',
    navigation: 'Navigation',
    deuteranopia: 'Deuteranopie (Grünblindheit)',
    protanopia: 'Protanopie (Rotblindheit)',
    tritanopia: 'Tritanopie (Blaubl indheit)',
    highlightHover: 'Hervorheben beim Hovern',
    ttsPause: 'Lesen pausieren',
    ttsResume: 'Lesen fortsetzen',
    ttsSpeed: 'Lesegeschwindigkeit',
    ttsSlower: 'Langsamer',
    ttsFaster: 'Schneller',
    quickStart: 'Schnellstart',
    presetLowVision: 'Sehschwäche',
    presetDyslexia: 'Legasthenie-freundlich',
    presetAdhd: 'ADHS / Kognitiv',
    presetMotor: 'Motorisch / Tastatur',
    presetMigraine: 'Migräne-sicher',
    settingsActive: 'Einstellungen aktiv',
    resetConfirmation: 'Alle Barrierefreiheitseinstellungen wurden zurückgesetzt',
    featureEnabled: 'aktiviert',
    featureDisabled: 'deaktiviert',
    tooltipMessage: 'Passen Sie Ihre Barrierefreiheitseinstellungen an',
    increaseLineHeight: "Zeilenhöhe erhöhen",
    decreaseLineHeight: "Zeilenhöhe verringern",
    increaseLetterSpacing: "Zeichenabstand erhöhen",
    decreaseLetterSpacing: "Zeichenabstand verringern",
    increaseWordSpacing: "Wortabstand erhöhen",
    decreaseWordSpacing: "Wortabstand verringern",
    reducedTransparency: "Reduzierte Transparenz",
    sensoryFriendly: "Sinnesfreundlich",
    readableFont: "Lesbare Schrift",
    saturation: "Sättigung",
    increaseSaturation: "Sättigung erhöhen",
    decreaseSaturation: "Sättigung verringern",
    brightness: "Helligkeit",
    increaseBrightness: "Helligkeit erhöhen",
    decreaseBrightness: "Helligkeit verringern",
    suppressNotifications: "Benachrichtigungen unterdrücken",
    devAuditBadge: "Entwickler-Audit",
    devAuditNoIssues: "Keine Alt-Text-Probleme gefunden",
    devAuditIssues: "Alt-Text-Probleme gefunden",
    zoomLockWarning: "⚠️ Diese Seite hat den Pinch-to-Zoom deaktiviert. Benutzer, die auf den Browser-Zoom angewiesen sind, könnten Schwierigkeiten beim Zugriff auf Inhalte haben.",
    position: "Widget-Position",
    topLeft: "Oben links",
    topRight: "Oben rechts",
    bottomLeft: "Unten links",
    bottomRight: "Unten rechts",
    profiles: "Profile",
    profileNamePlaceholder: "Profilname…",
    saveProfile: "Speichern",
    loadProfile: "Laden",
    deleteProfile: "Löschen",
    noProfiles: "Keine gespeicherten Profile",
    accessibilityStatementLink: "Barrierefreiheitserklärung",
    resetAll: 'Alles zur\u00FCcksetzen',
    closeMenu: 'Men\u00FC schlie\u00DFen',
    language: 'Sprache',
    disclaimer:
      'Dieses Widget garantiert keine vollst\u00E4ndige Barrierefreiheits-Konformit\u00E4t.',
  },

  ja: {
    nativeName: '\u65E5\u672C\u8A9E',
    menuTitle: '\u30A2\u30AF\u30BB\u30B7\u30D3\u30EA\u30C6\u30A3\u30E1\u30CB\u30E5\u30FC',
    highContrast: '\u9AD8\u30B3\u30F3\u30C8\u30E9\u30B9\u30C8',
    darkMode: '\u30C0\u30FC\u30AF\u30E2\u30FC\u30C9',
    fontSize: '\u30D5\u30A9\u30F3\u30C8\u30B5\u30A4\u30BA',
    increaseFontSize: '\u30D5\u30A9\u30F3\u30C8\u3092\u5927\u304D\u304F',
    decreaseFontSize: '\u30D5\u30A9\u30F3\u30C8\u3092\u5C0F\u3055\u304F',
    resetFontSize: '\u30D5\u30A9\u30F3\u30C8\u30B5\u30A4\u30BA\u3092\u30EA\u30BB\u30C3\u30C8',
    dyslexiaFont: '\u30C7\u30A3\u30B9\u30EC\u30AF\u30B7\u30A2\u5BFE\u5FDC\u30D5\u30A9\u30F3\u30C8',
    underlineLinks: '\u30EA\u30F3\u30AF\u306B\u4E0B\u7DDA',
    focusOutline: '\u30D5\u30A9\u30FC\u30AB\u30B9\u679A',
    hideImages: '\u753B\u50CF\u3092\u975E\u8868\u793A',
    textSpacing: '\u6587\u5B57\u9593\u9694',
    pauseAnimations: '\u30A2\u30CB\u30E1\u30FC\u30B7\u30E7\u30F3\u3092\u505C\u6B62',
    largeCursor: '\u5927\u304D\u3044\u30AB\u30FC\u30BD\u30EB',
    highlightHeadings: '\u898B\u51FA\u3057\u3092\u5F37\u8ABF',
    invertColors: '\u8272\u3092\u53CD\u8EE2',
    readingGuide: '\u8AAD\u66F8\u30AC\u30A4\u30C9',
    textToSpeech: '\u30C6\u30AD\u30B9\u30C8\u8AAD\u307F\u4E0A\u3052',
    focusMode: 'フォーカスモード',
    lineHeight: '行の高さ',
    letterSpacing: '文字間隔',
    wordSpacing: '単語間隔',
    visual: '視覚',
    content: 'コンテンツ',
    navigation: 'ナビゲーション',
    deuteranopia: '緑色盲 (Deuteranopia)',
    protanopia: '赤色盲 (Protanopia)',
    tritanopia: '青色盲 (Tritanopia)',
    highlightHover: 'ホバーでハイライト',
    ttsPause: '読み上げを一時停止',
    ttsResume: '読み上げを再開',
    ttsSpeed: '読み上げ速度',
    ttsSlower: '遅く',
    ttsFaster: '速く',
    quickStart: 'クイックスタート',
    presetLowVision: '弱視',
    presetDyslexia: 'ディスレクシア対応',
    presetAdhd: 'ADHD / 認知',
    presetMotor: 'モーター / キーボード',
    presetMigraine: '片頭痛対応',
    settingsActive: '設定が有効',
    resetConfirmation: 'すべてのアクセシビリティ設定がリセットされました',
    featureEnabled: '有効',
    featureDisabled: '無効',
    tooltipMessage: 'アクセシビリティ設定をカスタマイズ',
    increaseLineHeight: "行の高さを増やす",
    decreaseLineHeight: "行の高さを減らす",
    increaseLetterSpacing: "文字間隔を増やす",
    decreaseLetterSpacing: "文字間隔を減らす",
    increaseWordSpacing: "単語間隔を増やす",
    decreaseWordSpacing: "単語間隔を減らす",
    reducedTransparency: "透明度を下げる",
    sensoryFriendly: "感覚に配慮した表示",
    readableFont: "読みやすいフォント",
    saturation: "彩度",
    increaseSaturation: "彩度を上げる",
    decreaseSaturation: "彩度を下げる",
    brightness: "明るさ",
    increaseBrightness: "明るさを上げる",
    decreaseBrightness: "明るさを下げる",
    suppressNotifications: "通知を抑制",
    devAuditBadge: "開発者監査",
    devAuditNoIssues: "altテキストの問題は見つかりませんでした",
    devAuditIssues: "altテキストの問題が見つかりました",
    zoomLockWarning: "⚠️ このページはピンチズームを無効にしています。ブラウザのズームに依存するユーザーはコンテンツへのアクセスが困難になる場合があります。",
    position: "ウィジェットの位置",
    topLeft: "左上",
    topRight: "右上",
    bottomLeft: "左下",
    bottomRight: "右下",
    profiles: "プロファイル",
    profileNamePlaceholder: "プロファイル名…",
    saveProfile: "保存",
    loadProfile: "読み込む",
    deleteProfile: "削除",
    noProfiles: "保存されたプロファイルはありません",
    accessibilityStatementLink: "アクセシビリティ宣言",
    resetAll: '\u3059\u3079\u3066\u30EA\u30BB\u30C3\u30C8',
    closeMenu: '\u30E1\u30CB\u30E5\u30FC\u3092\u9589\u3058\u308B',
    language: '\u8A00\u8A9E',
    disclaimer:
      '\u3053\u306E\u30A6\u30A3\u30B8\u30A7\u30C3\u30C8\u306F\u5B8C\u5168\u306A\u30A2\u30AF\u30BB\u30B7\u30D3\u30EA\u30C6\u30A3\u6E96\u62E0\u3092\u4FDD\u8A3C\u3057\u307E\u305B\u3093\u3002',
  },

  ru: {
    nativeName: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439',
    menuTitle: '\u041C\u0435\u043D\u044E \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u0438',
    highContrast: '\u0412\u044B\u0441\u043E\u043A\u0438\u0439 \u043A\u043E\u043D\u0442\u0440\u0430\u0441\u0442',
    darkMode: '\u0422\u0451\u043C\u043D\u044B\u0439 \u0440\u0435\u0436\u0438\u043C',
    fontSize: '\u0420\u0430\u0437\u043C\u0435\u0440 \u0448\u0440\u0438\u0444\u0442\u0430',
    increaseFontSize: '\u0423\u0432\u0435\u043B\u0438\u0447\u0438\u0442\u044C \u0448\u0440\u0438\u0444\u0442',
    decreaseFontSize: '\u0423\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u044C \u0448\u0440\u0438\u0444\u0442',
    resetFontSize: '\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0440\u0430\u0437\u043C\u0435\u0440 \u0448\u0440\u0438\u0444\u0442\u0430',
    dyslexiaFont: '\u0428\u0440\u0438\u0444\u0442 \u0434\u043B\u044F \u0434\u0438\u0441\u043B\u0435\u043A\u0441\u0438\u0438',
    underlineLinks: '\u041F\u043E\u0434\u0447\u0435\u0440\u043A\u043D\u0443\u0442\u044C \u0441\u0441\u044B\u043B\u043A\u0438',
    focusOutline: '\u041A\u043E\u043D\u0442\u0443\u0440 \u0444\u043E\u043A\u0443\u0441\u0430',
    hideImages: '\u0421\u043A\u0440\u044B\u0442\u044C \u0438\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u044F',
    textSpacing: '\u0418\u043D\u0442\u0435\u0440\u0432\u0430\u043B \u0442\u0435\u043A\u0441\u0442\u0430',
    pauseAnimations: '\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C \u0430\u043D\u0438\u043C\u0430\u0446\u0438\u044E',
    largeCursor: '\u0411\u043E\u043B\u044C\u0448\u043E\u0439 \u043A\u0443\u0440\u0441\u043E\u0440',
    highlightHeadings: '\u0412\u044B\u0434\u0435\u043B\u0438\u0442\u044C \u0437\u0430\u0433\u043E\u043B\u043E\u0432\u043A\u0438',
    invertColors: '\u0418\u043D\u0432\u0435\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u0446\u0432\u0435\u0442\u0430',
    readingGuide: '\u041D\u0430\u043F\u0440\u0430\u0432\u043B\u044F\u044E\u0449\u0430\u044F \u0447\u0442\u0435\u043D\u0438\u044F',
    textToSpeech: '\u0422\u0435\u043A\u0441\u0442 \u0432 \u0440\u0435\u0447\u044C',
    focusMode: 'Режим фокуса',
    lineHeight: 'Высота строки',
    letterSpacing: 'Межбуквенный интервал',
    wordSpacing: 'Межсловный интервал',
    visual: 'Визуальный',
    content: 'Контент',
    navigation: 'Навигация',
    deuteranopia: 'Дейтеранопия',
    protanopia: 'Протанопия',
    tritanopia: 'Тританопия',
    highlightHover: 'Подсветка при наведении',
    ttsPause: 'Пауза чтения',
    ttsResume: 'Возобновить чтение',
    ttsSpeed: 'Скорость чтения',
    ttsSlower: 'Медленнее',
    ttsFaster: 'Быстрее',
    quickStart: 'Быстрый старт',
    presetLowVision: 'Слабовидящие',
    presetDyslexia: 'Дислексия-дружелюбный',
    presetAdhd: 'СДВГ / Когнитивный',
    presetMotor: 'Двигательный / Клавиатура',
    presetMigraine: 'Безопасно при мигрени',
    settingsActive: 'настроек активно',
    resetConfirmation: 'Все настройки специальных возможностей сброшены',
    featureEnabled: 'включено',
    featureDisabled: 'отключено',
    tooltipMessage: 'Настройте специальные возможности',
    increaseLineHeight: "Увеличить высоту строки",
    decreaseLineHeight: "Уменьшить высоту строки",
    increaseLetterSpacing: "Увеличить межбуквенный интервал",
    decreaseLetterSpacing: "Уменьшить межбуквенный интервал",
    increaseWordSpacing: "Увеличить межсловный интервал",
    decreaseWordSpacing: "Уменьшить межсловный интервал",
    reducedTransparency: "Сниженная прозрачность",
    sensoryFriendly: "Удобный для восприятия",
    readableFont: "Читаемый шрифт",
    saturation: "Насыщенность",
    increaseSaturation: "Увеличить насыщенность",
    decreaseSaturation: "Уменьшить насыщенность",
    brightness: "Яркость",
    increaseBrightness: "Увеличить яркость",
    decreaseBrightness: "Уменьшить яркость",
    suppressNotifications: "Скрыть уведомления",
    devAuditBadge: "Аудит разработчика",
    devAuditNoIssues: "Проблем с альтернативным текстом не найдено",
    devAuditIssues: "проблем с альтернативным текстом",
    zoomLockWarning: "⚠️ На этой странице отключено масштабирование жестами. Пользователи, зависящие от масштабирования браузера, могут испытывать трудности с доступом к содержимому.",
    position: "Положение виджета",
    topLeft: "Вверху слева",
    topRight: "Вверху справа",
    bottomLeft: "Внизу слева",
    bottomRight: "Внизу справа",
    profiles: "Профили",
    profileNamePlaceholder: "Название профиля…",
    saveProfile: "Сохранить",
    loadProfile: "Загрузить",
    deleteProfile: "Удалить",
    noProfiles: "Нет сохранённых профилей",
    accessibilityStatementLink: "Заявление о доступности",
    resetAll: '\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0432\u0441\u0451',
    closeMenu: '\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043C\u0435\u043D\u044E',
    language: '\u042F\u0437\u044B\u043A',
    disclaimer:
      '\u042D\u0442\u043E\u0442 \u0432\u0438\u0434\u0436\u0435\u0442 \u043D\u0435 \u0433\u0430\u0440\u0430\u043D\u0442\u0438\u0440\u0443\u0435\u0442 \u043F\u043E\u043B\u043D\u043E\u0433\u043E \u0441\u043E\u043E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0438\u044F \u0441\u0442\u0430\u043D\u0434\u0430\u0440\u0442\u0430\u043C \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u0438.',
  },

  hi: {
    nativeName: '\u0939\u093F\u0928\u094D\u0926\u0940',
    menuTitle: '\u0905\u092D\u093F\u0917\u092E\u094D\u092F\u0924\u093E \u092E\u0947\u0928\u0942',
    highContrast: '\u0909\u091A\u094D\u091A \u0915\u0928\u094D\u091F\u094D\u0930\u093E\u0938\u094D\u091F',
    darkMode: '\u0921\u093E\u0930\u094D\u0915 \u092E\u094B\u0921',
    fontSize: '\u092B\u093C\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930',
    increaseFontSize: '\u092B\u093C\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u092C\u095D\u093E\u090F\u0902',
    decreaseFontSize: '\u092B\u093C\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u0918\u091F\u093E\u090F\u0902',
    resetFontSize: '\u092B\u093C\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u0930\u0940\u0938\u0947\u091F \u0915\u0930\u0947\u0902',
    dyslexiaFont: '\u0921\u093F\u0938\u094D\u0932\u0947\u0915\u094D\u0938\u093F\u092F\u093E \u092B\u094D\u0930\u0947\u0902\u0921\u0932\u0940 \u092B\u093C\u0949\u0928\u094D\u091F',
    underlineLinks: '\u0932\u093F\u0902\u0915 \u0930\u0947\u0916\u093E\u0902\u0915\u093F\u0924 \u0915\u0930\u0947\u0902',
    focusOutline: '\u092B\u093C\u094B\u0915\u0938 \u0930\u0947\u0916\u093E',
    hideImages: '\u091B\u0935\u093F\u092F\u093E\u0902 \u091B\u0941\u092A\u093E\u090F\u0902',
    textSpacing: '\u0924\u0947\u095B \u0905\u0902\u0924\u0930\u093E\u0932',
    pauseAnimations: '\u090F\u0928\u093F\u092E\u0947\u0936\u0928 \u0930\u094B\u0915\u0947\u0902',
    largeCursor: '\u092C\u095C\u093E \u0915\u0930\u094D\u0938\u0930',
    highlightHeadings: '\u0936\u0940\u0930\u094D\u0937\u0915 \u0939\u093E\u0907\u0932\u093E\u0907\u091F \u0915\u0930\u0947\u0902',
    invertColors: '\u0930\u0902\u0917 \u0909\u0932\u091F\u0947\u0902',
    readingGuide: '\u092A\u0920\u0928 \u0917\u093E\u0907\u0921',
    textToSpeech: '\u0924\u0947\u095B \u0938\u0947 \u0935\u093E\u0923\u0940',
    focusMode: 'फोकस मोड',
    lineHeight: 'लाइन की उंचाई',
    letterSpacing: 'अक्षर अंतराल',
    wordSpacing: 'शब्द अंतराल',
    visual: 'दृश्य',
    content: 'सामग्री',
    navigation: 'नेविगेशन',
    deuteranopia: 'हरा रंगांधता',
    protanopia: 'लाल रंगांधता',
    tritanopia: 'नीला रंगांधता',
    highlightHover: 'होवर पर हाइलाइट',
    ttsPause: 'पठन रोकें',
    ttsResume: 'पठन जारी रखें',
    ttsSpeed: 'पठन गति',
    ttsSlower: 'धीमा',
    ttsFaster: 'तेज़',
    quickStart: 'त्वरित शुरुआत',
    presetLowVision: 'कम दृष्टि',
    presetDyslexia: 'डिस्लेक्सिया अनुकूल',
    presetAdhd: 'एडीएचडी / संज्ञानात्मक',
    presetMotor: 'मोटर / कीबोर्ड',
    presetMigraine: 'माइग्रेन सुरक्षित',
    settingsActive: 'सेटिंग्स सक्रिय',
    resetConfirmation: 'सभी अभिगम्यता सेटिंग्स रीसेट की गईं',
    featureEnabled: 'सक्षम',
    featureDisabled: 'अक्षम',
    tooltipMessage: 'अपनी अभिगम्यता सेटिंग्स कस्टमाइज़ करें',
    increaseLineHeight: "पंक्ति ऊँचाई बढ़ाएं",
    decreaseLineHeight: "पंक्ति ऊँचाई घटाएं",
    increaseLetterSpacing: "अक्षर अंतराल बढ़ाएं",
    decreaseLetterSpacing: "अक्षर अंतराल घटाएं",
    increaseWordSpacing: "शब्द अंतराल बढ़ाएं",
    decreaseWordSpacing: "शब्द अंतराल घटाएं",
    reducedTransparency: "कम पारदर्शिता",
    sensoryFriendly: "संवेदी-अनुकूल",
    readableFont: "पठनीय फ़ॉन्ट",
    saturation: "संतृप्ति",
    increaseSaturation: "संतृप्ति बढ़ाएं",
    decreaseSaturation: "संतृप्ति घटाएं",
    brightness: "चमक",
    increaseBrightness: "चमक बढ़ाएं",
    decreaseBrightness: "चमक घटाएं",
    suppressNotifications: "सूचनाएं दबाएं",
    devAuditBadge: "डेव ऑडिट",
    devAuditNoIssues: "कोई alt टेक्स्ट समस्या नहीं मिली",
    devAuditIssues: "alt टेक्स्ट समस्याएं मिलीं",
    zoomLockWarning: "⚠️ इस पृष्ठ ने पिंच-टू-जूम को अक्षम किया है। ब्राउज़र ज़ूम पर निर्भर उपयोगकर्ताओं को सामग्री तक पहुँचने में कठिनाई हो सकती है।",
    position: "विजेट स्थिति",
    topLeft: "ऊपर बाएं",
    topRight: "ऊपर दाएं",
    bottomLeft: "नीचे बाएं",
    bottomRight: "नीचे दाएं",
    profiles: "प्रोफाइल",
    profileNamePlaceholder: "प्रोफाइल नाम…",
    saveProfile: "सहेजें",
    loadProfile: "लोड करें",
    deleteProfile: "हटाएं",
    noProfiles: "कोई सहेजा गया प्रोफाइल नहीं",
    accessibilityStatementLink: "एक्सेसिबिलिटी स्टेटमेंट",
    resetAll: '\u0938\u092D\u0940 \u0930\u0940\u0938\u0947\u091F \u0915\u0930\u0947\u0902',
    closeMenu: '\u092E\u0947\u0928\u0942 \u092C\u0902\u0926 \u0915\u0930\u0947\u0902',
    language: '\u092D\u093E\u0937\u093E',
    disclaimer:
      '\u092F\u0939 \u0935\u093F\u091C\u0947\u091F \u092A\u0942\u0930\u094D\u0923 \u0905\u092D\u093F\u0917\u092E\u094D\u092F\u0924\u093E \u0905\u0928\u0941\u092A\u093E\u0932\u0928 \u0915\u0940 \u0917\u093E\u0930\u0902\u091F\u0940 \u0928\u0939\u0940\u0902 \u0926\u0947\u0924\u093E\u0964',
  },

  bn: {
    nativeName: '\u09AC\u09BE\u0982\u09B2\u09BE',
    menuTitle: '\u09AA\u09CD\u09B0\u09AC\u09C7\u09B6\u09AF\u09CB\u0997\u09CD\u09AF\u09A4\u09BE \u09AE\u09C7\u09A8\u09C1',
    highContrast: '\u0989\u099A\u09CD\u099A \u0995\u09A8\u09CD\u099F\u09CD\u09B0\u09BE\u09B8\u09CD\u099F',
    darkMode: '\u09A1\u09BE\u09B0\u09CD\u0995 \u09AE\u09CB\u09A1',
    fontSize: '\u09AB\u09A8\u09CD\u099F \u0986\u0995\u09BE\u09B0',
    increaseFontSize: '\u09AB\u09A8\u09CD\u099F \u09AC\u09A1\u09BC \u0995\u09B0\u09C1\u09A8',
    decreaseFontSize: '\u09AB\u09A8\u09CD\u099F \u099B\u09CB\u099F \u0995\u09B0\u09C1\u09A8',
    resetFontSize: '\u09AB\u09A8\u09CD\u099F \u0986\u0995\u09BE\u09B0 \u09B0\u09BF\u09B8\u09C7\u099F \u0995\u09B0\u09C1\u09A8',
    dyslexiaFont: '\u09A1\u09BF\u09B8\u09B2\u09C7\u0995\u09CD\u09B8\u09BF\u09AF\u09BC\u09BE \u09AC\u09BE\u09A8\u09CD\u09A7\u09AC \u09AB\u09A8\u09CD\u099F',
    underlineLinks: '\u09B2\u09BF\u0999\u09CD\u0995\u09C7 \u0986\u09A8\u09CD\u09A1\u09BE\u09B0\u09B2\u09BE\u0987\u09A8',
    focusOutline: '\u09AB\u09CB\u0995\u09BE\u09B8 \u09B0\u09C7\u0996\u09BE',
    hideImages: '\u099B\u09AC\u09BF \u09B2\u09C1\u0995\u09BE\u09A8',
    textSpacing: '\u09B2\u09C7\u0996\u09BE\u09B0 \u09AC\u09CD\u09AF\u09AC\u09A7\u09BE\u09A8',
    pauseAnimations: '\u0985\u09CD\u09AF\u09BE\u09A8\u09BF\u09AE\u09C7\u09B6\u09A8 \u09AC\u09BF\u09B0\u09BE\u09AE \u0995\u09B0\u09C1\u09A8',
    largeCursor: '\u09AC\u09A1\u09BC \u0995\u09BE\u09B0\u09CD\u09B8\u09BE\u09B0',
    highlightHeadings: '\u09B6\u09BF\u09B0\u09CB\u09A8\u09BE\u09AE \u09B9\u09BE\u0987\u09B2\u09BE\u0987\u099F \u0995\u09B0\u09C1\u09A8',
    invertColors: '\u09B0\u0999 \u0989\u09B2\u09CD\u099F\u09BE\u09A8',
    readingGuide: '\u09AA\u09BE\u09A0\u09C7\u09B0 \u09A8\u09BF\u09B0\u09CD\u09A6\u09C7\u09B6\u09BF\u0995\u09BE',
    textToSpeech: '\u099F\u09C7\u0995\u09CD\u09B8\u099F \u09A5\u09C7\u0995\u09C7 \u09B0\u09BE\u09B6\u09BF',
    focusMode: 'ফোকাস মোড',
    lineHeight: 'লাইনের উচ্চতা',
    letterSpacing: 'অক্ষরের ব্যবধান',
    wordSpacing: 'শব্দের ব্যবধান',
    visual: 'দৃশ্য',
    content: 'বিষয়বস্তু',
    navigation: 'নেভিগেশন',
    deuteranopia: 'সবুজ বর্ণান্ধ',
    protanopia: 'লাল বর্ণান্ধ',
    tritanopia: 'নীল বর্ণান্ধ',
    highlightHover: 'হোভারে হাইলাইট',
    ttsPause: 'পাঠ থামান',
    ttsResume: 'পাঠ পুনরায় শুরু করুন',
    ttsSpeed: 'পাঠের গতি',
    ttsSlower: 'ধীর',
    ttsFaster: 'দ্রুত',
    quickStart: 'দ্রুত শুরু',
    presetLowVision: 'কম দৃষ্টি',
    presetDyslexia: 'ডিসলেক্সিয়া বান্ধব',
    presetAdhd: 'এডিএইচডি / জ্ঞানীয়',
    presetMotor: 'মোটর / কীবোর্ড',
    presetMigraine: 'মাইগ্রেন নিরাপদ',
    settingsActive: 'সেটিংস সক্রিয়',
    resetConfirmation: 'সমস্ত অ্যোগ্যতা সেটিংস রিসেট হয়েছে',
    featureEnabled: 'সক্রিয়',
    featureDisabled: 'নিষ্ক্রিয়',
    tooltipMessage: 'আপনার প্রবেশযোগ্যতা সেটিংস কাস্টমাইজ করুন',
    increaseLineHeight: "লাইনের উচ্চতা বাড়ান",
    decreaseLineHeight: "লাইনের উচ্চতা কমান",
    increaseLetterSpacing: "অক্ষরের ব্যবধান বাড়ান",
    decreaseLetterSpacing: "অক্ষরের ব্যবধান কমান",
    increaseWordSpacing: "শব্দের ব্যবধান বাড়ান",
    decreaseWordSpacing: "শব্দের ব্যবধান কমান",
    reducedTransparency: "স্বচ্ছতা কমান",
    sensoryFriendly: "সংবেদনশীল-বান্ধব",
    readableFont: "পাঠযোগ্য ফন্ট",
    saturation: "স্যাচুরেশন",
    increaseSaturation: "স্যাচুরেশন বাড়ান",
    decreaseSaturation: "স্যাচুরেশন কমান",
    brightness: "উজ্জ্বলতা",
    increaseBrightness: "উজ্জ্বলতা বাড়ান",
    decreaseBrightness: "উজ্জ্বলতা কমান",
    suppressNotifications: "বিজ্ঞপ্তি দমন করুন",
    devAuditBadge: "ডেভ অডিট",
    devAuditNoIssues: "alt টেক্সটের কোনো সমস্যা পাওয়া যায়নি",
    devAuditIssues: "alt টেক্সটের সমস্যা পাওয়া গেছে",
    zoomLockWarning: "⚠️ এই পৃষ্ঠায় পিঞ্চ-টু-জুম অক্ষম করা হয়েছে। ব্রাউজার জুমের উপর নির্ভরশীল ব্যবহারকারীদের কনটেন্ট অ্যাক্সেস করতে অসুবিধা হতে পারে।",
    position: "উইজেটের অবস্থান",
    topLeft: "উপরে বামে",
    topRight: "উপরে ডানে",
    bottomLeft: "নিচে বামে",
    bottomRight: "নিচে ডানে",
    profiles: "প্রোফাইল",
    profileNamePlaceholder: "প্রোফাইলের নাম…",
    saveProfile: "সংরক্ষণ করুন",
    loadProfile: "লোড করুন",
    deleteProfile: "মুছুন",
    noProfiles: "কোনো সংরক্ষিত প্রোফাইল নেই",
    accessibilityStatementLink: "অ্যাক্সেসিবিলিটি স্টেটমেন্ট",
    resetAll: '\u09B8\u09AC \u09B0\u09BF\u09B8\u09C7\u099F \u0995\u09B0\u09C1\u09A8',
    closeMenu: '\u09AE\u09C7\u09A8\u09C1 \u09AC\u09A8\u09CD\u09A7 \u0995\u09B0\u09C1\u09A8',
    language: '\u09AD\u09BE\u09B7\u09BE',
    disclaimer:
      '\u098F\u0987 \u0989\u0987\u099C\u09C7\u099F \u09B8\u09AE\u09CD\u09AA\u09C2\u09B0\u09CD\u09A3 \u09AA\u09CD\u09B0\u09AC\u09C7\u09B6\u09AF\u09CB\u0997\u09CD\u09AF\u09A4\u09BE \u09AA\u09CD\u09B0\u09BE\u09AA\u09CD\u09A4\u09BF\u09B0 \u09A8\u09BF\u09B6\u09CD\u099A\u09AF\u09BC\u09A4\u09BE \u09A6\u09C7\u09AF\u09BC \u09A8\u09BE\u0964',
  },

  pa: {
    nativeName: '\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40',
    menuTitle: '\u0A2A\u0A39\u0A41\u0A70\u0A1A\u0A2F\u0A4B\u0A17\u0A24\u0A3E \u0A2E\u0A47\u0A28\u0A42',
    highContrast: '\u0A09\u0A1A\u0A4D\u0A1A \u0A15\u0A70\u0A1F\u0A4D\u0A30\u0A3E\u0A38\u0A1F',
    darkMode: '\u0A21\u0A3E\u0A30\u0A15 \u0A2E\u0A4B\u0A21',
    fontSize: '\u0A2B\u0A4C\u0A02\u0A1F \u0A06\u0A15\u0A3E\u0A30',
    increaseFontSize: '\u0A2B\u0A4C\u0A02\u0A1F \u0A06\u0A15\u0A3E\u0A30 \u0A35\u0A27\u0A3E\u0A13',
    decreaseFontSize: '\u0A2B\u0A4C\u0A02\u0A1F \u0A06\u0A15\u0A3E\u0A30 \u0A18\u0A1F\u0A3E\u0A13',
    resetFontSize: '\u0A2B\u0A4C\u0A02\u0A1F \u0A06\u0A15\u0A3E\u0A30 \u0A30\u0A40\u0A38\u0A48\u0A1F \u0A15\u0A30\u0A4B',
    dyslexiaFont: '\u0A21\u0A3F\u0A38\u0A32\u0A48\u0A15\u0A38\u0A40\u0A06 \u0A2B\u0A4D\u0A30\u0A47\u0A02\u0A21\u0A32\u0A40 \u0A2B\u0A4C\u0A02\u0A1F',
    underlineLinks: '\u0A32\u0A3F\u0A70\u0A15\u0A3E\u0A02 \u0A28\u0A42\u0A70 \u0A30\u0A47\u0A16\u0A3E\u0A02\u0A15\u0A3F\u0A24 \u0A15\u0A30\u0A4B',
    focusOutline: '\u0A2B\u0A4B\u0A15\u0A38 \u0A30\u0A47\u0A16\u0A3E',
    hideImages: '\u0A1A\u0A3F\u0A71\u0A24\u0A30 \u0A32\u0A41\u0A15\u0A3E\u0A13',
    textSpacing: '\u0A1F\u0A48\u0A15\u0A38\u0A1F \u0A05\u0A70\u0A24\u0A30',
    pauseAnimations: '\u0A10\u0A28\u0A40\u0A2E\u0A47\u0A38\u0A28 \u0A30\u0A4B\u0A15\u0A4B',
    largeCursor: '\u0A35\u0A71\u0A21\u0A3E \u0A15\u0A30\u0A38\u0A30',
    highlightHeadings: '\u0A38਼\u0A40\u0A30\u0A37\u0A15 \u0A39\u0A3E\u0A08\u0A32\u0A3E\u0A08\u0A1F \u0A15\u0A30\u0A4B',
    invertColors: '\u0A30\u0A70\u0A17 \u0A2A\u0A32\u0A1F\u0A4B',
    readingGuide: '\u0A2A\u0A22\u0A3C\u0A28 \u0A17\u0A3E\u0A07\u0A21',
    textToSpeech: '\u0A1F\u0A48\u0A15\u0A38\u0A1F \u0A24\u0A4B\u0A02 \u0A2C\u0A4B\u0A32\u0A40',
    focusMode: 'ਫੋਕਸ ਮੋਡ',
    lineHeight: 'ਲਾਈਨ ਦੀ ਉਚਾਈ',
    letterSpacing: 'ਅੱਖਰ ਦੂਰੀ',
    wordSpacing: 'ਸ਼ਬਦ ਦੂਰੀ',
    visual: 'ਦਿੱਖ',
    content: 'ਸਮ਱ਗਰੀ',
    navigation: 'ਨੇਵੀਗੇਸ਼ਨ',
    deuteranopia: 'ਹਰੇ ਰੰਗ ਅੰਧਲਾਪਣ',
    protanopia: 'ਲਾਲ ਰੰਗ ਅੰਧਲਾਪਣ',
    tritanopia: 'ਨੀਲਾ ਰੰਗ ਅੰਧਲਾਪਣ',
    highlightHover: 'ਹੋਵਰ ਤੇ ਹਾਈਲਾਈਟ',
    ttsPause: 'ਪੜ੍ਹਨਾ ਰੋਕੋ',
    ttsResume: 'ਪੜ੍ਹਨਾ ਜਾਰੀ ਰੱਖੋ',
    ttsSpeed: 'ਪੜ੍ਹਨ ਦੀ ਗਤੀ',
    ttsSlower: 'ਹੌਲੀ',
    ttsFaster: 'ਤੇਜ਼',
    quickStart: 'ਤੇਜ਼ ਸ਼ੁਰੂਆਤ',
    presetLowVision: 'ਘੱਟ ਨਜ਼ਰ',
    presetDyslexia: 'ਡਿਸਲੈਕਸੀਆ ਅਨੁਕੂਲ',
    presetAdhd: 'ਏਡੀਐਚਡੀ / ਬੋਧਾਤਮਕ',
    presetMotor: 'ਮੋਟਰ / ਕੀਬੋਰਡ',
    presetMigraine: 'ਮਾਈਗ੍ਰੇਨ ਸੁਰੱਖਿ੅ਤ',
    settingsActive: 'ਸੇਟਿੰਗਜ‍ ਸਰਗਰਮ',
    resetConfirmation: 'ਸਭ ਪਹੁੰਚਯોਗਤਾ ਸੇਟਿੰਗਜ‍ ਰੀਸੈਟ ਹો ਗਈਆਂ',
    featureEnabled: 'ਸਕਿਰਕ',
    featureDisabled: 'ਅਕਿਰਕ',
    tooltipMessage: 'ਅਪਣੀਆਂ ਪਹੁੰਚਯોਗਤਾ ਸੇਟਿੰਗਜ‍ ਕਸਟਮਾਈਜ‍ ਕਰੋ',
    increaseLineHeight: "ਲਾਈਨ ਉਚਾਈ ਵਧਾਓ",
    decreaseLineHeight: "ਲਾਈਨ ਉਚਾਈ ਘਟਾਓ",
    increaseLetterSpacing: "ਅੱਖਰ ਖਾਲੀ ਥਾਂ ਵਧਾਓ",
    decreaseLetterSpacing: "ਅੱਖਰ ਖਾਲੀ ਥਾਂ ਘਟਾਓ",
    increaseWordSpacing: "ਸ਼ਬਦ ਖਾਲੀ ਥਾਂ ਵਧਾਓ",
    decreaseWordSpacing: "ਸ਼ਬਦ ਖਾਲੀ ਥਾਂ ਘਟਾਓ",
    reducedTransparency: "ਘਟਾਈ ਹੋਈ ਪਾਰਦਰਸ਼ਿਤਾ",
    sensoryFriendly: "ਸੰਵੇਦਨਾ-ਅਨੁਕੂਲ",
    readableFont: "ਪੜ੍ਹਨਯੋਗ ਫੌਂਟ",
    saturation: "ਸੰਤ੍ਰਿਪਤਾ",
    increaseSaturation: "ਸੰਤ੍ਰਿਪਤਾ ਵਧਾਓ",
    decreaseSaturation: "ਸੰਤ੍ਰਿਪਤਾ ਘਟਾਓ",
    brightness: "ਚਮਕ",
    increaseBrightness: "ਚਮਕ ਵਧਾਓ",
    decreaseBrightness: "ਚਮਕ ਘਟਾਓ",
    suppressNotifications: "ਸੂਚਨਾਵਾਂ ਦਬਾਓ",
    devAuditBadge: "ਡੇਵ ਆਡਿਟ",
    devAuditNoIssues: "alt ਟੈਕਸਟ ਦੀਆਂ ਕੋਈ ਸਮੱਸਿਆਵਾਂ ਨਹੀਂ ਮਿਲੀਆਂ",
    devAuditIssues: "alt ਟੈਕਸਟ ਦੀਆਂ ਸਮੱਸਿਆਵਾਂ ਮਿਲੀਆਂ",
    zoomLockWarning: "⚠️ ਇਸ ਪੰਨੇ ਨੇ ਪਿੰਚ-ਟੂ-ਜ਼ੂਮ ਨੂੰ ਅਯੋਗ ਕੀਤਾ ਹੈ। ਬ੍ਰਾਊਜ਼ਰ ਜ਼ੂਮ ’ਤੇ ਨਿਰਭਰ ਕਰਨ ਵਾਲੇ ਉਪਭੋਗਤਾਵਾਂ ਨੂੰ ਸਮੱਗਰੀ ਤੱਕ ਪਹੁੰਚਣ ਵਿੱਚ ਮੁਸ਼ਕਲ ਹੋ ਸਕਦੀ ਹੈ।",
    position: "ਵਿਜੇਟ ਸਥਿਤੀ",
    topLeft: "ਉੱਪਰ ਖੱਬੇ",
    topRight: "ਉੱਪਰ ਸੱਜੇ",
    bottomLeft: "ਹੇਠਾਂ ਖੱਬੇ",
    bottomRight: "ਹੇਠਾਂ ਸੱਜੇ",
    profiles: "ਪ੍ਰੋਫਾਈਲ",
    profileNamePlaceholder: "ਪ੍ਰੋਫਾਈਲ ਨਾਮ…",
    saveProfile: "ਸੁਰੱਖਿਅਤ ਕਰੋ",
    loadProfile: "ਲੋਡ ਕਰੋ",
    deleteProfile: "ਮਿਟਾਓ",
    noProfiles: "ਕੋਈ ਸੁਰੱਖਿਅਤ ਪ੍ਰੋਫਾਈਲ ਨਹੀਂ",
    accessibilityStatementLink: "ਪਹੁੰਚਯੋਗਤਾ ਬਿਆਨ",
    resetAll: '\u0A38\u0A2D \u0A30\u0A40\u0A38\u0A48\u0A1F \u0A15\u0A30\u0A4B',
    closeMenu: '\u0A2E\u0A47\u0A28\u0A42 \u0A2C\u0A70\u0A26 \u0A15\u0A30\u0A4B',
    language: '\u0A2D\u0A3E\u0A38\u0A3C\u0A3E',
    disclaimer:
      '\u0A07\u0A39 \u0A35\u0A3F\u0A1C\u0A47\u0A1F \u0A2A\u0A42\u0A30\u0A40 \u0A2A\u0A39\u0A41\u0A70\u0A1A\u0A2F\u0A4B\u0A17\u0A24\u0A3E \u0A05\u0A28\u0A41\u0A2A\u0A3E\u0A32\u0A28 \u0A26\u0A40 \u0A17\u0A3E\u0A30\u0A70\u0A1F\u0A40 \u0A28\u0A39\u0A40\u0A02 \u0A26\u0A3F\u0A70\u0A26\u0A3E\u0964',
  },

  id: {
    nativeName: 'Indonesia',
    menuTitle: 'Menu Aksesibilitas',
    highContrast: 'Kontras Tinggi',
    darkMode: 'Mode Gelap',
    fontSize: 'Ukuran Font',
    increaseFontSize: 'Perbesar Font',
    decreaseFontSize: 'Perkecil Font',
    resetFontSize: 'Reset Ukuran Font',
    dyslexiaFont: 'Font Ramah Disleksia',
    underlineLinks: 'Garis Bawah Tautan',
    focusOutline: 'Garis Fokus',
    hideImages: 'Sembunyikan Gambar',
    textSpacing: 'Spasi Teks',
    pauseAnimations: 'Jeda Animasi',
    largeCursor: 'Kursor Besar',
    highlightHeadings: 'Sorot Judul',
    invertColors: 'Balik Warna',
    readingGuide: 'Panduan Membaca',
    textToSpeech: 'Teks ke Suara',
    focusMode: 'Mode Fokus',
    lineHeight: 'Tinggi Baris',
    letterSpacing: 'Jarak Huruf',
    wordSpacing: 'Jarak Kata',
    visual: 'Visual',
    content: 'Konten',
    navigation: 'Navigasi',
    deuteranopia: 'Deuteranopia (buta hijau)',
    protanopia: 'Protanopia (buta merah)',
    tritanopia: 'Tritanopia (buta biru)',
    highlightHover: 'Sorot saat hover',
    ttsPause: 'Jeda membaca',
    ttsResume: 'Lanjutkan membaca',
    ttsSpeed: 'Kecepatan membaca',
    ttsSlower: 'Lebih lambat',
    ttsFaster: 'Lebih cepat',
    quickStart: 'Mulai Cepat',
    presetLowVision: 'Penglihatan Rendah',
    presetDyslexia: 'Ramah Disleksia',
    presetAdhd: 'ADHD / Kognitif',
    presetMotor: 'Motorik / Keyboard',
    presetMigraine: 'Aman untuk Migrain',
    settingsActive: 'pengaturan aktif',
    resetConfirmation: 'Semua pengaturan aksesibilitas telah direset',
    featureEnabled: 'diaktifkan',
    featureDisabled: 'dinonaktifkan',
    tooltipMessage: 'Sesuaikan pengaturan aksesibilitas Anda',
    increaseLineHeight: "Tambah tinggi baris",
    decreaseLineHeight: "Kurangi tinggi baris",
    increaseLetterSpacing: "Tambah jarak huruf",
    decreaseLetterSpacing: "Kurangi jarak huruf",
    increaseWordSpacing: "Tambah jarak kata",
    decreaseWordSpacing: "Kurangi jarak kata",
    reducedTransparency: "Transparansi dikurangi",
    sensoryFriendly: "Ramah indera",
    readableFont: "Font yang mudah dibaca",
    saturation: "Saturasi",
    increaseSaturation: "Tambah saturasi",
    decreaseSaturation: "Kurangi saturasi",
    brightness: "Kecerahan",
    increaseBrightness: "Tambah kecerahan",
    decreaseBrightness: "Kurangi kecerahan",
    suppressNotifications: "Sembunyikan notifikasi",
    devAuditBadge: "Audit pengembang",
    devAuditNoIssues: "Tidak ada masalah teks alternatif ditemukan",
    devAuditIssues: "masalah teks alternatif ditemukan",
    zoomLockWarning: "⚠️ Halaman ini telah menonaktifkan zoom cubit. Pengguna yang mengandalkan zoom browser mungkin kesulitan mengakses konten.",
    position: "Posisi widget",
    topLeft: "Kiri atas",
    topRight: "Kanan atas",
    bottomLeft: "Kiri bawah",
    bottomRight: "Kanan bawah",
    profiles: "Profil",
    profileNamePlaceholder: "Nama profil…",
    saveProfile: "Simpan",
    loadProfile: "Muat",
    deleteProfile: "Hapus",
    noProfiles: "Tidak ada profil tersimpan",
    accessibilityStatementLink: "Pernyataan aksesibilitas",
    resetAll: 'Reset Semua',
    closeMenu: 'Tutup Menu',
    language: 'Bahasa',
    disclaimer:
      'Widget ini tidak menjamin kepatuhan aksesibilitas penuh.',
  },

  ur: {
    nativeName: '\u0627\u0631\u062F\u0648',
    menuTitle: '\u0631\u0633\u0627\u0626\u06CC \u0645\u06CC\u0646\u0648',
    highContrast: '\u0632\u06CC\u0627\u062F\u06C1 \u062A\u0636\u0627\u062F',
    darkMode: '\u0688\u0627\u0631\u06A9 \u0645\u0648\u0688',
    fontSize: '\u062E\u0637 \u06A9\u0627 \u062D\u062C\u0645',
    increaseFontSize: '\u062E\u0637 \u06A9\u0627 \u062D\u062C\u0645 \u0628\u0691\u06BE\u0627\u0626\u06CC\u06BA',
    decreaseFontSize: '\u062E\u0637 \u06A9\u0627 \u062D\u062C\u0645 \u06AF\u06BE\u0679\u0627\u0626\u06CC\u06BA',
    resetFontSize: '\u062E\u0637 \u06A9\u0627 \u062D\u062C\u0645 \u0631\u06CC\u0633\u06CC\u0679 \u06A9\u0631\u06CC\u06BA',
    dyslexiaFont: '\u0688\u0633\u0644\u06CC\u06A9\u0633\u06CC\u0627 \u062F\u0648\u0633\u062A \u062E\u0637',
    underlineLinks: '\u0644\u0646\u06A9\u0633 \u06A9\u06D2 \u0646\u06CC\u0686\u06D2 \u0644\u06A9\u06CC\u0631',
    focusOutline: '\u0641\u0648\u06A9\u0633 \u0622\u0624\u0679 \u0644\u0627\u0626\u0646',
    hideImages: '\u062A\u0635\u0627\u0648\u06CC\u0631 \u0686\u06BE\u067E\u0627\u0626\u06CC\u06BA',
    textSpacing: '\u0645\u062A\u0646 \u06A9\u0627 \u0641\u0627\u0635\u0644\u06C1',
    pauseAnimations: '\u0627\u06CC\u0646\u06CC\u0645\u06CC\u0634\u0646 \u0631\u0648\u06A9\u06CC\u06BA',
    largeCursor: '\u0628\u0691\u0627 \u06A9\u0631\u0633\u0631',
    highlightHeadings: '\u0633\u0631\u062E\u06CC\u0627\u06BA \u0646\u0645\u0627\u06CC\u0627\u06BA \u06A9\u0631\u06CC\u06BA',
    invertColors: '\u0631\u0646\u06AF \u0627\u0644\u0679\u06CC\u06BA',
    readingGuide: '\u067E\u0691\u06BE\u0646\u06D2 \u06A9\u06CC \u0631\u06C1\u0646\u0645\u0627\u0626\u06CC',
    textToSpeech: '\u0645\u062A\u0646 \u0633\u06D2 \u062A\u0642\u0631\u06CC\u0631',
    focusMode: 'فوکس موڈ',
    lineHeight: 'لائن کی اونچائی',
    letterSpacing: 'حرف کا فاصلہ',
    wordSpacing: 'لفظ کا فاصلہ',
    visual: 'بصری',
    content: 'مواد',
    navigation: 'نیویگیشن',
    deuteranopia: 'سبز رنگ اندھا پن',
    protanopia: 'سرخ رنگ اندھا پن',
    tritanopia: 'نیلا رنگ اندھا پن',
    highlightHover: 'ہوور پر روشنی',
    ttsPause: 'پڑھنا روکیں',
    ttsResume: 'پڑھنا دوبارہ شروع کریں',
    ttsSpeed: 'پڑھنے کی رفتار',
    ttsSlower: 'سست',
    ttsFaster: 'تیز',
    quickStart: 'فوری شروعات',
    presetLowVision: 'کم بصارت',
    presetDyslexia: 'ڈسلیکسیا دوست',
    presetAdhd: 'توجہ خسارہ / ادراکی',
    presetMotor: 'موٹر / کی بورڈ',
    presetMigraine: 'درد شقیقہ محفوظ',
    settingsActive: 'سیٹنگز فعال',
    resetConfirmation: 'تمام ایکسیسبلیٹی سیٹنگز ریسیٹ کر دی گئی ہیں',
    featureEnabled: 'فعال',
    featureDisabled: 'غیر فعال',
    tooltipMessage: 'اپنی ایکسیسبلیٹی سیٹنگز کسٹمائز کریں',
    increaseLineHeight: "لائن کی اونچائی بڑھائیں",
    decreaseLineHeight: "لائن کی اونچائی کم کریں",
    increaseLetterSpacing: "حروف کا فاصلہ بڑھائیں",
    decreaseLetterSpacing: "حروف کا فاصلہ کم کریں",
    increaseWordSpacing: "الفاظ کا فاصلہ بڑھائیں",
    decreaseWordSpacing: "الفاظ کا فاصلہ کم کریں",
    reducedTransparency: "کم شفافیت",
    sensoryFriendly: "حسی دوست",
    readableFont: "پڑھنے کے قابل فونٹ",
    saturation: "سیچوریشن",
    increaseSaturation: "سیچوریشن بڑھائیں",
    decreaseSaturation: "سیچوریشن کم کریں",
    brightness: "چمک",
    increaseBrightness: "چمک بڑھائیں",
    decreaseBrightness: "چمک کم کریں",
    suppressNotifications: "اطلاعات دبائیں",
    devAuditBadge: "ڈیو آڈٹ",
    devAuditNoIssues: "alt متن کے کوئی مسائل نہیں ملے",
    devAuditIssues: "alt متن کے مسائل ملے",
    zoomLockWarning: "⚠️ اس صفحے نے پنچ-ٹو-زوم غیر فعال کر دیا ہے۔ براؤزر زوم پر انحصار کرنے والے صارفین کو مواد تک رسائی میں دشواری ہو سکتی ہے۔",
    position: "وجیٹ کی پوزیشن",
    topLeft: "اوپر بائیں",
    topRight: "اوپر دائیں",
    bottomLeft: "نیچے بائیں",
    bottomRight: "نیچے دائیں",
    profiles: "پروفائل",
    profileNamePlaceholder: "پروفائل کا نام…",
    saveProfile: "محفوظ کریں",
    loadProfile: "لوڈ کریں",
    deleteProfile: "حذف کریں",
    noProfiles: "کوئی محفوظ پروفائل نہیں",
    accessibilityStatementLink: "رسائی کا بیان",
    resetAll: '\u0633\u0628 \u0631\u06CC\u0633\u06CC\u0679 \u06A9\u0631\u06CC\u06BA',
    closeMenu: '\u0645\u06CC\u0646\u0648 \u0628\u0646\u062F \u06A9\u0631\u06CC\u06BA',
    language: '\u0632\u0628\u0627\u0646',
    disclaimer:
      '\u06CC\u06C1 \u0648\u0688\u062C\u0679 \u0645\u06A9\u0645\u0644 \u0631\u0633\u0627\u0626\u06CC \u0627\u0646\u0637\u0628\u0627\u0642 \u06A9\u06CC \u0636\u0645\u0627\u0646\u062A \u0646\u06C1\u06CC\u06BA \u062F\u06CC\u062A\u0627\u0964',
  },

  tr: {
    nativeName: 'T\u00FCrk\u00E7e',
    menuTitle: 'Eri\u015Filebilirlik Men\u00FCs\u00FC',
    highContrast: 'Y\u00FCksek Kontrast',
    darkMode: 'Karanl\u0131k Mod',
    fontSize: 'Yaz\u0131 Boyutu',
    increaseFontSize: 'Yaz\u0131 Boyutunu Art\u0131r',
    decreaseFontSize: 'Yaz\u0131 Boyutunu Azalt',
    resetFontSize: 'Yaz\u0131 Boyutunu S\u0131f\u0131rla',
    dyslexiaFont: 'Disleksi Dostu Yaz\u0131 Tipi',
    underlineLinks: 'Ba\u011Flant\u0131lar\u0131 Alt\u00E7iz',
    focusOutline: 'Odak Kenarl\u0131\u011F\u0131',
    hideImages: 'G\u00F6rselleri Gizle',
    textSpacing: 'Metin Aral\u0131\u011F\u0131',
    pauseAnimations: 'Animasyonlar\u0131 Duraklat',
    largeCursor: 'B\u00FCy\u00FCk \u0130mle\u00E7',
    highlightHeadings: 'Ba\u015Fl\u0131klar\u0131 Vurgula',
    invertColors: 'Renkleri Ters \u00C7evir',
    readingGuide: 'Okuma K\u0131lavuzu',
    textToSpeech: 'Metinden Sese',
    focusMode: 'Odak Modu',
    lineHeight: 'Satır Yüksekliği',
    letterSpacing: 'Harf Aralığı',
    wordSpacing: 'Kelime Aralığı',
    visual: 'Görsel',
    content: 'İçerik',
    navigation: 'Gezinme',
    deuteranopia: 'Deuteranopi (yeşil körlüğü)',
    protanopia: 'Protanopi (kırmızı körlüğü)',
    tritanopia: 'Tritanopi (mavi körlüğü)',
    highlightHover: 'Üzerine gelince vurgula',
    ttsPause: 'Okumayı duraklat',
    ttsResume: 'Okumaya devam et',
    ttsSpeed: 'Okuma hızı',
    ttsSlower: 'Daha yavaş',
    ttsFaster: 'Daha hızlı',
    quickStart: 'Hızlı Başlangıç',
    presetLowVision: 'Düşük Görme',
    presetDyslexia: 'Disleksi Dostu',
    presetAdhd: 'DEHB / Bilişsel',
    presetMotor: 'Motor / Klavye',
    presetMigraine: 'Migrene Güvenli',
    settingsActive: 'ayarlar etkin',
    resetConfirmation: 'Tüm erişilebilirlik ayarları sıfırlandı',
    featureEnabled: 'etkinleştirildi',
    featureDisabled: 'devre dışı bırakıldı',
    tooltipMessage: 'Erişilebilirlik ayarlarınızı özelleştirin',
    increaseLineHeight: "Satır yüksekliğini artır",
    decreaseLineHeight: "Satır yüksekliğini azalt",
    increaseLetterSpacing: "Harf aralığını artır",
    decreaseLetterSpacing: "Harf aralığını azalt",
    increaseWordSpacing: "Kelime aralığını artır",
    decreaseWordSpacing: "Kelime aralığını azalt",
    reducedTransparency: "Azaltılmış şeffaflık",
    sensoryFriendly: "Duyusal dostu",
    readableFont: "Okunabilir yazı tipi",
    saturation: "Doygunluk",
    increaseSaturation: "Doygunluğu artır",
    decreaseSaturation: "Doygunluğu azalt",
    brightness: "Parlaklık",
    increaseBrightness: "Parlaklığı artır",
    decreaseBrightness: "Parlaklığı azalt",
    suppressNotifications: "Bildirimleri engelle",
    devAuditBadge: "Geliştirici Denetimi",
    devAuditNoIssues: "Alt metin sorunu bulunamadı",
    devAuditIssues: "alt metin sorunu bulundu",
    zoomLockWarning: "⚠️ Bu sayfa yakınlaştırma hareketini devre dışı bırakmış. Tarayıcı yakınlaştırmasına bağımlı kullanıcılar içeriğe erişmekte güçlük çekebilir.",
    position: "Widget konumu",
    topLeft: "Sol üst",
    topRight: "Sağ üst",
    bottomLeft: "Sol alt",
    bottomRight: "Sağ alt",
    profiles: "Profiller",
    profileNamePlaceholder: "Profil adı…",
    saveProfile: "Kaydet",
    loadProfile: "Yükle",
    deleteProfile: "Sil",
    noProfiles: "Kayıtlı profil yok",
    accessibilityStatementLink: "Erişilebilirlik Beyanı",
    resetAll: 'T\u00FCm\u00FCn\u00FC S\u0131f\u0131rla',
    closeMenu: 'Men\u00FCleri Kapat',
    language: 'Dil',
    disclaimer:
      'Bu widget tam eri\u015Filebilirlik uyumunu garanti etmez.',
  },

  vi: {
    nativeName: 'Ti\u1EBFng Vi\u1EC7t',
    menuTitle: 'Menu Ti\u1EBFp c\u1EADn',
    highContrast: '\u0110\u1ED9 t\u01B0\u01A1ng ph\u1EA3n cao',
    darkMode: 'Ch\u1EBF \u0111\u1ED9 t\u1ED1i',
    fontSize: 'C\u1EE1 ch\u1EEF',
    increaseFontSize: 'T\u0103ng c\u1EE1 ch\u1EEF',
    decreaseFontSize: 'Gi\u1EA3m c\u1EE1 ch\u1EEF',
    resetFontSize: '\u0110\u1EB7t l\u1EA1i c\u1EE1 ch\u1EEF',
    dyslexiaFont: 'Ph\u00F4ng ch\u1EEF h\u1ED7 tr\u1EE3 ch\u1EE9ng kh\u00F3 \u0111\u1ECDc',
    underlineLinks: 'G\u1EA1ch ch\u00E2n li\u00EAn k\u1EBFt',
    focusOutline: 'Vi\u1EC1n ti\u00EAu \u0111i\u1EC3m',
    hideImages: '\u1EA8n h\u00ECnh \u1EA3nh',
    textSpacing: 'Gi\u00E3n c\u00E1ch v\u0103n b\u1EA3n',
    pauseAnimations: 'T\u1EA1m d\u1EEBng ho\u1EA1t \u1EA3nh',
    largeCursor: 'Con tr\u1ECF l\u1EDBn',
    highlightHeadings: 'T\u00F4 s\u00E1ng ti\u00EAu \u0111\u1EC1',
    invertColors: '\u0110\u1EA3o ng\u01B0\u1EE3c m\u00E0u s\u1EAFc',
    readingGuide: 'H\u01B0\u1EDBng d\u1EABn \u0111\u1ECDc',
    textToSpeech: 'V\u0103n b\u1EA3n th\u00E0nh gi\u1ECDng n\u00F3i',
    focusMode: 'Chế độ tập trung',
    lineHeight: 'Chiều cao dòng',
    letterSpacing: 'Khoảng cách chữ',
    wordSpacing: 'Khoảng cách từ',
    visual: 'Trực quan',
    content: 'Nội dung',
    navigation: 'Điều hướng',
    deuteranopia: 'Mù xanh lá',
    protanopia: 'Mù đỏ',
    tritanopia: 'Mù xanh da trời',
    highlightHover: 'Đánh dấu khi di chuột',
    ttsPause: 'Tạm dừng đọc',
    ttsResume: 'Tiếp tục đọc',
    ttsSpeed: 'Tốc độ đọc',
    ttsSlower: 'Chậm hơn',
    ttsFaster: 'Nhanh hơn',
    quickStart: 'Bắt đầu nhanh',
    presetLowVision: 'Thị lực kém',
    presetDyslexia: 'Thân thiện với chứng khó đọc',
    presetAdhd: 'ADHD / Nhận thức',
    presetMotor: 'Vận động / Bàn phím',
    presetMigraine: 'An toàn với đau nửa đầu',
    settingsActive: 'cài đặt đang hoạt động',
    resetConfirmation: 'Tất cả cài đặt khả năng truy cập đã được đặt lại',
    featureEnabled: 'đã bật',
    featureDisabled: 'đã tắt',
    tooltipMessage: 'Tùy chỉnh cài đặt khả năng truy cập của bạn',
    increaseLineHeight: "Tăng chiều cao dòng",
    decreaseLineHeight: "Giảm chiều cao dòng",
    increaseLetterSpacing: "Tăng khoảng cách chữ cái",
    decreaseLetterSpacing: "Giảm khoảng cách chữ cái",
    increaseWordSpacing: "Tăng khoảng cách từ",
    decreaseWordSpacing: "Giảm khoảng cách từ",
    reducedTransparency: "Giảm độ trong suốt",
    sensoryFriendly: "Thân thiện với giác quan",
    readableFont: "Phông chữ dễ đọc",
    saturation: "Độ bão hòa",
    increaseSaturation: "Tăng độ bão hòa",
    decreaseSaturation: "Giảm độ bão hòa",
    brightness: "Độ sáng",
    increaseBrightness: "Tăng độ sáng",
    decreaseBrightness: "Giảm độ sáng",
    suppressNotifications: "Tắt thông báo",
    devAuditBadge: "Kiểm tra nhà phát triển",
    devAuditNoIssues: "Không tìm thấy vấn đề về văn bản thay thế",
    devAuditIssues: "vấn đề về văn bản thay thế được tìm thấy",
    zoomLockWarning: "⚠️ Trang này đã tắt tính năng phóng to bằng cử chỉ. Người dùng phụ thuộc vào zoom trình duyệt có thể gặp khó khăn khi truy cập nội dung.",
    position: "Vị trí widget",
    topLeft: "Trên trái",
    topRight: "Trên phải",
    bottomLeft: "Dưới trái",
    bottomRight: "Dưới phải",
    profiles: "Hồ sơ",
    profileNamePlaceholder: "Tên hồ sơ…",
    saveProfile: "Lưu",
    loadProfile: "Tải",
    deleteProfile: "Xóa",
    noProfiles: "Không có hồ sơ đã lưu",
    accessibilityStatementLink: "Tuyên bố về khả năng tiếp cận",
    resetAll: '\u0110\u1EB7t l\u1EA1i t\u1EA5t c\u1EA3',
    closeMenu: '\u0110\u00F3ng menu',
    language: 'Ng\u00F4n ng\u1EEF',
    disclaimer:
      'Widget n\u00E0y kh\u00F4ng \u0111\u1EA3m b\u1EA3o tu\u00E2n th\u1EE7 \u0111\u1EA7y \u0111\u1EE7 v\u1EC1 kh\u1EA3 n\u0103ng ti\u1EBFp c\u1EADn.',
  },

  ko: {
    nativeName: '\ud55c\uad6d\uc5b4',
    menuTitle: '\uc811\uadfc\uc131 \uba54\ub274',
    highContrast: '\uace0\ub300\ube44',
    darkMode: '\ub2e4\ud06c \ubaa8\ub4dc',
    fontSize: '\uae00\uc790 \ud06c\uae30',
    increaseFontSize: '\uae00\uc790 \ud06c\uae30 \ub298\ub9ac\uae30',
    decreaseFontSize: '\uae00\uc790 \ud06c\uae30 \uc904\uc774\uae30',
    resetFontSize: '\uae00\uc790 \ud06c\uae30 \ucd08\uae30\ud654',
    dyslexiaFont: '\ub09c\ub3c5\uc99d \uce5c\ud654\uc801 \uae00\uaf34',
    underlineLinks: '\ub9c1\ud06c \ubc11\uc904',
    focusOutline: '\ud3ec\ucee4\uc2a4 \uc6f8\ub77c\uc778',
    hideImages: '\uc774\ubbf8\uc9c0 \uc228\uae30\uae30',
    textSpacing: '\ud14d\uc2a4\ud2b8 \uac04\uaca9',
    pauseAnimations: '\uc560\ub2c8\uba54\uc774\uc158 \uc77c\uc2dc\uc815\uc9c0',
    largeCursor: '\ud070 \ucee4\uc11c',
    highlightHeadings: '\uc81c\ubaa9 \uac15\uc870',
    invertColors: '\uc0c9\uc0c1 \ubc18\uc804',
    readingGuide: '\uc77d\uae30 \uc548\ub0b4\uc120',
    textToSpeech: '\ud14d\uc2a4\ud2b8 \uc74c\uc131 \ubcc0\ud658',
    focusMode: '집중 모드',
    lineHeight: '줄 높이',
    letterSpacing: '자간',
    wordSpacing: '단어 간격',
    visual: '시각',
    content: '콘텐츠',
    navigation: '네비게이션',
    deuteranopia: '녹색 일색성 시각',
    protanopia: '빨간색 일색성 시각',
    tritanopia: '파란색 일색성 시각',
    highlightHover: '마우스 오버 시 강조',
    ttsPause: '읽기 일시정지',
    ttsResume: '읽기 재개',
    ttsSpeed: '읽기 속도',
    ttsSlower: '더 느리게',
    ttsFaster: '더 빠르게',
    quickStart: '빠른 시작',
    presetLowVision: '저시력',
    presetDyslexia: '난독증 친화적',
    presetAdhd: 'ADHD / 인지',
    presetMotor: '운동 / 키보드',
    presetMigraine: '편두통 안전',
    settingsActive: '활성 설정',
    resetConfirmation: '모든 접근성 설정이 초기화되었습니다',
    featureEnabled: '활성화됨',
    featureDisabled: '비활성화됨',
    tooltipMessage: '접근성 설정을 사용자 지정하세요',
    increaseLineHeight: "줄 높이 늘리기",
    decreaseLineHeight: "줄 높이 줄이기",
    increaseLetterSpacing: "자간 늘리기",
    decreaseLetterSpacing: "자간 줄이기",
    increaseWordSpacing: "단어 간격 늘리기",
    decreaseWordSpacing: "단어 간격 줄이기",
    reducedTransparency: "투명도 낮추기",
    sensoryFriendly: "감각 친화적",
    readableFont: "읽기 쉬운 글꼴",
    saturation: "채도",
    increaseSaturation: "채도 높이기",
    decreaseSaturation: "채도 낮추기",
    brightness: "밝기",
    increaseBrightness: "밝기 높이기",
    decreaseBrightness: "밝기 낮추기",
    suppressNotifications: "알림 숨기기",
    devAuditBadge: "개발자 감사",
    devAuditNoIssues: "alt 텍스트 문제가 없습니다",
    devAuditIssues: "alt 텍스트 문제 발견",
    zoomLockWarning: "⚠️ 이 페이지는 핀치 줌을 비활성화했습니다. 브라우저 확대/축소에 의존하는 사용자는 콘텐츠에 접근하는 데 어려움을 겪을 수 있습니다.",
    position: "위젯 위치",
    topLeft: "왼쪽 상단",
    topRight: "오른쪽 상단",
    bottomLeft: "왼쪽 하단",
    bottomRight: "오른쪽 하단",
    profiles: "프로필",
    profileNamePlaceholder: "프로필 이름…",
    saveProfile: "저장",
    loadProfile: "불러오기",
    deleteProfile: "삭제",
    noProfiles: "저장된 프로필 없음",
    accessibilityStatementLink: "접근성 성명",
    resetAll: '\ubaa8\ub450 \ucd08\uae30\ud654',
    closeMenu: '\uba54\ub274 \ub2eb\uae30',
    language: '\uc5b8\uc5b4',
    disclaimer:
      '\uc774 \uc704\uc82f\uc740 \uc644\uc804\ud55c \uc811\uadfc\uc131 \uc900\uc218\ub97c \ubcf4\uc7a5\ud558\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
  },

  it: {
    nativeName: 'Italiano',
    menuTitle: 'Menu di accessibilit\u00E0',
    highContrast: 'Alto contrasto',
    darkMode: 'Modalit\u00E0 scura',
    fontSize: 'Dimensione carattere',
    increaseFontSize: 'Aumenta carattere',
    decreaseFontSize: 'Riduci carattere',
    resetFontSize: 'Reimposta carattere',
    dyslexiaFont: 'Carattere per dislessici',
    underlineLinks: 'Sottolinea i link',
    focusOutline: 'Contorno di messa a fuoco',
    hideImages: 'Nascondi immagini',
    textSpacing: 'Spaziatura del testo',
    pauseAnimations: 'Metti in pausa le animazioni',
    largeCursor: 'Cursore grande',
    highlightHeadings: 'Evidenzia i titoli',
    invertColors: 'Inverti colori',
    readingGuide: 'Guida alla lettura',
    textToSpeech: 'Da testo a voce',
    focusMode: 'Modalità focus',
    lineHeight: 'Altezza riga',
    letterSpacing: 'Spaziatura lettere',
    wordSpacing: 'Spaziatura parole',
    visual: 'Visivo',
    content: 'Contenuto',
    navigation: 'Navigazione',
    deuteranopia: 'Deuteranopia (cecità verde)',
    protanopia: 'Protanopia (cecità rossa)',
    tritanopia: 'Tritanopia (cecità blu)',
    highlightHover: 'Evidenzia al passaggio',
    ttsPause: 'Pausa lettura',
    ttsResume: 'Riprendi lettura',
    ttsSpeed: 'Velocità lettura',
    ttsSlower: 'Più lento',
    ttsFaster: 'Più veloce',
    quickStart: 'Avvio rapido',
    presetLowVision: 'Ipovisione',
    presetDyslexia: 'Adatto alla dislessia',
    presetAdhd: 'ADHD / Cognitivo',
    presetMotor: 'Motorio / Tastiera',
    presetMigraine: 'Sicuro per emicrania',
    settingsActive: 'impostazioni attive',
    resetConfirmation: 'Tutte le impostazioni di accessibilità sono state reimpostate',
    featureEnabled: 'abilitato',
    featureDisabled: 'disabilitato',
    tooltipMessage: 'Personalizza le tue impostazioni di accessibilità',
    increaseLineHeight: "Aumenta altezza riga",
    decreaseLineHeight: "Riduci altezza riga",
    increaseLetterSpacing: "Aumenta spaziatura lettere",
    decreaseLetterSpacing: "Riduci spaziatura lettere",
    increaseWordSpacing: "Aumenta spaziatura parole",
    decreaseWordSpacing: "Riduci spaziatura parole",
    reducedTransparency: "Trasparenza ridotta",
    sensoryFriendly: "Adatto ai sensi",
    readableFont: "Font leggibile",
    saturation: "Saturazione",
    increaseSaturation: "Aumenta saturazione",
    decreaseSaturation: "Riduci saturazione",
    brightness: "Luminosità",
    increaseBrightness: "Aumenta luminosità",
    decreaseBrightness: "Riduci luminosità",
    suppressNotifications: "Sopprimi notifiche",
    devAuditBadge: "Audit sviluppatore",
    devAuditNoIssues: "Nessun problema di testo alternativo trovato",
    devAuditIssues: "problemi di testo alternativo trovati",
    zoomLockWarning: "⚠️ Questa pagina ha disabilitato il pinch-to-zoom. Gli utenti che dipendono dallo zoom del browser potrebbero avere difficoltà ad accedere ai contenuti.",
    position: "Posizione widget",
    topLeft: "In alto a sinistra",
    topRight: "In alto a destra",
    bottomLeft: "In basso a sinistra",
    bottomRight: "In basso a destra",
    profiles: "Profili",
    profileNamePlaceholder: "Nome profilo…",
    saveProfile: "Salva",
    loadProfile: "Carica",
    deleteProfile: "Elimina",
    noProfiles: "Nessun profilo salvato",
    accessibilityStatementLink: "Dichiarazione di accessibilità",
    resetAll: 'Reimposta tutto',
    closeMenu: 'Chiudi il menu',
    language: 'Lingua',
    disclaimer:
      'Questo widget non garantisce la piena conformit\u00E0 alle norme di accessibilit\u00E0.',
  },

  fa: {
    nativeName: '\u0641\u0627\u0631\u0633\u06CC',
    menuTitle: '\u0645\u0646\u0648\u06CC \u062F\u0633\u062A\u0631\u0633\u06CC\u200C\u067E\u0630\u06CC\u0631\u06CC',
    highContrast: '\u06A9\u0646\u062A\u0631\u0627\u0633\u062A \u0628\u0627\u0644\u0627',
    darkMode: '\u062D\u0627\u0644\u062A \u062A\u0627\u0631\u06CC\u06A9',
    fontSize: '\u0627\u0646\u062F\u0627\u0632\u0647 \u0642\u0644\u0645',
    increaseFontSize: '\u0627\u0641\u0632\u0627\u06CC\u0634 \u0627\u0646\u062F\u0627\u0632\u0647 \u0642\u0644\u0645',
    decreaseFontSize: '\u06A9\u0627\u0647\u0634 \u0627\u0646\u062F\u0627\u0632\u0647 \u0642\u0644\u0645',
    resetFontSize: '\u0628\u0627\u0632\u0646\u0634\u0627\u0646\u06CC \u0627\u0646\u062F\u0627\u0632\u0647 \u0642\u0644\u0645',
    dyslexiaFont: '\u0642\u0644\u0645 \u0645\u0646\u0627\u0633\u0628 \u0646\u0627\u0631\u0633\u0627\u062E\u0648\u0627\u0646\u06CC',
    underlineLinks: '\u062E\u0637 \u0632\u06CC\u0631 \u067E\u06CC\u0648\u0646\u062F\u0647\u0627',
    focusOutline: '\u06A9\u0627\u062F\u0631 \u062A\u0645\u0631\u06A9\u0632',
    hideImages: '\u067E\u0646\u0647\u0627\u0646 \u06A9\u0631\u062F\u0646 \u062A\u0635\u0627\u0648\u06CC\u0631',
    textSpacing: '\u0641\u0627\u0635\u0644\u0647 \u0645\u062A\u0646',
    pauseAnimations: '\u062A\u0648\u0642\u0641 \u0627\u0646\u06CC\u0645\u06CC\u0634\u0646\u200C\u0647\u0627',
    largeCursor: '\u0645\u06A9\u0627\u0646\u0646\u0645\u0627\u06CC \u0628\u0632\u0631\u06AF',
    highlightHeadings: '\u0628\u0631\u062C\u0633\u062A\u0647\u200C\u0633\u0627\u0632\u06CC \u0639\u0646\u0627\u0648\u06CC\u0646',
    invertColors: '\u0645\u0639\u06A9\u0648\u0633 \u06A9\u0631\u062F\u0646 \u0631\u0646\u06AF\u200C\u0647\u0627',
    readingGuide: '\u0631\u0627\u0647\u0646\u0645\u0627\u06CC \u062E\u0648\u0627\u0646\u062F\u0646',
    textToSpeech: '\u062A\u0628\u062F\u06CC\u0644 \u0645\u062A\u0646 \u0628\u0647 \u06AF\u0641\u062A\u0627\u0631',
    focusMode: 'حالت تمرکز',
    lineHeight: 'ارتفاع خط',
    letterSpacing: 'فاصله حروف',
    wordSpacing: 'فاصله کلمات',
    visual: 'بصری',
    content: 'محتوا',
    navigation: 'ناوبری',
    deuteranopia: 'کوری سبز',
    protanopia: 'کوری قرمز',
    tritanopia: 'کوری آبی',
    highlightHover: 'برجسته‌سازی با نشانگر',
    ttsPause: 'مکث خواندن',
    ttsResume: 'ادامه خواندن',
    ttsSpeed: 'سرعت خواندن',
    ttsSlower: 'کندتر',
    ttsFaster: 'سریع‌تر',
    quickStart: 'شروع سریع',
    presetLowVision: 'کم‌بینی',
    presetDyslexia: 'دوستانه‌ با دیسلکسی',
    presetAdhd: 'بیش‌فعالی / شناختی',
    presetMotor: 'حرکتی / کیبورد',
    presetMigraine: 'ایمن برای میگرن',
    settingsActive: 'تنظیمات فعال',
    resetConfirmation: 'تمام تنظیمات دسترسی‌پذیری بازنشانی شدند',
    featureEnabled: 'فعال',
    featureDisabled: 'غیرفعال',
    tooltipMessage: 'تنظیمات دسترسی‌پذیری خود را سفارشی‌سازی کنید',
    increaseLineHeight: "افزایش ارتفاع خط",
    decreaseLineHeight: "کاهش ارتفاع خط",
    increaseLetterSpacing: "افزایش فاصله حروف",
    decreaseLetterSpacing: "کاهش فاصله حروف",
    increaseWordSpacing: "افزایش فاصله کلمات",
    decreaseWordSpacing: "کاهش فاصله کلمات",
    reducedTransparency: "شفافیت کاهش‌یافته",
    sensoryFriendly: "مناسب برای حواس",
    readableFont: "فونت خوانا",
    saturation: "اشباع رنگ",
    increaseSaturation: "افزایش اشباع رنگ",
    decreaseSaturation: "کاهش اشباع رنگ",
    brightness: "روشنایی",
    increaseBrightness: "افزایش روشنایی",
    decreaseBrightness: "کاهش روشنایی",
    suppressNotifications: "حذف اعلان‌ها",
    devAuditBadge: "حسابرسی توسعه‌دهنده",
    devAuditNoIssues: "هیچ مشکلی در متن جایگزین یافت نشد",
    devAuditIssues: "مشکل در متن جایگزین یافت شد",
    zoomLockWarning: "⚠️ این صفحه بزرگ‌نمایی با انگشت را غیرفعال کرده است. کاربرانی که به بزرگ‌نمایی مرورگر وابسته هستند ممکن است در دسترسی به محتوا مشکل داشته باشند.",
    position: "موقعیت ابزارک",
    topLeft: "بالا چپ",
    topRight: "بالا راست",
    bottomLeft: "پایین چپ",
    bottomRight: "پایین راست",
    profiles: "پروفایل‌ها",
    profileNamePlaceholder: "نام پروفایل…",
    saveProfile: "ذخیره",
    loadProfile: "بارگذاری",
    deleteProfile: "حذف",
    noProfiles: "هیچ پروفایل ذخیره‌شده‌ای وجود ندارد",
    accessibilityStatementLink: "بیانیه دسترس‌پذیری",
    resetAll: '\u0628\u0627\u0632\u0646\u0634\u0627\u0646\u06CC \u0647\u0645\u0647',
    closeMenu: '\u0628\u0633\u062A\u0646 \u0645\u0646\u0648',
    language: '\u0632\u0628\u0627\u0646',
    disclaimer:
      '\u0627\u06CC\u0646 \u0627\u0628\u0632\u0627\u0631\u06A9 \u062A\u0636\u0645\u06CC\u0646 \u0631\u0639\u0627\u06CC\u062A \u06A9\u0627\u0645\u0644 \u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F\u0647\u0627\u06CC \u062F\u0633\u062A\u0631\u0633\u06CC\u200C\u067E\u0630\u06CC\u0631\u06CC \u0631\u0627 \u0646\u0645\u06CC\u200C\u062F\u0647\u062F.',
  },

  th: {
    nativeName: '\u0E44\u0E17\u0E22',
    menuTitle: '\u0E40\u0E21\u0E19\u0E39\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E16\u0E36\u0E07',
    highContrast: '\u0E04\u0E2D\u0E19\u0E17\u0E23\u0E32\u0E2A\u0E15\u0E4C\u0E2A\u0E39\u0E07',
    darkMode: '\u0E42\u0E2B\u0E21\u0E14\u0E21\u0E37\u0E14',
    fontSize: '\u0E02\u0E19\u0E32\u0E14\u0E15\u0E31\u0E27\u0E2D\u0E31\u0E01\u0E29\u0E23',
    increaseFontSize: '\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E02\u0E19\u0E32\u0E14\u0E15\u0E31\u0E27\u0E2D\u0E31\u0E01\u0E29\u0E23',
    decreaseFontSize: '\u0E25\u0E14\u0E02\u0E19\u0E32\u0E14\u0E15\u0E31\u0E27\u0E2D\u0E31\u0E01\u0E29\u0E23',
    resetFontSize: '\u0E23\u0E35\u0E40\u0E0B\u0E47\u0E15\u0E02\u0E19\u0E32\u0E14\u0E15\u0E31\u0E27\u0E2D\u0E31\u0E01\u0E29\u0E23',
    dyslexiaFont: '\u0E41\u0E1A\u0E1A\u0E2D\u0E31\u0E01\u0E29\u0E23\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E1C\u0E39\u0E49\u0E1B\u0E48\u0E27\u0E22\u0E14\u0E34\u0E2A\u0E40\u0E25\u0E47\u0E01\u0E40\u0E0B\u0E35\u0E22',
    underlineLinks: '\u0E02\u0E35\u0E14\u0E40\u0E2A\u0E49\u0E19\u0E43\u0E15\u0E49\u0E25\u0E34\u0E07\u0E01\u0E4C',
    focusOutline: '\u0E40\u0E2A\u0E49\u0E19\u0E42\u0E1F\u0E01\u0E31\u0E2A',
    hideImages: '\u0E0B\u0E48\u0E2D\u0E19\u0E23\u0E39\u0E1B\u0E20\u0E32\u0E1E',
    textSpacing: '\u0E23\u0E30\u0E22\u0E30\u0E2B\u0E48\u0E32\u0E07\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21',
    pauseAnimations: '\u0E2B\u0E22\u0E38\u0E14\u0E41\u0E2D\u0E19\u0E34\u0E40\u0E21\u0E0A\u0E31\u0E19',
    largeCursor: '\u0E40\u0E04\u0E2D\u0E23\u0E4C\u0E40\u0E0B\u0E2D\u0E23\u0E4C\u0E02\u0E19\u0E32\u0E14\u0E43\u0E2B\u0E0D\u0E48',
    highlightHeadings: '\u0E44\u0E2E\u0E44\u0E25\u0E15\u0E4C\u0E2B\u0E31\u0E27\u0E40\u0E23\u0E37\u0E48\u0E2D\u0E07',
    invertColors: '\u0E2A\u0E25\u0E31\u0E1A\u0E2A\u0E35',
    readingGuide: '\u0E04\u0E39\u0E48\u0E21\u0E37\u0E2D\u0E01\u0E32\u0E23\u0E2D\u0E48\u0E32\u0E19',
    textToSpeech: '\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E40\u0E2A\u0E35\u0E22\u0E07',
    focusMode: 'โหมดโฟกัส',
    lineHeight: 'ความสูงบรรทัด',
    letterSpacing: 'ระยะห่างตัวอักษร',
    wordSpacing: 'ระยะห่างคำ',
    visual: 'ภาพ',
    content: 'เนื้อหา',
    navigation: 'การนำทาง',
    deuteranopia: 'ตาบอดสีเขียว',
    protanopia: 'ตาบอดสีแดง',
    tritanopia: 'ตาบอดสีน้ำเงิน',
    highlightHover: 'ไฮไลต์เมื่อวางเมาส์',
    ttsPause: 'หยุดอ่านชั่วคราว',
    ttsResume: 'อ่านต่อ',
    ttsSpeed: 'ความเร็วในการอ่าน',
    ttsSlower: 'ช้าลง',
    ttsFaster: 'เร็วขึ้น',
    quickStart: 'เริ่มต้นอย่างรวดเร็ว',
    presetLowVision: 'สายตาเลือนราง',
    presetDyslexia: 'เป็นมิตรกับดิสเล็กเซีย',
    presetAdhd: 'สมาธิสั้น / ความรู้ความเข้าใจ',
    presetMotor: 'การเคลื่อนไหว / แป้นพิมพ์',
    presetMigraine: 'ปลอดภัยสำหรับไมเกรน',
    settingsActive: 'การตั้งค่าที่ใช้งาน',
    resetConfirmation: 'รีเซ็ตการตั้งค่าการเข้าถึงทั้งหมดแล้ว',
    featureEnabled: 'เปิดใช้งาน',
    featureDisabled: 'ปิดใช้งาน',
    tooltipMessage: 'ปรับแต่งการตั้งค่าการเข้าถึงของคุณ',
    increaseLineHeight: "เพิ่มความสูงบรรทัด",
    decreaseLineHeight: "ลดความสูงบรรทัด",
    increaseLetterSpacing: "เพิ่มระยะห่างอักษร",
    decreaseLetterSpacing: "ลดระยะห่างอักษร",
    increaseWordSpacing: "เพิ่มระยะห่างคำ",
    decreaseWordSpacing: "ลดระยะห่างคำ",
    reducedTransparency: "ลดความโปร่งใส",
    sensoryFriendly: "เป็นมิตรกับประสาทสัมผัส",
    readableFont: "แบบอักษรที่อ่านง่าย",
    saturation: "ความอิ่มตัวของสี",
    increaseSaturation: "เพิ่มความอิ่มตัวของสี",
    decreaseSaturation: "ลดความอิ่มตัวของสี",
    brightness: "ความสว่าง",
    increaseBrightness: "เพิ่มความสว่าง",
    decreaseBrightness: "ลดความสว่าง",
    suppressNotifications: "ปิดการแจ้งเตือน",
    devAuditBadge: "ตรวจสอบนักพัฒนา",
    devAuditNoIssues: "ไม่พบปัญหา alt text",
    devAuditIssues: "พบปัญหา alt text",
    zoomLockWarning: "⚠️ หน้านี้ได้ปิดใช้งานการบีบนิ้วเพื่อซูม ผู้ใช้ที่พึ่งพาการซูมของเบราว์เซอร์อาจมีปัญหาในการเข้าถึงเนื้อหา",
    position: "ตำแหน่งวิดเจ็ต",
    topLeft: "บนซ้าย",
    topRight: "บนขวา",
    bottomLeft: "ล่างซ้าย",
    bottomRight: "ล่างขวา",
    profiles: "โปรไฟล์",
    profileNamePlaceholder: "ชื่อโปรไฟล์…",
    saveProfile: "บันทึก",
    loadProfile: "โหลด",
    deleteProfile: "ลบ",
    noProfiles: "ไม่มีโปรไฟล์ที่บันทึกไว้",
    accessibilityStatementLink: "คำชี้แจงการเข้าถึง",
    resetAll: '\u0E23\u0E35\u0E40\u0E0B\u0E47\u0E15\u0E17\u0E31\u0E49\u0E07\u0E2B\u0E21\u0E14',
    closeMenu: '\u0E1B\u0E34\u0E14\u0E40\u0E21\u0E19\u0E39',
    language: '\u0E20\u0E32\u0E29\u0E32',
    disclaimer:
      '\u0E27\u0E34\u0E14\u0E40\u0E08\u0E47\u0E15\u0E19\u0E35\u0E49\u0E44\u0E21\u0E48\u0E23\u0E31\u0E1A\u0E1B\u0E23\u0E30\u0E01\u0E31\u0E19\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E16\u0E36\u0E07\u0E17\u0E35\u0E48\u0E2A\u0E21\u0E1A\u0E39\u0E23\u0E13\u0E4C\u0E2D\u0E22\u0E48\u0E32\u0E07\u0E04\u0E23\u0E1A\u0E16\u0E49\u0E27\u0E19',
  },

  ta: {
    nativeName: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD',
    menuTitle: '\u0B85\u0BA3\u0BC1\u0B95\u0BB2\u0BCD \u0BAE\u0BC6\u0BA9\u0BC1',
    highContrast: '\u0B89\u0BAF\u0BB0\u0BCD \u0BAE\u0BBE\u0BB1\u0BC1\u0BAA\u0BBE\u0B9F\u0BC1',
    darkMode: '\u0B87\u0BB0\u0BC1\u0BB3\u0BCD \u0BAE\u0BCB\u0B9F\u0BC1',
    fontSize: '\u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1 \u0B85\u0BB3\u0BB5\u0BC1',
    increaseFontSize: '\u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1 \u0B85\u0BB3\u0BB5\u0BC8 \u0BAA\u0BC6\u0BB0\u0BBF\u0BA4\u0BBE\u0B95\u0BCD\u0B95\u0BC1',
    decreaseFontSize: '\u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1 \u0B85\u0BB3\u0BB5\u0BC8 \u0B9A\u0BBF\u0BB1\u0BBF\u0BA4\u0BBE\u0B95\u0BCD\u0B95\u0BC1',
    resetFontSize: '\u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1 \u0B85\u0BB3\u0BB5\u0BC8 \u0BAE\u0BC0\u0B9F\u0BCD\u0B9F\u0BAE\u0BC8',
    dyslexiaFont: '\u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BB2\u0BCD \u0B95\u0BC1\u0BB1\u0BC8\u0BAA\u0BBE\u0B9F\u0BC1 \u0B89\u0BA4\u0BB5\u0BBF \u0B8E\u0BB4\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1\u0BB0\u0BC1',
    underlineLinks: '\u0B87\u0BA3\u0BC8\u0BAA\u0BCD\u0BAA\u0BC1\u0B95\u0BB3\u0BC8 \u0B95\u0BCB\u0B9F\u0BBF\u0B9F\u0BC1',
    focusOutline: '\u0B95\u0BB5\u0BA9 \u0BB5\u0BB0\u0BC8\u0BAF\u0BB1\u0BC8',
    hideImages: '\u0BAA\u0B9F\u0B99\u0BCD\u0B95\u0BB3\u0BC8 \u0BAE\u0BB1\u0BC8',
    textSpacing: '\u0B89\u0BB0\u0BC8 \u0B87\u0B9F\u0BC8\u0BB5\u0BC6\u0BB3\u0BBF',
    pauseAnimations: '\u0B8F\u0BA9\u0BBF\u0BAE\u0BC7\u0BB7\u0BA9\u0BCD\u0B95\u0BB3\u0BC8 \u0B87\u0B9F\u0BC8\u0BA8\u0BBF\u0BB1\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1',
    largeCursor: '\u0BAA\u0BC6\u0BB0\u0BBF\u0BAF \u0B95\u0BB0\u0BCD\u0B9A\u0BB0\u0BCD',
    highlightHeadings: '\u0BA4\u0BB2\u0BC8\u0BAA\u0BCD\u0BAA\u0BC1\u0B95\u0BB3\u0BC8 \u0B8E\u0B9F\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1\u0B95\u0BCD\u0B95\u0BBE\u0B9F\u0BCD\u0B9F\u0BC1',
    invertColors: '\u0BA8\u0BBF\u0BB1\u0B99\u0BCD\u0B95\u0BB3\u0BC8 \u0BA4\u0BBF\u0BB0\u0BC1\u0BAE\u0BCD\u0BAA\u0BC1',
    readingGuide: '\u0BB5\u0BBE\u0B9A\u0BBF\u0BAA\u0BCD\u0BAA\u0BC1 \u0BB5\u0BB4\u0BBF\u0B95\u0BBE\u0B9F\u0BCD\u0B9F\u0BBF',
    textToSpeech: '\u0B89\u0BB0\u0BC8\u0BAF\u0BBF\u0BB2\u0BBF\u0BB0\u0BC1\u0BA8\u0BCD\u0BA4\u0BC1 \u0B89\u0BB0\u0B95\u0BCD\u0B95\u0BC1',
    focusMode: 'கவன பயன்முறை',
    lineHeight: 'வரி உயரம்',
    letterSpacing: 'எழுத்து இடைவெளி',
    wordSpacing: 'சொல் இடைவெளி',
    visual: 'பார்வை',
    content: 'உள்ளடகம்',
    navigation: 'வழிசெலவு',
    deuteranopia: 'Deuteranopia',
    protanopia: 'Protanopia',
    tritanopia: 'Tritanopia',
    highlightHover: 'மிதிகும்போது முன்னிலைப்படுத்து',
    ttsPause: 'வாசிப்பை இடைநிறுத்து',
    ttsResume: 'வாசிப்பை தொடர்',
    ttsSpeed: 'வாசிப்பு வேகம்',
    ttsSlower: 'மெதுவாச',
    ttsFaster: 'வேகமாச',
    quickStart: 'விரைவு தொடக்கம்',
    presetLowVision: 'குறைநத பார்வை',
    presetDyslexia: 'டிஸ்லெக்சியா நட்பு',
    presetAdhd: 'ADHD / અறிவாற்றல்',
    presetMotor: 'மோட்டார் / விசைப்பலகை',
    presetMigraine: 'தலைவிசி பாதுகாப்பு',
    settingsActive: 'அமைப்புகள் சுறுத்துனர்',
    resetConfirmation: 'அனைத்து அணுகல் அமைப்புகளும் மீட்டமைக்கப்பட்டன',
    featureEnabled: 'இயக்கம்',
    featureDisabled: 'இயக்கமற்றம்',
    tooltipMessage: 'உஙகள் அணுகல் அமைப்புகளை தனியாக்குக',
    increaseLineHeight: "வரி உயரத்தை அதிகரிக்கவும்",
    decreaseLineHeight: "வரி உயரத்தை குறைக்கவும்",
    increaseLetterSpacing: "எழுத்து இடைவெளியை அதிகரிக்கவும்",
    decreaseLetterSpacing: "எழுத்து இடைவெளியை குறைக்கவும்",
    increaseWordSpacing: "வார்த்தை இடைவெளியை அதிகரிக்கவும்",
    decreaseWordSpacing: "வார்த்தை இடைவெளியை குறைக்கவும்",
    reducedTransparency: "குறைக்கப்பட்ட வெளிப்படைத்தன்மை",
    sensoryFriendly: "புலன் நட்பு",
    readableFont: "படிக்கக்கூடிய எழுத்துரு",
    saturation: "நிறை",
    increaseSaturation: "நிறையை அதிகரிக்கவும்",
    decreaseSaturation: "நிறையை குறைக்கவும்",
    brightness: "பொலிவு",
    increaseBrightness: "பொலிவை அதிகரிக்கவும்",
    decreaseBrightness: "பொலிவை குறைக்கவும்",
    suppressNotifications: "அறிவிப்புகளை அடக்குக",
    devAuditBadge: "டெவ் ஆடிட்",
    devAuditNoIssues: "alt உரை சிக்கல்கள் எதுவும் கண்டறியவில்லை",
    devAuditIssues: "alt உரை சிக்கல்கள் கண்டறியப்பட்டன",
    zoomLockWarning: "⚠️ இந்தப் பக்கம் பிஞ்ச்-டு-ஜூமை முடக்கியுள்ளது. உலாவி ஜூம் மீது நம்பியிருக்கும் பயனர்களுக்கு உள்ளடக்கத்தை அணுகுவதில் சிரமம் ஏற்படலாம்.",
    position: "விட்ஜெட் நிலை",
    topLeft: "மேல் இடது",
    topRight: "மேல் வலது",
    bottomLeft: "கீழ் இடது",
    bottomRight: "கீழ் வலது",
    profiles: "சுயவிவரங்கள்",
    profileNamePlaceholder: "சுயவிவர பெயர்…",
    saveProfile: "சேமிக்கவும்",
    loadProfile: "ஏற்றவும்",
    deleteProfile: "நீக்கவும்",
    noProfiles: "சேமிக்கப்பட்ட சுயவிவரங்கள் இல்லை",
    accessibilityStatementLink: "அணுகல்தன்மை அறிக்கை",
    resetAll: '\u0B85\u0BA9\u0BC8\u0BA4\u0BCD\u0BA4\u0BC8\u0BAF\u0BC1\u0BAE\u0BCD \u0BAE\u0BC0\u0B9F\u0BCD\u0B9F\u0BAE\u0BC8',
    closeMenu: '\u0BAE\u0BC6\u0BA9\u0BC1\u0BB5\u0BC8 \u0BAE\u0BC2\u0B9F\u0BC1',
    language: '\u0BAE\u0BCA\u0BB4\u0BBF',
    disclaimer:
      '\u0B87\u0BA8\u0BCD\u0BA4 \u0BB5\u0BBF\u0B9F\u0BCD\u0B9C\u0BC6\u0B9F\u0BCD \u0BAE\u0BC1\u0BB4\u0BC1 \u0B85\u0BA3\u0BC1\u0B95\u0BB2\u0BCD \u0B87\u0BA3\u0B99\u0BCD\u0B95\u0BBF\u0BAF\u0BC8\u0BAA\u0BCD\u0BAA\u0BC8 \u0B89\u0BB1\u0BC1\u0BA4\u0BBF\u0BAA\u0B9F\u0BC1\u0BA4\u0BCD\u0BA4\u0BBE\u0BA4\u0BC1.',
  },

  mr: {
    nativeName: '\u092E\u0930\u093E\u0920\u0940',
    menuTitle: '\u092A\u094D\u0930\u0935\u0947\u0936\u092F\u094B\u0917\u094D\u092F\u0924\u093E \u092E\u0947\u0928\u0942',
    highContrast: '\u0909\u091A\u094D\u091A \u0915\u0949\u0928\u094D\u091F\u094D\u0930\u093E\u0938\u094D\u091F',
    darkMode: '\u0917\u0921\u0926 \u092E\u094B\u0921',
    fontSize: '\u092B\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930',
    increaseFontSize: '\u092B\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u0935\u093E\u0922\u0935\u093E',
    decreaseFontSize: '\u092B\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u0915\u092E\u0940 \u0915\u0930\u093E',
    resetFontSize: '\u092B\u0949\u0928\u094D\u091F \u0906\u0915\u093E\u0930 \u0930\u0940\u0938\u0947\u091F \u0915\u0930\u093E',
    dyslexiaFont: '\u0921\u093F\u0938\u094D\u0932\u0947\u0915\u094D\u0938\u093F\u092F\u093E-\u0938\u094D\u0928\u0947\u0939\u0940 \u092B\u0949\u0928\u094D\u091F',
    underlineLinks: '\u0932\u093F\u0902\u0915\u093E\u0902\u0928\u093E \u0905\u0927\u094B\u0930\u0947\u0916\u093E \u0926\u094D\u092F\u093E',
    focusOutline: '\u092B\u094B\u0915\u0938 \u0938\u0940\u092E\u093E\u0930\u0947\u0937\u093E',
    hideImages: '\u091B\u093E\u092F\u093E\u091A\u093F\u0924\u094D\u0930\u0947 \u0932\u092A\u0935\u093E',
    textSpacing: '\u092E\u091C\u0915\u0942\u0930 \u0905\u0902\u0924\u0930',
    pauseAnimations: '\u0905\u0928\u093F\u092E\u0947\u0936\u0928 \u0925\u093E\u0902\u092C\u0935\u093E',
    largeCursor: '\u092E\u094B\u0920\u093E \u0915\u0930\u094D\u0938\u0930',
    highlightHeadings: '\u0936\u0940\u0930\u094D\u0937\u0915\u0947 \u0920\u0933\u0935\u093E',
    invertColors: '\u0930\u0902\u0917 \u0909\u0932\u091F\u0935\u093E',
    readingGuide: '\u0935\u093E\u091A\u0928 \u092E\u093E\u0930\u094D\u0917\u0926\u0930\u094D\u0936\u0915',
    textToSpeech: '\u092E\u091C\u0915\u0942\u0930 \u0924\u0947 \u092D\u093E\u0937\u0923',
    focusMode: 'फोकस मोड',
    lineHeight: 'ओळीची उंची',
    letterSpacing: 'अक्षर अंतर',
    wordSpacing: 'शब्द अंतर',
    visual: 'दृश्य',
    content: 'सामग्री',
    navigation: 'नेविगेशन',
    deuteranopia: 'हिरवे रंगांधता',
    protanopia: 'लाल रंगांधता',
    tritanopia: 'निळे रंगांधता',
    highlightHover: 'होव्हर वर हायलाइट',
    ttsPause: 'वाचन थांबवा',
    ttsResume: 'वाचन सुरू ठेवा',
    ttsSpeed: 'वाचन गती',
    ttsSlower: 'हळू',
    ttsFaster: 'जलद',
    quickStart: 'जलद सुरुवात',
    presetLowVision: 'कमी दृष्टी',
    presetDyslexia: 'डिस्लेक्सिया मित्रत्व',
    presetAdhd: 'एडीएचडी / संज्ञानात्मक',
    presetMotor: 'मोटर / कीबोर्ड',
    presetMigraine: 'मायग्रेन सुरक्षित',
    settingsActive: 'सेटिंग्स सक्रिय',
    resetConfirmation: 'सर्व अभिगम्यता सेटिंग्स रीसेट केल्या',
    featureEnabled: 'सक्षम',
    featureDisabled: 'अक्षम',
    tooltipMessage: 'आपल्या अभिगम्यता सेटिंग्स कस्टमाइझ करा',
    increaseLineHeight: "ओळ उंची वाढवा",
    decreaseLineHeight: "ओळ उंची कमी करा",
    increaseLetterSpacing: "अक्षर अंतर वाढवा",
    decreaseLetterSpacing: "अक्षर अंतर कमी करा",
    increaseWordSpacing: "शब्द अंतर वाढवा",
    decreaseWordSpacing: "शब्द अंतर कमी करा",
    reducedTransparency: "कमी पारदर्शकता",
    sensoryFriendly: "संवेदनशील-अनुकूल",
    readableFont: "वाचनीय फॉन्ट",
    saturation: "संपृक्तता",
    increaseSaturation: "संपृक्तता वाढवा",
    decreaseSaturation: "संपृक्तता कमी करा",
    brightness: "चमक",
    increaseBrightness: "चमक वाढवा",
    decreaseBrightness: "चमक कमी करा",
    suppressNotifications: "सूचना दाबा",
    devAuditBadge: "डेव ऑडिट",
    devAuditNoIssues: "alt मजकूर समस्या आढळल्या नाहीत",
    devAuditIssues: "alt मजकूर समस्या आढळल्या",
    zoomLockWarning: "⚠️ या पृष्ठाने पिंच-टू-झूम अक्षम केले आहे. ब्राउझर झूमवर अवलंबून असलेल्या वापरकर्त्यांना सामग्री अ‍ॅक्सेस करण्यात अडचण येऊ शकते.",
    position: "विजेट स्थिती",
    topLeft: "वरती डावीकडे",
    topRight: "वरती उजवीकडे",
    bottomLeft: "खाली डावीकडे",
    bottomRight: "खाली उजवीकडे",
    profiles: "प्रोफाइल",
    profileNamePlaceholder: "प्रोफाइल नाव…",
    saveProfile: "जतन करा",
    loadProfile: "लोड करा",
    deleteProfile: "हटवा",
    noProfiles: "कोणतेही जतन केलेले प्रोफाइल नाही",
    accessibilityStatementLink: "अ‍ॅक्सेसिबिलिटी स्टेटमेंट",
    resetAll: '\u0938\u0930\u094D\u0935 \u0930\u0940\u0938\u0947\u091F \u0915\u0930\u093E',
    closeMenu: '\u092E\u0947\u0928\u0942 \u092C\u0902\u0926 \u0915\u0930\u093E',
    language: '\u092D\u093E\u0937\u093E',
    disclaimer:
      '\u0939\u093E \u0935\u093F\u091C\u0947\u091F \u0938\u0902\u092A\u0942\u0930\u094D\u0923 \u092A\u094D\u0930\u0935\u0947\u0936\u092F\u094B\u0917\u094D\u092F\u0924\u093E \u0905\u0928\u0941\u092A\u093E\u0932\u0928\u093E\u091A\u0940 \u0939\u092E\u0940 \u0926\u0947\u0924 \u0928\u093E\u0939\u0940.',
  },

  te: {
    nativeName: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41',
    menuTitle: '\u0C2A\u0C4D\u0C30\u0C35\u0C47\u0C36\u0C2F\u0C4B\u0C17\u0C4D\u0C2F\u0C24 \u0C2E\u0C46\u0C28\u0C42',
    highContrast: '\u0C05\u0C27\u0C3F\u0C15 \u0C15\u0C3E\u0C02\u0C1F\u0C4D\u0C30\u0C3E\u0C38\u0C4D\u0C1F\u0C4D',
    darkMode: '\u0C21\u0C3E\u0C30\u0C4D\u0C15\u0C4D \u0C2E\u0C4B\u0C21\u0C4D',
    fontSize: '\u0C05\u0C15\u0C4D\u0C37\u0C30 \u0C2A\u0C30\u0C3F\u0C2E\u0C3E\u0C23\u0C02',
    increaseFontSize: '\u0C05\u0C15\u0C4D\u0C37\u0C30 \u0C2A\u0C30\u0C3F\u0C2E\u0C3E\u0C23\u0C02 \u0C2A\u0C46\u0C02\u0C1A\u0C41',
    decreaseFontSize: '\u0C05\u0C15\u0C4D\u0C37\u0C30 \u0C2A\u0C30\u0C3F\u0C2E\u0C3E\u0C23\u0C02 \u0C24\u0C17\u0C4D\u0C17\u0C3F\u0C02\u0C1A\u0C41',
    resetFontSize: '\u0C05\u0C15\u0C4D\u0C37\u0C30 \u0C2A\u0C30\u0C3F\u0C2E\u0C3E\u0C23\u0C02 \u0C30\u0C40\u0C38\u0C46\u0C1F\u0C4D \u0C1A\u0C47\u0C2F\u0C3F',
    dyslexiaFont: '\u0C21\u0C3F\u0C38\u0C4D\u0C32\u0C46\u0C15\u0C4D\u0C38\u0C3F\u0C2F\u0C3E \u0C38\u0C4D\u0C28\u0C47\u0C39\u0C3F\u0C24 \u0C05\u0C15\u0C4D\u0C37\u0C30\u0C38\u0C4D\u0C1F\u0C48\u0C32\u0C4D',
    underlineLinks: '\u0C32\u0C3F\u0C02\u0C15\u0C4D\u0C32\u0C15\u0C41 \u0C05\u0C02\u0C21\u0C30\u0C4D\u200C\u0C32\u0C48\u0C28\u0C4D',
    focusOutline: '\u0C2B\u0C4B\u0C15\u0C38\u0C4D \u0C05\u0C2C\u0C4D\u0C2C\u0C3E\u0C23\u0C02',
    hideImages: '\u0C1A\u0C3F\u0C24\u0C4D\u0C30\u0C3E\u0C32\u0C41 \u0C26\u0C3E\u0C1A\u0C41',
    textSpacing: '\u0C35\u0C3E\u0C15\u0C4D\u0C2F \u0C05\u0C02\u0C24\u0C30\u0C02',
    pauseAnimations: '\u0C05\u0C28\u0C3F\u0C2E\u0C47\u0C37\u0C28\u0C4D\u0C32\u0C41 \u0C28\u0C3F\u0C32\u0C41\u0C2A\u0C41',
    largeCursor: '\u0C2A\u0C46\u0C26\u0C4D\u0C26 \u0C15\u0C30\u0C4D\u0C38\u0C30\u0C4D',
    highlightHeadings: '\u0C36\u0C40\u0C30\u0C4D\u0C37\u0C3F\u0C15\u0C32\u0C41 \u0C39\u0C48\u0C32\u0C48\u0C1F\u0C4D \u0C1A\u0C47\u0C2F\u0C3F',
    invertColors: '\u0C30\u0C02\u0C17\u0C41\u0C32\u0C41 \u0C35\u0C3F\u0C2A\u0C30\u0C40\u0C24\u0C02 \u0C1A\u0C47\u0C2F\u0C3F',
    readingGuide: '\u0C1A\u0C26\u0C35\u0C21\u0C02 \u0C17\u0C48\u0C21\u0C4D',
    textToSpeech: '\u0C35\u0C3E\u0C15\u0C4D\u0C2F\u0C02 \u0C28\u0C41\u0C02\u0C21\u0C3F \u0C2E\u0C3E\u0C1F',
    focusMode: 'ఫోకస్ మోడ్',
    lineHeight: 'లైన్ హైట్',
    letterSpacing: 'అక్షర అంతరం',
    wordSpacing: 'పదం అంతరం',
    visual: 'దృశ్యమానం',
    content: 'విషయం',
    navigation: 'నావిగేషన్',
    deuteranopia: 'ఆకు పచ్చదనం',
    protanopia: 'ఎరుపు నేత్రాంధము',
    tritanopia: 'నీలం నేత్రాంధము',
    highlightHover: 'హోవర్‌లో హైలైట్',
    ttsPause: 'చదవడం ఆపు',
    ttsResume: 'చదవడం కొనసాగించు',
    ttsSpeed: 'చదవే వేగం',
    ttsSlower: 'నెమ్మది',
    ttsFaster: 'వేగంగా',
    quickStart: 'శీఘ్ర ప్రారంభం',
    presetLowVision: 'తక్కువ దృష్టి',
    presetDyslexia: 'డిస్లెక్సియా స్నేహపూర్వక',
    presetAdhd: 'ADHD / జ్ఞానాత్మక',
    presetMotor: 'మోటార్ / కీబోర్డ్',
    presetMigraine: 'మైగ్రేన్ సురక్షితం',
    settingsActive: 'సెట్టింగ్సు సక్రియంగా ఉన్నాయి',
    resetConfirmation: 'అన్ని ప్రాప్యత సెట్టింగ్సు రీసెట్ చేయబడ్డాయి',
    featureEnabled: 'సక్రియమైంది',
    featureDisabled: 'నిష్క్రియమైంది',
    tooltipMessage: 'మీ ప్రాప్యత సెట్టింగ్సును అనుకూలీకరించండి',
    increaseLineHeight: "లైన్ ఎత్తును పెంచండి",
    decreaseLineHeight: "లైన్ ఎత్తును తగ్గించండి",
    increaseLetterSpacing: "అక్షర అంతరాన్ని పెంచండి",
    decreaseLetterSpacing: "అక్షర అంతరాన్ని తగ్గించండి",
    increaseWordSpacing: "పద అంతరాన్ని పెంచండి",
    decreaseWordSpacing: "పద అంతరాన్ని తగ్గించండి",
    reducedTransparency: "పారదర్శకత తగ్గింపు",
    sensoryFriendly: "ఇంద్రియ స్నేహపూర్వక",
    readableFont: "చదవడానికి అనువైన ఫాంట్",
    saturation: "సంతృప్తత",
    increaseSaturation: "సంతృప్తత పెంచండి",
    decreaseSaturation: "సంతృప్తత తగ్గించండి",
    brightness: "ప్రకాశం",
    increaseBrightness: "ప్రకాశం పెంచండి",
    decreaseBrightness: "ప్రకాశం తగ్గించండి",
    suppressNotifications: "నోటిఫికేషన్‌లను అణచివేయండి",
    devAuditBadge: "డెవ్ ఆడిట్",
    devAuditNoIssues: "alt టెక్స్ట్ సమస్యలు కనుగొనబడలేదు",
    devAuditIssues: "alt టెక్స్ట్ సమస్యలు కనుగొనబడ్డాయి",
    zoomLockWarning: "⚠️ ఈ పేజీ పిన్చ్-టు-జూమ్‌ను నిలిపివేసింది. బ్రౌజర్ జూమ్‌పై ఆధారపడే వినియోగదారులకు కంటెంట్ యాక్సెస్ చేయడం కష్టంగా ఉండవచ్చు.",
    position: "విడ్జెట్ స్థానం",
    topLeft: "పై ఎడమ",
    topRight: "పై కుడి",
    bottomLeft: "కింది ఎడమ",
    bottomRight: "కింది కుడి",
    profiles: "ప్రొఫైల్‌లు",
    profileNamePlaceholder: "ప్రొఫైల్ పేరు…",
    saveProfile: "సేవ్ చేయండి",
    loadProfile: "లోడ్ చేయండి",
    deleteProfile: "తొలగించండి",
    noProfiles: "సేవ్ చేసిన ప్రొఫైల్‌లు లేవు",
    accessibilityStatementLink: "యాక్సెసిబిలిటీ ప్రకటన",
    resetAll: '\u0C05\u0C28\u0C4D\u0C28\u0C3F\u0C02\u0C1F\u0C3F\u0C28\u0C40 \u0C30\u0C40\u0C38\u0C46\u0C1F\u0C4D \u0C1A\u0C47\u0C2F\u0C3F',
    closeMenu: '\u0C2E\u0C46\u0C28\u0C42 \u0C2E\u0C42\u0C38\u0C3F\u0C35\u0C47\u0C2F\u0C3F',
    language: '\u0C2D\u0C3E\u0C37',
    disclaimer:
      '\u0C08 \u0C35\u0C3F\u0C1C\u0C46\u0C1F\u0C4D \u0C38\u0C02\u0C2A\u0C42\u0C30\u0C4D\u0C23 \u0C2A\u0C4D\u0C30\u0C35\u0C47\u0C36\u0C2F\u0C4B\u0C17\u0C4D\u0C2F\u0C24 \u0C05\u0C28\u0C41\u0C2A\u0C3E\u0C32\u0C28\u0C28\u0C41 \u0C39\u0C3E\u0C2E\u0C40 \u0C07\u0C35\u0C4D\u0C35\u0C26\u0C41.',
  },

  gu: {
    nativeName: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0',
    menuTitle: '\u0A8F\u0A95\u0ACD\u0AB8\u0AC7\u0AB8\u0ABF\u0AAC\u0ABF\u0AB2\u0ABF\u0A9F\u0AC0 \u0AAE\u0AC7\u0AA8\u0AC1',
    highContrast: '\u0A89\u0A9A\u0ACD\u0A9A \u0A95\u0ACB\u0AA8\u0ACD\u0A9F\u0ACD\u0AB0\u0ABE\u0AB8\u0ACD\u0A9F',
    darkMode: '\u0AA1\u0ABE\u0AB0\u0ACD\u0A95 \u0AAE\u0ACB\u0AA1',
    fontSize: '\u0AAB\u0ACB\u0AA8\u0ACD\u0A9F \u0A95\u0ACD\u0AB0\u0AAE',
    increaseFontSize: '\u0AAB\u0ACB\u0AA8\u0ACD\u0A9F \u0AB5\u0AA7\u0ABE\u0AB0\u0ACB',
    decreaseFontSize: '\u0AAB\u0ACB\u0AA8\u0ACD\u0A9F \u0A98\u0A9F\u0ABE\u0AA1\u0ACB',
    resetFontSize: '\u0AAB\u0ACB\u0AA8\u0ACD\u0A9F \u0AB0\u0AC0\u0AB8\u0AC7\u0A9F \u0A95\u0AB0\u0ACB',
    dyslexiaFont: '\u0AA1\u0ABF\u0AB8\u0AB2\u0AC7\u0A95\u0ACD\u0AB8\u0ABF\u0AAF\u0ABE \u0AAB\u0ACD\u0AB0\u0AC7\u0AA8\u0ACD\u0AA1\u0AB2\u0AC0 \u0AAB\u0ACB\u0AA8\u0ACD\u0A9F',
    underlineLinks: '\u0AB2\u0ABF\u0A82\u0A95\u0AA8\u0AC7 \u0A85\u0AA8\u0ACD\u0AA1\u0AB0\u0AB2\u0ABE\u0A87\u0AA8 \u0A95\u0AB0\u0ACB',
    focusOutline: '\u0AAB\u0ACB\u0A95\u0AB8 \u0AB0\u0AC7\u0A96\u0ABE',
    hideImages: '\u0A9B\u0AAC\u0ABF\u0A93 \u0A9B\u0AC1\u0AAA\u0ABE\u0AB5\u0ACB',
    textSpacing: '\u0AA4\u0AC7\u0A95\u0ACD\u0AB8\u0ACD\u0A9F \u0A85\u0A82\u0AA4\u0AB0',
    pauseAnimations: '\u0A8F\u0AA8\u0ABF\u0AAE\u0AC7\u0AB6\u0AA8 \u0AB0\u0ACB\u0A95\u0ACB',
    largeCursor: '\u0AAE\u0ACB\u0A9F\u0ABE\u0AB0 \u0A95\u0AB0\u0ACD\u0AB8\u0AB0',
    highlightHeadings: '\u0AB6\u0AC0\u0AB0\u0ACD\u0AB7\u0A95\u0ACB\u0AA8\u0AC7 \u0AB9\u0ABE\u0A87\u0AB2\u0ABE\u0A87\u0A9F \u0A95\u0AB0\u0ACB',
    invertColors: '\u0AB0\u0A82\u0A97\u0ACB \u0A89\u0AB2\u0A9F\u0ABE\u0AB5\u0ACB',
    readingGuide: '\u0AB5\u0ABE\u0A82\u0A9A\u0AA8 \u0AAE\u0ABE\u0AB0\u0ACD\u0A97\u0AA6\u0AB0\u0ACD\u0AB6\u0ABF\u0A95\u0ABE',
    textToSpeech: '\u0AA4\u0AC7\u0A95\u0ACD\u0AB8\u0ACD\u0A9F\u0AAE\u0ABE\u0A82\u0AA5\u0AC0 \u0AA7\u0ACD\u0AB5\u0AA8\u0ABF',
    focusMode: 'ફોકસ મોਡ',
    lineHeight: 'લાઇન ઊંચાઈ',
    letterSpacing: 'અક્ષર અંતર',
    wordSpacing: 'શબ્દ અંતર',
    visual: 'દૃશ્ય',
    content: 'સામગ્રી',
    navigation: 'નેવિગેશન',
    deuteranopia: 'લીલો રંગઅંધતા',
    protanopia: 'લાલ રંગઅંધતા',
    tritanopia: 'નીલો રંગઅંધતા',
    highlightHover: 'હોવર પર હાઇલાઇટ',
    ttsPause: 'વાંચવું અટકાવો',
    ttsResume: 'વાંચવાનું ફરી શરૂ કરો',
    ttsSpeed: 'વાંચવાની ઝડપ',
    ttsSlower: 'ધીમા',
    ttsFaster: 'ઝડપી',
    quickStart: 'ઝડપી શરૂઆત',
    presetLowVision: 'ઓછી દ્રષ્ટિ',
    presetDyslexia: 'ડિસ્લેક્સિયા મૈત્રીપૂર્ણ',
    presetAdhd: 'ADHD / જ્ઞાનાત્મક',
    presetMotor: 'મોટર / કીબોર્ડ',
    presetMigraine: 'માઇગ્રેન સલામત',
    settingsActive: 'સેટિંગ્સ સક્રિય',
    resetConfirmation: 'એલા એક્સેસિબિલિટી સેટિંગ્સ રીસેટ થયાં',
    featureEnabled: 'સક્ષમ',
    featureDisabled: 'અક્ષમ',
    tooltipMessage: 'તમારા એક્સેસિબિલિટી સેટિંગ્સ કસ્ટમાયવ કરો',
    increaseLineHeight: "લઇન ઊચઈ વધર",
    decreaseLineHeight: "લઇન ઊચઈ ઘટડ",
    increaseLetterSpacing: "અકષર અતર વધર",
    decreaseLetterSpacing: "અકષર અતર ઘટડ",
    increaseWordSpacing: "શબદ અતર વધર",
    decreaseWordSpacing: "શબદ અતર ઘટડ",
    reducedTransparency: "ઘટડલ પરદરશત",
    sensoryFriendly: "ઇનદરય-અનકળ",
    readableFont: "વચ શકય તવ ફનટ",
    saturation: "સતપત",
    increaseSaturation: "સતપત વધર",
    decreaseSaturation: "સતપત ઘટડ",
    brightness: "તજ",
    increaseBrightness: "તજ વધર",
    decreaseBrightness: "તજ ઘટડ",
    suppressNotifications: "સચનઓ દબવ",
    devAuditBadge: "ડવ ઑડટ",
    devAuditNoIssues: "alt ટકસટ સમસયઓ મળ નથ",
    devAuditIssues: "alt ટકસટ સમસયઓ મળ",
    zoomLockWarning: "⚠️ આ પજ પનચ-ટ-ઝમ અકષમ કરય છ. બરઉઝર ઝમ પર નરભર વપરશકરતઓ સમગર ઍકસસ કરવમ મશકલ અનભવ શક છ.",
    position: "વજટ સથત",
    topLeft: "ઉપર ડબ",
    topRight: "ઉપર જમણ",
    bottomLeft: "નચ ડબ",
    bottomRight: "નચ જમણ",
    profiles: "પરફઇલ",
    profileNamePlaceholder: "પરફઇલ નમ…",
    saveProfile: "સચવ",
    loadProfile: "લડ કર",
    deleteProfile: "ભસ",
    noProfiles: "કઈ સચવલ પરફઇલ નથ",
    accessibilityStatementLink: "ઍક્સેસિબિલિટી સ્ટેટમેન્ટ",
    resetAll: '\u0A85\u0AB2\u0AA8\u0AC7 \u0AB0\u0AC0\u0AB8\u0AC7\u0A9F \u0A95\u0AB0\u0ACB',
    closeMenu: '\u0AAE\u0AC7\u0AA8\u0AC1 \u0AAC\u0A82\u0AA7 \u0A95\u0AB0\u0ACB',
    language: '\u0AAD\u0ABE\u0AB7\u0ABE',
    disclaimer:
      '\u0A86 \u0AB5\u0ABF\u0A9C\u0AC7\u0A9F \u0AB8\u0A82\u0AAA\u0AC2\u0AB0\u0ACD\u0AA3 \u0A8F\u0A95\u0ACD\u0AB8\u0AC7\u0AB8\u0ABF\u0AAC\u0ABF\u0AB2\u0ABF\u0A9F\u0AC0 \u0A85\u0AA8\u0AC1\u0AAA\u0ABE\u0AB2\u0AA8\u0AA8\u0AC0 \u0A96\u0ABE\u0AA4\u0AB0\u0AC0 \u0A86\u0AAA\u0AA4\u0AC1\u0AA8\u0AA5\u0AC0.',
  },

  pl: {
    nativeName: 'Polski',
    menuTitle: 'Menu dost\u0119pno\u015Bci',
    highContrast: 'Wysoki kontrast',
    darkMode: 'Tryb ciemny',
    fontSize: 'Rozmiar czcionki',
    increaseFontSize: 'Zwi\u0119ksz czcionk\u0119',
    decreaseFontSize: 'Zmniejsz czcionk\u0119',
    resetFontSize: 'Resetuj rozmiar czcionki',
    dyslexiaFont: 'Czcionka dla dyslektyków',
    underlineLinks: 'Podkre\u015Bl linki',
    focusOutline: 'Kontur fokusu',
    hideImages: 'Ukryj obrazy',
    textSpacing: 'Odst\u0119py tekstu',
    pauseAnimations: 'Wstrzymaj animacje',
    largeCursor: 'Du\u017Cy kursor',
    highlightHeadings: 'Wyró\u017Cnij nag\u0142\u00F3wki',
    invertColors: 'Odwró\u0107 kolory',
    readingGuide: 'Przewodnik czytania',
    textToSpeech: 'Tekst na mow\u0119',
    focusMode: 'Tryb skupienia',
    lineHeight: 'Wysokość linii',
    letterSpacing: 'Odstęp liter',
    wordSpacing: 'Odstęp słów',
    visual: 'Wizualny',
    content: 'Treść',
    navigation: 'Nawigacja',
    deuteranopia: 'Deuteranopia (niewidomość zielona)',
    protanopia: 'Protanopia (niewidomość czerwona)',
    tritanopia: 'Tritanopia (niewidomość niebieska)',
    highlightHover: 'Podświetl po najechaniu',
    ttsPause: 'Wstrzymaj czytanie',
    ttsResume: 'Wznów czytanie',
    ttsSpeed: 'Szybkość czytania',
    ttsSlower: 'Wolniej',
    ttsFaster: 'Szybciej',
    quickStart: 'Szybki start',
    presetLowVision: 'Słaby wzrok',
    presetDyslexia: 'Przyjazny dla dysleksji',
    presetAdhd: 'ADHD / Poznawczy',
    presetMotor: 'Motoryczny / Klawiatura',
    presetMigraine: 'Bezpieczny przy migrenie',
    settingsActive: 'ustawień aktywnych',
    resetConfirmation: 'Wszystkie ustawienia dostępności zostały zresetowane',
    featureEnabled: 'włączono',
    featureDisabled: 'wyłączono',
    tooltipMessage: 'Dostosuj ustawienia dostępności',
    increaseLineHeight: "Zwiększ wysokość linii",
    decreaseLineHeight: "Zmniejsz wysokość linii",
    increaseLetterSpacing: "Zwiększ odstępy między literami",
    decreaseLetterSpacing: "Zmniejsz odstępy między literami",
    increaseWordSpacing: "Zwiększ odstępy między słowami",
    decreaseWordSpacing: "Zmniejsz odstępy między słowami",
    reducedTransparency: "Zmniejszona przezroczystość",
    sensoryFriendly: "Przyjazny dla zmysłów",
    readableFont: "Czytelna czcionka",
    saturation: "Nasycenie",
    increaseSaturation: "Zwiększ nasycenie",
    decreaseSaturation: "Zmniejsz nasycenie",
    brightness: "Jasność",
    increaseBrightness: "Zwiększ jasność",
    decreaseBrightness: "Zmniejsz jasność",
    suppressNotifications: "Wycisz powiadomienia",
    devAuditBadge: "Audyt deweloperski",
    devAuditNoIssues: "Nie znaleziono problemów z tekstem alternatywnym",
    devAuditIssues: "znalezione problemy z tekstem alternatywnym",
    zoomLockWarning: "⚠️ Ta strona wyłączyła powiększanie gestem. Użytkownicy korzystający ze powiększenia przeglądarki mogą mieć trudności z dostępem do treści.",
    position: "Pozycja widżetu",
    topLeft: "Górny lewy",
    topRight: "Górny prawy",
    bottomLeft: "Dolny lewy",
    bottomRight: "Dolny prawy",
    profiles: "Profile",
    profileNamePlaceholder: "Nazwa profilu…",
    saveProfile: "Zapisz",
    loadProfile: "Wczytaj",
    deleteProfile: "Usuń",
    noProfiles: "Brak zapisanych profili",
    accessibilityStatementLink: "Oświadczenie o dostępności",
    resetAll: 'Resetuj wszystko',
    closeMenu: 'Zamknij menu',
    language: 'J\u0119zyk',
    disclaimer:
      'Ten widget nie gwarantuje pe\u0142nej zgodno\u015Bci z wymaganiami dost\u0119pno\u015Bci.',
  },

  ms: {
    nativeName: 'Bahasa Melayu',
    menuTitle: 'Menu Kebolehcapaian',
    highContrast: 'Kontras Tinggi',
    darkMode: 'Mod Gelap',
    fontSize: 'Saiz Fon',
    increaseFontSize: 'Besarkan Fon',
    decreaseFontSize: 'Kecilkan Fon',
    resetFontSize: 'Tetap Semula Saiz Fon',
    dyslexiaFont: 'Fon Mesra Disleksia',
    underlineLinks: 'Gariskan Bawah Pautan',
    focusOutline: 'Garis Fokus',
    hideImages: 'Sembunyikan Imej',
    textSpacing: 'Jarak Teks',
    pauseAnimations: 'Jeda Animasi',
    largeCursor: 'Kursor Besar',
    highlightHeadings: 'Serlahkan Tajuk',
    invertColors: 'Songsangkan Warna',
    readingGuide: 'Panduan Membaca',
    textToSpeech: 'Teks kepada Pertuturan',
    focusMode: 'Mod Fokus',
    lineHeight: 'Tinggi Baris',
    letterSpacing: 'Jarak Huruf',
    wordSpacing: 'Jarak Perkataan',
    visual: 'Visual',
    content: 'Kandungan',
    navigation: 'Navigasi',
    deuteranopia: 'Deuteranopia (buta hijau)',
    protanopia: 'Protanopia (buta merah)',
    tritanopia: 'Tritanopia (buta biru)',
    highlightHover: 'Sorot semasa hover',
    ttsPause: 'Jeda membaca',
    ttsResume: 'Sambung membaca',
    ttsSpeed: 'Kelajuan membaca',
    ttsSlower: 'Lebih perlahan',
    ttsFaster: 'Lebih cepat',
    quickStart: 'Mula Cepat',
    presetLowVision: 'Penglihatan Rendah',
    presetDyslexia: 'Mesra Disleksia',
    presetAdhd: 'ADHD / Kognitif',
    presetMotor: 'Motor / Papan Kekunci',
    presetMigraine: 'Selamat untuk Migrain',
    settingsActive: 'tetapan aktif',
    resetConfirmation: 'Semua tetapan kebolehcapaian telah ditetapkan semula',
    featureEnabled: 'diaktifkan',
    featureDisabled: 'dinyahaktifkan',
    tooltipMessage: 'Sesuaikan tetapan kebolehcapaian anda',
    increaseLineHeight: "Tingkatkan ketinggian baris",
    decreaseLineHeight: "Kurangkan ketinggian baris",
    increaseLetterSpacing: "Tingkatkan jarak huruf",
    decreaseLetterSpacing: "Kurangkan jarak huruf",
    increaseWordSpacing: "Tingkatkan jarak perkataan",
    decreaseWordSpacing: "Kurangkan jarak perkataan",
    reducedTransparency: "Kelegapan dikurangkan",
    sensoryFriendly: "Mesra deria",
    readableFont: "Fon yang mudah dibaca",
    saturation: "Ketepuan",
    increaseSaturation: "Tingkatkan ketepuan",
    decreaseSaturation: "Kurangkan ketepuan",
    brightness: "Kecerahan",
    increaseBrightness: "Tingkatkan kecerahan",
    decreaseBrightness: "Kurangkan kecerahan",
    suppressNotifications: "Sembunyikan pemberitahuan",
    devAuditBadge: "Audit pembangun",
    devAuditNoIssues: "Tiada isu teks alt ditemui",
    devAuditIssues: "isu teks alt ditemui",
    zoomLockWarning: "⚠️ Halaman ini telah mematikan zum cubit. Pengguna yang bergantung pada zum pelayar mungkin menghadapi kesukaran untuk mengakses kandungan.",
    position: "Kedudukan widget",
    topLeft: "Kiri atas",
    topRight: "Kanan atas",
    bottomLeft: "Kiri bawah",
    bottomRight: "Kanan bawah",
    profiles: "Profil",
    profileNamePlaceholder: "Nama profil…",
    saveProfile: "Simpan",
    loadProfile: "Muat",
    deleteProfile: "Padam",
    noProfiles: "Tiada profil tersimpan",
    accessibilityStatementLink: "Penyataan Kebolehcapaian",
    resetAll: 'Tetap Semula Semua',
    closeMenu: 'Tutup Menu',
    language: 'Bahasa',
    disclaimer:
      'Widget ini tidak menjamin pematuhan penuh kebolehcapaian.',
  },

  nl: {
    nativeName: 'Nederlands',
    menuTitle: 'Toegankelijkheidsmenu',
    highContrast: 'Hoog contrast',
    darkMode: 'Donkere modus',
    fontSize: 'Lettergrootte',
    increaseFontSize: 'Lettergrootte vergroten',
    decreaseFontSize: 'Lettergrootte verkleinen',
    resetFontSize: 'Lettergrootte resetten',
    dyslexiaFont: 'Dyslexievriendelijk lettertype',
    underlineLinks: 'Links onderstrepen',
    focusOutline: 'Focusomlijning',
    hideImages: 'Afbeeldingen verbergen',
    textSpacing: 'Tekstafstand',
    pauseAnimations: 'Animaties pauzeren',
    largeCursor: 'Grote cursor',
    highlightHeadings: 'Koppen markeren',
    invertColors: 'Kleuren omkeren',
    readingGuide: 'Leesgids',
    textToSpeech: 'Tekst naar spraak',
    focusMode: 'Focusmodus',
    lineHeight: 'Regelhoogte',
    letterSpacing: 'Letterafstand',
    wordSpacing: 'Woordafstand',
    visual: 'Visueel',
    content: 'Inhoud',
    navigation: 'Navigatie',
    deuteranopia: 'Deuteranopie (groen-blind)',
    protanopia: 'Protanopie (rood-blind)',
    tritanopia: 'Tritanopie (blauw-blind)',
    highlightHover: 'Markeer bij hover',
    ttsPause: 'Lezen pauzeren',
    ttsResume: 'Lezen hervatten',
    ttsSpeed: 'Leessnelheid',
    ttsSlower: 'Langzamer',
    ttsFaster: 'Sneller',
    quickStart: 'Snel starten',
    presetLowVision: 'Slechtziend',
    presetDyslexia: 'Dyslexie-vriendelijk',
    presetAdhd: 'ADHD / Cognitief',
    presetMotor: 'Motorisch / Toetsenbord',
    presetMigraine: 'Veilig bij migraine',
    settingsActive: 'instellingen actief',
    resetConfirmation: 'Alle toegankelijkheidsinstellingen zijn opnieuw ingesteld',
    featureEnabled: 'ingeschakeld',
    featureDisabled: 'uitgeschakeld',
    tooltipMessage: 'Pas uw toegankelijkheidsinstellingen aan',
    increaseLineHeight: "Regelhoogte vergroten",
    decreaseLineHeight: "Regelhoogte verkleinen",
    increaseLetterSpacing: "Letterafstand vergroten",
    decreaseLetterSpacing: "Letterafstand verkleinen",
    increaseWordSpacing: "Woordafstand vergroten",
    decreaseWordSpacing: "Woordafstand verkleinen",
    reducedTransparency: "Verminderde transparantie",
    sensoryFriendly: "Zintuigvriendelijk",
    readableFont: "Leesbaar lettertype",
    saturation: "Verzadiging",
    increaseSaturation: "Verzadiging vergroten",
    decreaseSaturation: "Verzadiging verkleinen",
    brightness: "Helderheid",
    increaseBrightness: "Helderheid vergroten",
    decreaseBrightness: "Helderheid verkleinen",
    suppressNotifications: "Meldingen onderdrukken",
    devAuditBadge: "Ontwikkelaarsaudit",
    devAuditNoIssues: "Geen alt-tekstproblemen gevonden",
    devAuditIssues: "alt-tekstproblemen gevonden",
    zoomLockWarning: "⚠️ Deze pagina heeft inzoomen met knijpgebaar uitgeschakeld. Gebruikers die afhankelijk zijn van browserzoom kunnen moeite hebben met toegang tot inhoud.",
    position: "Widgetpositie",
    topLeft: "Linksboven",
    topRight: "Rechtsboven",
    bottomLeft: "Linksonder",
    bottomRight: "Rechtsonder",
    profiles: "Profielen",
    profileNamePlaceholder: "Profielnaam…",
    saveProfile: "Opslaan",
    loadProfile: "Laden",
    deleteProfile: "Verwijderen",
    noProfiles: "Geen opgeslagen profielen",
    accessibilityStatementLink: "Toegankelijkheidsverklaring",
    resetAll: 'Alles resetten',
    closeMenu: 'Menu sluiten',
    language: 'Taal',
    disclaimer:
      'Dit widget garandeert geen volledige naleving van toegankelijkheidsvereisten.',
  },

  tl: {
    nativeName: 'Filipino',
    menuTitle: 'Menu ng Accessibility',
    highContrast: 'Mataas na Contrast',
    darkMode: 'Dark Mode',
    fontSize: 'Laki ng Font',
    increaseFontSize: 'Palakihin ang Font',
    decreaseFontSize: 'Paliitin ang Font',
    resetFontSize: 'I-reset ang Laki ng Font',
    dyslexiaFont: 'Font para sa Dyslexia',
    underlineLinks: 'Salungguhitan ang mga Link',
    focusOutline: 'Hangganan ng Focus',
    hideImages: 'Itago ang mga Larawan',
    textSpacing: 'Espasyo ng Teksto',
    pauseAnimations: 'I-pause ang mga Animasyon',
    largeCursor: 'Malaking Cursor',
    highlightHeadings: 'I-highlight ang mga Pamagat',
    invertColors: 'Baligtarin ang mga Kulay',
    readingGuide: 'Gabay sa Pagbabasa',
    textToSpeech: 'Teksto sa Pagsasalita',
    focusMode: 'Focus Mode',
    lineHeight: 'Taas ng Linya',
    letterSpacing: 'Espasyo ng Titik',
    wordSpacing: 'Espasyo ng Salita',
    visual: 'Visual',
    content: 'Nilalaman',
    navigation: 'Nabigasyon',
    deuteranopia: 'Deuteranopia (bulag sa berde)',
    protanopia: 'Protanopia (bulag sa pula)',
    tritanopia: 'Tritanopia (bulag sa asul)',
    highlightHover: 'I-highlight sa hover',
    ttsPause: 'I-pause ang pagbabasa',
    ttsResume: 'Ipagpatuloy ang pagbabasa',
    ttsSpeed: 'Bilis ng pagbabasa',
    ttsSlower: 'Mas mabagal',
    ttsFaster: 'Mas mabilis',
    quickStart: 'Mabilis na Simula',
    presetLowVision: 'Mahina ang Paningin',
    presetDyslexia: 'Palakaibigan ng Dyslexia',
    presetAdhd: 'ADHD / Kognitibo',
    presetMotor: 'Motor / Keyboard',
    presetMigraine: 'Ligtas sa Migraine',
    settingsActive: 'aktibong mga setting',
    resetConfirmation: 'Nai-reset na ang lahat ng accessibility settings',
    featureEnabled: 'pinagana',
    featureDisabled: 'hindi pinagana',
    tooltipMessage: 'I-customize ang iyong mga accessibility settings',
    increaseLineHeight: "Palakihin ang taas ng linya",
    decreaseLineHeight: "Bawasan ang taas ng linya",
    increaseLetterSpacing: "Palakihin ang agwat ng letra",
    decreaseLetterSpacing: "Bawasan ang agwat ng letra",
    increaseWordSpacing: "Palakihin ang agwat ng salita",
    decreaseWordSpacing: "Bawasan ang agwat ng salita",
    reducedTransparency: "Pinababang transparency",
    sensoryFriendly: "Maayos sa pandama",
    readableFont: "Madaling basahing font",
    saturation: "Saturation",
    increaseSaturation: "Palakihin ang saturation",
    decreaseSaturation: "Bawasan ang saturation",
    brightness: "Liwanag",
    increaseBrightness: "Palakihin ang liwanag",
    decreaseBrightness: "Bawasan ang liwanag",
    suppressNotifications: "Pigilan ang mga abiso",
    devAuditBadge: "Dev Audit",
    devAuditNoIssues: "Walang nahanap na isyu sa alt text",
    devAuditIssues: "mga isyu sa alt text ang nahanap",
    zoomLockWarning: "⚠️ Ang pahinang ito ay may na-disable na pinch-to-zoom. Ang mga gumagamit na umaasa sa browser zoom ay maaaring mahirapan sa pag-access ng nilalaman.",
    position: "Posisyon ng widget",
    topLeft: "Itaas kaliwa",
    topRight: "Itaas kanan",
    bottomLeft: "Ibaba kaliwa",
    bottomRight: "Ibaba kanan",
    profiles: "Mga profile",
    profileNamePlaceholder: "Pangalan ng profile…",
    saveProfile: "I-save",
    loadProfile: "I-load",
    deleteProfile: "Tanggalin",
    noProfiles: "Walang mga naka-save na profile",
    accessibilityStatementLink: "Pahayag ng Accessibility",
    resetAll: 'I-reset ang Lahat',
    closeMenu: 'Isara ang Menu',
    language: 'Wika',
    disclaimer:
      'Ang widget na ito ay hindi ginagarantiyahan ang ganap na pagsunod sa accessibility.',
  },

  uk: {
    nativeName: '\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430',
    menuTitle: '\u041C\u0435\u043D\u044E \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u0456',
    highContrast: '\u0412\u0438\u0441\u043E\u043A\u0438\u0439 \u043A\u043E\u043D\u0442\u0440\u0430\u0441\u0442',
    darkMode: '\u0422\u0435\u043C\u043D\u0438\u0439 \u0440\u0435\u0436\u0438\u043C',
    fontSize: '\u0420\u043E\u0437\u043C\u0456\u0440 \u0448\u0440\u0438\u0444\u0442\u0443',
    increaseFontSize: '\u0417\u0431\u0456\u043B\u044C\u0448\u0438\u0442\u0438 \u0448\u0440\u0438\u0444\u0442',
    decreaseFontSize: '\u0417\u043C\u0435\u043D\u0448\u0438\u0442\u0438 \u0448\u0440\u0438\u0444\u0442',
    resetFontSize: '\u0421\u043A\u0438\u043D\u0443\u0442\u0438 \u0440\u043E\u0437\u043C\u0456\u0440 \u0448\u0440\u0438\u0444\u0442\u0443',
    dyslexiaFont: '\u0428\u0440\u0438\u0444\u0442 \u0434\u043B\u044F \u0434\u0438\u0441\u043B\u0435\u043A\u0441\u0456\u043A\u0456\u0432',
    underlineLinks: '\u041F\u0456\u0434\u043A\u0440\u0435\u0441\u043B\u0438\u0442\u0438 \u043F\u043E\u0441\u0438\u043B\u0430\u043D\u043D\u044F',
    focusOutline: '\u041A\u043E\u043D\u0442\u0443\u0440 \u0444\u043E\u043A\u0443\u0441\u0443',
    hideImages: '\u041F\u0440\u0438\u0445\u043E\u0432\u0430\u0442\u0438 \u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u043D\u044F',
    textSpacing: '\u0406\u043D\u0442\u0435\u0440\u0432\u0430\u043B \u0442\u0435\u043A\u0441\u0442\u0443',
    pauseAnimations: '\u0417\u0443\u043F\u0438\u043D\u0438\u0442\u0438 \u0430\u043D\u0456\u043C\u0430\u0446\u0456\u0457',
    largeCursor: '\u0412\u0435\u043B\u0438\u043A\u0438\u0439 \u043A\u0443\u0440\u0441\u043E\u0440',
    highlightHeadings: '\u0412\u0438\u0434\u0456\u043B\u0438\u0442\u0438 \u0437\u0430\u0433\u043E\u043B\u043E\u0432\u043A\u0438',
    invertColors: '\u0406\u043D\u0432\u0435\u0440\u0442\u0443\u0432\u0430\u0442\u0438 \u043A\u043E\u043B\u044C\u043E\u0440\u0438',
    readingGuide: '\u041F\u043E\u0441\u0456\u0431\u043D\u0438\u043A \u0447\u0438\u0442\u0430\u043D\u043D\u044F',
    textToSpeech: '\u0422\u0435\u043A\u0441\u0442 \u0443 \u043C\u043E\u0432\u043B\u0435\u043D\u043D\u044F',
    focusMode: 'Режим фокусу',
    lineHeight: 'Висота рядка',
    letterSpacing: 'Відстань між літерами',
    wordSpacing: 'Відстань між словами',
    visual: 'Візуальний',
    content: 'Вміст',
    navigation: 'Навігація',
    deuteranopia: 'Дейтеранопія',
    protanopia: 'Протанопія',
    tritanopia: 'Тританопія',
    highlightHover: 'Підсвітка при наведенні',
    ttsPause: 'Призупинити читання',
    ttsResume: 'Відновити читання',
    ttsSpeed: 'Швидкість читання',
    ttsSlower: 'Повільніше',
    ttsFaster: 'Швидше',
    quickStart: 'Швидкий початок',
    presetLowVision: 'Слабкий зір',
    presetDyslexia: 'Дружній до дислексії',
    presetAdhd: 'СДУГ / Когнітивний',
    presetMotor: 'Моторний / Клавіатура',
    presetMigraine: 'Безпечно при мігрені',
    settingsActive: 'налаштувань активно',
    resetConfirmation: 'Усі налаштування доступності скинуто',
    featureEnabled: 'увімкнено',
    featureDisabled: 'вимкнено',
    tooltipMessage: 'Налаштуйте параметри доступності',
    increaseLineHeight: "Збільшити висоту рядка",
    decreaseLineHeight: "Зменшити висоту рядка",
    increaseLetterSpacing: "Збільшити міжлітерний інтервал",
    decreaseLetterSpacing: "Зменшити міжлітерний інтервал",
    increaseWordSpacing: "Збільшити міжслівний інтервал",
    decreaseWordSpacing: "Зменшити міжслівний інтервал",
    reducedTransparency: "Знижена прозорість",
    sensoryFriendly: "Зручний для сприйняття",
    readableFont: "Читабельний шрифт",
    saturation: "Насиченість",
    increaseSaturation: "Збільшити насиченість",
    decreaseSaturation: "Зменшити насиченість",
    brightness: "Яскравість",
    increaseBrightness: "Збільшити яскравість",
    decreaseBrightness: "Зменшити яскравість",
    suppressNotifications: "Приховати сповіщення",
    devAuditBadge: "Аудит розробника",
    devAuditNoIssues: "Проблем із альтернативним текстом не знайдено",
    devAuditIssues: "проблем із альтернативним текстом",
    zoomLockWarning: "⚠️ Ця сторінка вимкнула масштабування жестами. Користувачі, що покладаються на масштабування браузера, можуть мати труднощі з доступом до вмісту.",
    position: "Положення віджету",
    topLeft: "Вгорі ліворуч",
    topRight: "Вгорі праворуч",
    bottomLeft: "Внизу ліворуч",
    bottomRight: "Внизу праворуч",
    profiles: "Профілі",
    profileNamePlaceholder: "Назва профілю…",
    saveProfile: "Зберегти",
    loadProfile: "Завантажити",
    deleteProfile: "Видалити",
    noProfiles: "Немає збережених профілів",
    accessibilityStatementLink: "Заява про доступність",
    resetAll: '\u0421\u043A\u0438\u043D\u0443\u0442\u0438 \u0432\u0441\u0435',
    closeMenu: '\u0417\u0430\u043A\u0440\u0438\u0442\u0438 \u043C\u0435\u043D\u044E',
    language: '\u041C\u043E\u0432\u0430',
    disclaimer:
      '\u0426\u0435\u0439 \u0432\u0456\u0434\u0436\u0435\u0442 \u043D\u0435 \u0433\u0430\u0440\u0430\u043D\u0442\u0443\u0454 \u043F\u043E\u0432\u043D\u043E\u0457 \u0432\u0456\u0434\u043F\u043E\u0432\u0456\u0434\u043D\u043E\u0441\u0442\u0456 \u0441\u0442\u0430\u043D\u0434\u0430\u0440\u0442\u0430\u043C \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E\u0441\u0442\u0456.',
  },

  sw: {
    nativeName: 'Kiswahili',
    menuTitle: 'Menyu ya Upatikanaji',
    highContrast: 'Tofauti Kubwa',
    darkMode: 'Hali ya Giza',
    fontSize: 'Ukubwa wa Fonti',
    increaseFontSize: 'Ongeza Ukubwa wa Fonti',
    decreaseFontSize: 'Punguza Ukubwa wa Fonti',
    resetFontSize: 'Weka Upya Ukubwa wa Fonti',
    dyslexiaFont: 'Fonti Rafiki kwa Dyslexia',
    underlineLinks: 'Pigia Mstari Viungo',
    focusOutline: 'Mpaka wa Makini',
    hideImages: 'Ficha Picha',
    textSpacing: 'Nafasi ya Maandishi',
    pauseAnimations: 'Simamisha Michoro',
    largeCursor: 'Kishale Kikubwa',
    highlightHeadings: 'Angazia Vichwa',
    invertColors: 'Geuza Rangi',
    readingGuide: 'Mwongozo wa Kusoma',
    textToSpeech: 'Maandishi hadi Sauti',
    focusMode: 'Hali ya Kuzingatia',
    lineHeight: 'Urefu wa Mstari',
    letterSpacing: 'Nafasi ya Herufi',
    wordSpacing: 'Nafasi ya Maneno',
    visual: 'Muonekano',
    content: 'Maudhui',
    navigation: 'Urambazaji',
    deuteranopia: 'Upofu wa kijani',
    protanopia: 'Upofu wa nyekundu',
    tritanopia: 'Upofu wa bluu',
    highlightHover: 'Angazia wakati wa kupita',
    ttsPause: 'Simamisha kusoma',
    ttsResume: 'Endelea kusoma',
    ttsSpeed: 'Kasi ya kusoma',
    ttsSlower: 'Polepole zaidi',
    ttsFaster: 'Haraka zaidi',
    quickStart: 'Anza Haraka',
    presetLowVision: 'Maono Kidogo',
    presetDyslexia: 'Rafiki wa Dyslexia',
    presetAdhd: 'ADHD / Utambuzi',
    presetMotor: 'Mwendo / Kibodi',
    presetMigraine: 'Salama kwa Maumivu ya Kichwa',
    settingsActive: 'mipangilio inayotumika',
    resetConfirmation: 'Mipangilio yote ya ufikiaji imewekwa upya',
    featureEnabled: 'imewezeshwa',
    featureDisabled: 'imezimwa',
    tooltipMessage: 'Binafsisha mipangilio yako ya ufikiaji',
    increaseLineHeight: "Ongeza urefu wa mstari",
    decreaseLineHeight: "Punguza urefu wa mstari",
    increaseLetterSpacing: "Ongeza nafasi ya herufi",
    decreaseLetterSpacing: "Punguza nafasi ya herufi",
    increaseWordSpacing: "Ongeza nafasi ya maneno",
    decreaseWordSpacing: "Punguza nafasi ya maneno",
    reducedTransparency: "Uwazi uliopunguzwa",
    sensoryFriendly: "Rafiki kwa hisia",
    readableFont: "Fonti inayosomeka",
    saturation: "Msindikizo wa rangi",
    increaseSaturation: "Ongeza msindikizo",
    decreaseSaturation: "Punguza msindikizo",
    brightness: "Mwangaza",
    increaseBrightness: "Ongeza mwangaza",
    decreaseBrightness: "Punguza mwangaza",
    suppressNotifications: "Kuzuia arifa",
    devAuditBadge: "Ukaguzi wa Msanidi",
    devAuditNoIssues: "Hakuna matatizo ya maandishi mbadala",
    devAuditIssues: "matatizo ya maandishi mbadala yamepatikana",
    zoomLockWarning: "⚠️ Ukurasa huu umezima kukuza kwa vidole. Watumiaji wanaotegemea kukuza kwa kivinjari wanaweza kuwa na ugumu wa kufikia maudhui.",
    position: "Nafasi ya wijeti",
    topLeft: "Juu kushoto",
    topRight: "Juu kulia",
    bottomLeft: "Chini kushoto",
    bottomRight: "Chini kulia",
    profiles: "Maelezo",
    profileNamePlaceholder: "Jina la wasifu…",
    saveProfile: "Hifadhi",
    loadProfile: "Pakia",
    deleteProfile: "Futa",
    noProfiles: "Hakuna maelezo yaliyohifadhiwa",
    accessibilityStatementLink: "Taarifa ya Ufikiaji",
    resetAll: 'Weka Upya Yote',
    closeMenu: 'Funga Menyu',
    language: 'Lugha',
    disclaimer:
      'Wijiti hii haihakikishi uzingatiaji kamili wa upatikanaji.',
  },

  sv: {
    nativeName: 'Svenska',
    menuTitle: 'Tillg\u00E4nglighetsmenyn',
    highContrast: 'H\u00F6g kontrast',
    darkMode: 'M\u00F6rkt l\u00E4ge',
    fontSize: 'Textstorlek',
    increaseFontSize: '\u00D6ka textstorlek',
    decreaseFontSize: 'Minska textstorlek',
    resetFontSize: '\u00C5terst\u00E4ll textstorlek',
    dyslexiaFont: 'Dyslexi\u00E4npassat teckensnitt',
    underlineLinks: 'Stryk under l\u00E4nkar',
    focusOutline: 'Fokusram',
    hideImages: 'D\u00F6lj bilder',
    textSpacing: 'Textavst\u00E5nd',
    pauseAnimations: 'Pausa animationer',
    largeCursor: 'Stor markör',
    highlightHeadings: 'Markera rubriker',
    invertColors: 'Invertera f\u00E4rger',
    readingGuide: 'L\u00E4sst\u00F6d',
    textToSpeech: 'Text till tal',
    focusMode: 'Fokusläge',
    lineHeight: 'Radhöjd',
    letterSpacing: 'Bokstavsavstånd',
    wordSpacing: 'Ordavstånd',
    visual: 'Visuellt',
    content: 'Innehåll',
    navigation: 'Navigering',
    deuteranopia: 'Deuteranopi (grönblindhet)',
    protanopia: 'Protanopi (rödblindhet)',
    tritanopia: 'Tritanopi (blåblindhet)',
    highlightHover: 'Markera vid hovring',
    ttsPause: 'Pausa uppläsning',
    ttsResume: 'Återuppta uppläsning',
    ttsSpeed: 'Uppläsningshastighet',
    ttsSlower: 'Långsammare',
    ttsFaster: 'Snabbare',
    quickStart: 'Snabbstart',
    presetLowVision: 'Svagt seende',
    presetDyslexia: 'Dyslexivänlig',
    presetAdhd: 'ADHD / Kognitiv',
    presetMotor: 'Motorisk / Tangentbord',
    presetMigraine: 'Säker vid migrän',
    settingsActive: 'inställningar aktiva',
    resetConfirmation: 'Alla tillgänglighetsinställningar har återställts',
    featureEnabled: 'aktiverad',
    featureDisabled: 'inaktiverad',
    tooltipMessage: 'Anpassa dina tillgänglighetsinställningar',
    increaseLineHeight: "Öka radhöjd",
    decreaseLineHeight: "Minska radhöjd",
    increaseLetterSpacing: "Öka bokstavsavstånd",
    decreaseLetterSpacing: "Minska bokstavsavstånd",
    increaseWordSpacing: "Öka ordavstånd",
    decreaseWordSpacing: "Minska ordavstånd",
    reducedTransparency: "Reducerad transparens",
    sensoryFriendly: "Sinnesvänlig",
    readableFont: "Lättläst teckensnitt",
    saturation: "Mättnad",
    increaseSaturation: "Öka mättnad",
    decreaseSaturation: "Minska mättnad",
    brightness: "Ljusstyrka",
    increaseBrightness: "Öka ljusstyrka",
    decreaseBrightness: "Minska ljusstyrka",
    suppressNotifications: "Undertryck aviseringar",
    devAuditBadge: "Utvecklargranskning",
    devAuditNoIssues: "Inga problem med alt-text hittades",
    devAuditIssues: "problem med alt-text hittades",
    zoomLockWarning: "⚠️ Den här sidan har inaktiverat nypa-för-att-zooma. Användare som förlitar sig på webbläsarzoom kan ha svårt att komma åt innehåll.",
    position: "Widgetposition",
    topLeft: "Uppe till vänster",
    topRight: "Uppe till höger",
    bottomLeft: "Nere till vänster",
    bottomRight: "Nere till höger",
    profiles: "Profiler",
    profileNamePlaceholder: "Profilnamn…",
    saveProfile: "Spara",
    loadProfile: "Ladda",
    deleteProfile: "Ta bort",
    noProfiles: "Inga sparade profiler",
    accessibilityStatementLink: "Tillgänglighetsutlåtande",
    resetAll: '\u00C5terst\u00E4ll allt',
    closeMenu: 'St\u00E4ng menyn',
    language: 'Spr\u00E5k',
    disclaimer:
      'Denna widget garanterar inte fullst\u00E4ndig efterlevnad av tillg\u00E4nglighetskrav.',
  },

  da: {
    nativeName: 'Dansk',
    menuTitle: 'Tilg\u00E6ngelighedsmenu',
    highContrast: 'H\u00F8j kontrast',
    darkMode: 'M\u00F8rk tilstand',
    fontSize: 'Skriftst\u00F8rrelse',
    increaseFontSize: 'For\u00F8g skriftst\u00F8rrelse',
    decreaseFontSize: 'Formindsk skriftst\u00F8rrelse',
    resetFontSize: 'Nulstil skriftst\u00F8rrelse',
    dyslexiaFont: 'Dyslexi-venlig skrifttype',
    underlineLinks: 'Understreg links',
    focusOutline: 'Fokusramme',
    hideImages: 'Skjul billeder',
    textSpacing: 'Tekstafstand',
    pauseAnimations: 'Pause animationer',
    largeCursor: 'Stor mark\u00F8r',
    highlightHeadings: 'Fremh\u00E6v overskrifter',
    invertColors: 'Inverter farver',
    readingGuide: 'L\u00E6sest\u00F8tte',
    textToSpeech: 'Tekst til tale',
    focusMode: 'Fokusmodus',
    lineHeight: 'Linjeafstand',
    letterSpacing: 'Bogstavafstand',
    wordSpacing: 'Ordafstand',
    visual: 'Visuel',
    content: 'Indhold',
    navigation: 'Navigation',
    deuteranopia: 'Deuteranopi (grønblindhed)',
    protanopia: 'Protanopi (rødblindhed)',
    tritanopia: 'Tritanopi (blåblindhed)',
    highlightHover: 'Fremhæv ved hover',
    ttsPause: 'Pause oplæsning',
    ttsResume: 'Genoptag oplæsning',
    ttsSpeed: 'Oplæsningshastighed',
    ttsSlower: 'Langsommere',
    ttsFaster: 'Hurtigere',
    quickStart: 'Hurtig start',
    presetLowVision: 'Svagtseende',
    presetDyslexia: 'Dysleksivenllig',
    presetAdhd: 'ADHD / Kognitiv',
    presetMotor: 'Motorisk / Tastatur',
    presetMigraine: 'Sikker ved migræne',
    settingsActive: 'indstillinger aktive',
    resetConfirmation: 'Alle tilgængelighedsindstillinger er nulstillet',
    featureEnabled: 'aktiveret',
    featureDisabled: 'deaktiveret',
    tooltipMessage: 'Tilpas dine tilgængelighedsindstillinger',
    increaseLineHeight: "Forøg linjehøjde",
    decreaseLineHeight: "Reducer linjehøjde",
    increaseLetterSpacing: "Forøg bogstavafstand",
    decreaseLetterSpacing: "Reducer bogstavafstand",
    increaseWordSpacing: "Forøg ordafstand",
    decreaseWordSpacing: "Reducer ordafstand",
    reducedTransparency: "Reduceret gennemsigtighed",
    sensoryFriendly: "Sansesvenlig",
    readableFont: "Læsbar skrifttype",
    saturation: "Mætning",
    increaseSaturation: "Forøg mætning",
    decreaseSaturation: "Reducer mætning",
    brightness: "Lysstyrke",
    increaseBrightness: "Forøg lysstyrke",
    decreaseBrightness: "Reducer lysstyrke",
    suppressNotifications: "Undertryk notifikationer",
    devAuditBadge: "Udviklerrevision",
    devAuditNoIssues: "Ingen alt-tekstproblemer fundet",
    devAuditIssues: "alt-tekstproblemer fundet",
    zoomLockWarning: "⚠️ Denne side har deaktiveret knib-for-at-zoome. Brugere, der er afhængige af browserzooming, kan have svært ved at få adgang til indhold.",
    position: "Widget-position",
    topLeft: "Øverst til venstre",
    topRight: "Øverst til højre",
    bottomLeft: "Nederst til venstre",
    bottomRight: "Nederst til højre",
    profiles: "Profiler",
    profileNamePlaceholder: "Profilnavn…",
    saveProfile: "Gem",
    loadProfile: "Indlæs",
    deleteProfile: "Slet",
    noProfiles: "Ingen gemte profiler",
    accessibilityStatementLink: "Tilgængelighedserklæring",
    resetAll: 'Nulstil alt',
    closeMenu: 'Luk menu',
    language: 'Sprog',
    disclaimer:
      'Denne widget garanterer ikke fuld overensstemmelse med tilg\u00E6ngelighedskrav.',
  },

  ro: {
    nativeName: 'Rom\u00E2n\u0103',
    menuTitle: 'Meniu accesibilitate',
    highContrast: 'Contrast ridicat',
    darkMode: 'Mod \u00EEntunecat',
    fontSize: 'Dimensiune font',
    increaseFontSize: 'M\u0103re\u015Fte fontul',
    decreaseFontSize: 'Mic\u015Foreaz\u0103 fontul',
    resetFontSize: 'Revenire la fontul implicit',
    dyslexiaFont: 'Font pentru dislexie',
    underlineLinks: 'Subliniaz\u0103 leg\u0103turile',
    focusOutline: 'Contur de focus',
    hideImages: 'Ascunde imaginile',
    textSpacing: 'Spa\u021Biere text',
    pauseAnimations: 'Pune pe pauz\u0103 anima\u021Biile',
    largeCursor: 'Cursor mare',
    highlightHeadings: 'Eviden\u021Biaz\u0103 titlurile',
    invertColors: 'Inverseaz\u0103 culorile',
    readingGuide: 'Ghid de citire',
    textToSpeech: 'Text la vorbire',
    focusMode: 'Mod focus',
    lineHeight: 'Înălțime linie',
    letterSpacing: 'Spațiere litere',
    wordSpacing: 'Spațiere cuvinte',
    visual: 'Vizual',
    content: 'Conținut',
    navigation: 'Navigare',
    deuteranopia: 'Deuteranopie (orbire verde)',
    protanopia: 'Protanopie (orbire roşie)',
    tritanopia: 'Tritanopie (orbire albastră)',
    highlightHover: 'Evidențiați la hover',
    ttsPause: 'Pauză citire',
    ttsResume: 'Reluare citire',
    ttsSpeed: 'Viteză citire',
    ttsSlower: 'Mai lent',
    ttsFaster: 'Mai rapid',
    quickStart: 'Start rapid',
    presetLowVision: 'Vedere slabă',
    presetDyslexia: 'Prietenos cu dislexia',
    presetAdhd: 'ADHD / Cognitiv',
    presetMotor: 'Motor / Tastatură',
    presetMigraine: 'Sigur pentru migrenă',
    settingsActive: 'setări active',
    resetConfirmation: 'Toate setările de accesibilitate au fost resetate',
    featureEnabled: 'activat',
    featureDisabled: 'dezactivat',
    tooltipMessage: 'Personalizați setările dvs. de accesibilitate',
    increaseLineHeight: "Mărește înălțimea liniei",
    decreaseLineHeight: "Micșorează înălțimea liniei",
    increaseLetterSpacing: "Mărește spațierea literelor",
    decreaseLetterSpacing: "Micșorează spațierea literelor",
    increaseWordSpacing: "Mărește spațierea cuvintelor",
    decreaseWordSpacing: "Micșorează spațierea cuvintelor",
    reducedTransparency: "Transparență redusă",
    sensoryFriendly: "Prietenos cu simțurile",
    readableFont: "Font lizibil",
    saturation: "Saturație",
    increaseSaturation: "Mărește saturația",
    decreaseSaturation: "Micșorează saturația",
    brightness: "Luminozitate",
    increaseBrightness: "Mărește luminozitatea",
    decreaseBrightness: "Micșorează luminozitatea",
    suppressNotifications: "Suprimă notificările",
    devAuditBadge: "Audit dezvoltator",
    devAuditNoIssues: "Nu s-au găsit probleme cu textul alternativ",
    devAuditIssues: "probleme cu textul alternativ găsite",
    zoomLockWarning: "⚠️ Această pagină a dezactivat zoom-ul prin ciupire. Utilizatorii care se bazează pe zoom-ul browserului pot întâmpina dificultăți în accesarea conținutului.",
    position: "Poziție widget",
    topLeft: "Sus stânga",
    topRight: "Sus dreapta",
    bottomLeft: "Jos stânga",
    bottomRight: "Jos dreapta",
    profiles: "Profile",
    profileNamePlaceholder: "Nume profil…",
    saveProfile: "Salvează",
    loadProfile: "Încarcă",
    deleteProfile: "Șterge",
    noProfiles: "Nu există profile salvate",
    accessibilityStatementLink: "Declarație de accesibilitate",
    resetAll: 'Reseteaz\u0103 tot',
    closeMenu: '\u00CEnchide meniul',
    language: 'Limb\u0103',
    disclaimer:
      'Acest widget nu garanteaz\u0103 conformitatea complet\u0103 cu cerin\u021Bele de accesibilitate.',
  },

  el: {
    nativeName: '\u0395\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC',
    menuTitle: '\u039C\u03B5\u03BD\u03BF\u03CD \u03C0\u03C1\u03BF\u03C3\u03B2\u03B1\u03C3\u03B9\u03BC\u03CC\u03C4\u03B7\u03C4\u03B1\u03C2',
    highContrast: '\u03A5\u03C8\u03B7\u03BB\u03AE \u03B1\u03BD\u03C4\u03AF\u03B8\u03B5\u03C3\u03B7',
    darkMode: '\u0398\u03B5\u03BC\u03B1 \u03C3\u03BA\u03BF\u03C4\u03B5\u03B9\u03BD\u03BF\u03CD',
    fontSize: '\u039C\u03AD\u03B3\u03B5\u03B8\u03BF\u03C2 \u03B3\u03C1\u03B1\u03BC\u03BC\u03B1\u03C4\u03BF\u03C3\u03B5\u03B9\u03C1\u03AC\u03C2',
    increaseFontSize: '\u0391\u03CD\u03BE\u03B7\u03C3\u03B7 \u03B3\u03C1\u03B1\u03BC\u03BC\u03B1\u03C4\u03BF\u03C3\u03B5\u03B9\u03C1\u03AC\u03C2',
    decreaseFontSize: '\u039C\u03B5\u03AF\u03C9\u03C3\u03B7 \u03B3\u03C1\u03B1\u03BC\u03BC\u03B1\u03C4\u03BF\u03C3\u03B5\u03B9\u03C1\u03AC\u03C2',
    resetFontSize: '\u0395\u03C0\u03B1\u03BD\u03B1\u03C6\u03BF\u03C1\u03AC \u03BC\u03B5\u03B3\u03AD\u03B8\u03BF\u03C5\u03C2 \u03B3\u03C1\u03B1\u03BC\u03BC\u03B1\u03C4\u03BF\u03C3\u03B5\u03B9\u03C1\u03AC\u03C2',
    dyslexiaFont: '\u0393\u03C1\u03B1\u03BC\u03BC\u03B1\u03C4\u03BF\u03C3\u03B5\u03B9\u03C1\u03AC \u03B3\u03B9\u03B1 \u03B4\u03C5\u03C3\u03BB\u03B5\u03BE\u03AF\u03B1',
    underlineLinks: '\u03A5\u03C0\u03BF\u03B3\u03C1\u03AC\u03BC\u03BC\u03B9\u03C3\u03B7 \u03C3\u03C5\u03BD\u03B4\u03AD\u03C3\u03BC\u03C9\u03BD',
    focusOutline: '\u03A0\u03B5\u03C1\u03AF\u03B3\u03C1\u03B1\u03BC\u03BC\u03B1 \u03B5\u03C3\u03C4\u03AF\u03B1\u03C3\u03B7\u03C2',
    hideImages: '\u0391\u03C0\u03CC\u03BA\u03C1\u03C5\u03C8\u03B7 \u03B5\u03B9\u03BA\u03CC\u03BD\u03C9\u03BD',
    textSpacing: '\u0394\u03B9\u03AC\u03C3\u03C4\u03B1\u03C3\u03B7 \u03BA\u03B5\u03B9\u03BC\u03AD\u03BD\u03BF\u03C5',
    pauseAnimations: '\u0391\u03BD\u03B1\u03C3\u03C4\u03BF\u03BB\u03AE \u03BA\u03B9\u03BD\u03BF\u03CD\u03BC\u03B5\u03BD\u03C9\u03BD \u03B5\u03B9\u03BA\u03CC\u03BD\u03C9\u03BD',
    largeCursor: '\u039C\u03B5\u03B3\u03AC\u03BB\u03BF\u03C2 \u03B4\u03C1\u03BF\u03BC\u03AD\u03B1\u03C2',
    highlightHeadings: '\u0395\u03C0\u03B9\u03C3\u03AE\u03BC\u03B1\u03BD\u03C3\u03B7 \u03C4\u03AF\u03C4\u03BB\u03C9\u03BD',
    invertColors: '\u0391\u03BD\u03C4\u03B9\u03C3\u03C4\u03C1\u03BF\u03C6\u03AE \u03C7\u03C1\u03C9\u03BC\u03AC\u03C4\u03C9\u03BD',
    readingGuide: '\u039F\u03B4\u03B7\u03B3\u03CC\u03C2 \u03B1\u03BD\u03AC\u03B3\u03BD\u03C9\u03C3\u03B7\u03C2',
    textToSpeech: '\u039A\u03B5\u03AF\u03BC\u03B5\u03BD\u03BF \u03C3\u03B5 \u03BF\u03BC\u03B9\u03BB\u03AF\u03B1',
    focusMode: 'Λειτουργία εστίασης',
    lineHeight: '΍ψος γραμμής',
    letterSpacing: 'Διαστοίχιση',
    wordSpacing: 'Αναλογία λέξεων',
    visual: 'Οπτικό',
    content: 'Περιεχόμενο',
    navigation: 'Πλοήγηση',
    deuteranopia: 'Δευτερανωπία',
    protanopia: 'Πρωτανωπία',
    tritanopia: 'Τριτανωπία',
    highlightHover: 'Επισήμανση κατά τη μετακίνηση',
    ttsPause: 'Παύση ανάγνωσης',
    ttsResume: 'Συνέχεια ανάγνωσης',
    ttsSpeed: 'Ταχύτητα ανάγνωσης',
    ttsSlower: 'Πιο αργά',
    ttsFaster: 'Πιο γρήγορα',
    quickStart: 'Γρήγορη εκκίνηση',
    presetLowVision: 'Μειωμένη όραση',
    presetDyslexia: 'Φιλικό για δυσλεξία',
    presetAdhd: 'ΔΕΠΥ / Γνωστικό',
    presetMotor: 'Κινητικό / Πληκτρολόγιο',
    presetMigraine: 'Ασφαλές για ημικρανία',
    settingsActive: 'ρυθμίσεις ενεργές',
    resetConfirmation: 'Όλες οι ρυθμίσεις προσβασιμότητας επαναφέρθηκαν',
    featureEnabled: 'ενεργοποιήθηκε',
    featureDisabled: 'απενεργοποιήθηκε',
    tooltipMessage: 'Προσαρμόστε τις ρυθμίσεις προσβασιμότητας σας',
    increaseLineHeight: "Αύξηση ύψους γραμμής",
    decreaseLineHeight: "Μείωση ύψους γραμμής",
    increaseLetterSpacing: "Αύξηση διαστήματος γραμμάτων",
    decreaseLetterSpacing: "Μείωση διαστήματος γραμμάτων",
    increaseWordSpacing: "Αύξηση διαστήματος λέξεων",
    decreaseWordSpacing: "Μείωση διαστήματος λέξεων",
    reducedTransparency: "Μειωμένη διαφάνεια",
    sensoryFriendly: "Φιλικό προς τις αισθήσεις",
    readableFont: "Ευανάγνωστη γραμματοσειρά",
    saturation: "Κορεσμός",
    increaseSaturation: "Αύξηση κορεσμού",
    decreaseSaturation: "Μείωση κορεσμού",
    brightness: "Φωτεινότητα",
    increaseBrightness: "Αύξηση φωτεινότητας",
    decreaseBrightness: "Μείωση φωτεινότητας",
    suppressNotifications: "Απόκρυψη ειδοποιήσεων",
    devAuditBadge: "Έλεγχος προγραμματιστή",
    devAuditNoIssues: "Δεν βρέθηκαν προβλήματα εναλλακτικού κειμένου",
    devAuditIssues: "προβλήματα εναλλακτικού κειμένου βρέθηκαν",
    zoomLockWarning: "⚠️ Αυτή η σελίδα έχει απενεργοποιήσει τη μεγέθυνση με κίνηση. Οι χρήστες που εξαρτώνται από τη μεγέθυνση του προγράμματος περιήγησης ενδέχεται να αντιμετωπίσουν δυσκολίες στην πρόσβαση στο περιεχόμενο.",
    position: "Θέση widget",
    topLeft: "Πάνω αριστερά",
    topRight: "Πάνω δεξιά",
    bottomLeft: "Κάτω αριστερά",
    bottomRight: "Κάτω δεξιά",
    profiles: "Προφίλ",
    profileNamePlaceholder: "Όνομα προφίλ…",
    saveProfile: "Αποθήκευση",
    loadProfile: "Φόρτωση",
    deleteProfile: "Διαγραφή",
    noProfiles: "Δεν υπάρχουν αποθηκευμένα προφίλ",
    accessibilityStatementLink: "Δήλωση προσβασιμότητας",
    resetAll: '\u0395\u03C0\u03B1\u03BD\u03B1\u03C6\u03BF\u03C1\u03AC \u03CC\u03BB\u03C9\u03BD',
    closeMenu: '\u039A\u03BB\u03B5\u03AF\u03C3\u03B9\u03BC\u03BF \u03BC\u03B5\u03BD\u03BF\u03CD',
    language: '\u0393\u03BB\u03CE\u03C3\u03C3\u03B1',
    disclaimer:
      '\u0391\u03C5\u03C4\u03CC \u03C4\u03BF widget \u03B4\u03B5\u03BD \u03B5\u03B3\u03B3\u03C5\u03AC\u03C4\u03B1\u03B9 \u03C0\u03BB\u03AE\u03C1\u03B7 \u03C3\u03C5\u03BC\u03BC\u03CC\u03C1\u03C6\u03C9\u03C3\u03B7 \u03BC\u03B5 \u03C4\u03B1 \u03C0\u03C1\u03CC\u03C4\u03C5\u03C0\u03B1 \u03C0\u03C1\u03BF\u03C3\u03B2\u03B1\u03C3\u03B9\u03BC\u03CC\u03C4\u03B7\u03C4\u03B1\u03C2.',
  },

  cs: {
    nativeName: '\u010Ce\u0161tina',
    menuTitle: 'Nab\u00EDdka p\u0159\u00EDstupnosti',
    highContrast: 'Vysok\u00FD kontrast',
    darkMode: 'Tmav\u00FD re\u017Eim',
    fontSize: 'Velikost p\u00EDsma',
    increaseFontSize: 'Zv\u011Bt\u0161it p\u00EDsmo',
    decreaseFontSize: 'Zmen\u0161it p\u00EDsmo',
    resetFontSize: 'Obnovit velikost p\u00EDsma',
    dyslexiaFont: 'P\u00EDsmo pro dyslektiky',
    underlineLinks: 'Podtrhnout odkazy',
    focusOutline: 'Obrys fokusu',
    hideImages: 'Skr\u00FDt obr\u00E1zky',
    textSpacing: 'Rozestupy textu',
    pauseAnimations: 'Pozastavit animace',
    largeCursor: 'Velk\u00FD kurzor',
    highlightHeadings: 'Zvýraznit nadpisy',
    invertColors: 'Invertovat barvy',
    readingGuide: '\u010Cten\u00E1\u0159sk\u00FD pr\u016Fvodce',
    textToSpeech: 'Text na \u0159e\u010D',
    focusMode: 'Režim zaostření',
    lineHeight: 'Výška řádku',
    letterSpacing: 'Mezery mezi písmeny',
    wordSpacing: 'Mezery mezi slovy',
    visual: 'Vizuální',
    content: 'Obsah',
    navigation: 'Navigace',
    deuteranopia: 'Deuteranopie (zelená slepota)',
    protanopia: 'Protanopie (červená slepota)',
    tritanopia: 'Tritanopie (modrá slepota)',
    highlightHover: 'Zvýraznit při přechodu',
    ttsPause: 'Pozastavit čtení',
    ttsResume: 'Obnovit čtení',
    ttsSpeed: 'Rychlost čtení',
    ttsSlower: 'Pomaleji',
    ttsFaster: 'Rychleji',
    quickStart: 'Rychlý start',
    presetLowVision: 'Slabé vidění',
    presetDyslexia: 'Přátelský pro dyslexii',
    presetAdhd: 'ADHD / Kognitivní',
    presetMotor: 'Motorický / Klávesnice',
    presetMigraine: 'Bezpečný při migréně',
    settingsActive: 'nastavení aktivní',
    resetConfirmation: 'Všechna nastavení přístupnosti byla resetována',
    featureEnabled: 'povoleno',
    featureDisabled: 'zakázáno',
    tooltipMessage: 'Přizpůsobte nastavení přístupnosti',
    increaseLineHeight: "Zvýšit výšku řádku",
    decreaseLineHeight: "Snížit výšku řádku",
    increaseLetterSpacing: "Zvýšit mezery mezi písmeny",
    decreaseLetterSpacing: "Snížit mezery mezi písmeny",
    increaseWordSpacing: "Zvýšit mezery mezi slovy",
    decreaseWordSpacing: "Snížit mezery mezi slovy",
    reducedTransparency: "Snížená průhlednost",
    sensoryFriendly: "Přívětivý pro smysly",
    readableFont: "Čitelné písmo",
    saturation: "Sytost",
    increaseSaturation: "Zvýšit sytost",
    decreaseSaturation: "Snížit sytost",
    brightness: "Jas",
    increaseBrightness: "Zvýšit jas",
    decreaseBrightness: "Snížit jas",
    suppressNotifications: "Skrýt oznámení",
    devAuditBadge: "Audit vývojáře",
    devAuditNoIssues: "Nenalezeny žádné problémy s alternativním textem",
    devAuditIssues: "nalezeny problémy s alternativním textem",
    zoomLockWarning: "⚠️ Tato stránka zakázala přiblížení gestem. Uživatelé, kteří se spoléhají na přiblížení prohlížeče, mohou mít potíže s přístupem k obsahu.",
    position: "Pozice widgetu",
    topLeft: "Vlevo nahoře",
    topRight: "Vpravo nahoře",
    bottomLeft: "Vlevo dole",
    bottomRight: "Vpravo dole",
    profiles: "Profily",
    profileNamePlaceholder: "Název profilu…",
    saveProfile: "Uložit",
    loadProfile: "Načíst",
    deleteProfile: "Smazat",
    noProfiles: "Žádné uložené profily",
    accessibilityStatementLink: "Prohlášení o přístupnosti",
    resetAll: 'Obnovit v\u0161e',
    closeMenu: 'Zav\u0159\u00EDt nab\u00EDdku',
    language: 'Jazyk',
    disclaimer:
      'Tento widget nezaru\u010Duje \u00FApln\u00E9 spln\u011Bn\u00ED po\u017Eadavk\u016F na p\u0159\u00EDstupnost.',
  },

  hu: {
    nativeName: 'Magyar',
    menuTitle: 'Kiseg\u00EDt\u0151 men\u00FC',
    highContrast: 'Magas kontraszt',
    darkMode: 'S\u00F6t\u00E9t m\u00F3d',
    fontSize: 'Bet\u0171m\u00E9ret',
    increaseFontSize: 'Bet\u0171m\u00E9ret n\u00F6vel\u00E9se',
    decreaseFontSize: 'Bet\u0171m\u00E9ret cs\u00F6kkent\u00E9se',
    resetFontSize: 'Bet\u0171m\u00E9ret visszaallít\u00E1sa',
    dyslexiaFont: 'Diszlexia-bar\u00E1t bet\u0171t\u00EDpus',
    underlineLinks: 'Hivatkoz\u00E1sok al\u00E1h\u00FAz\u00E1sa',
    focusOutline: 'F\u00F3kusz keret',
    hideImages: 'K\u00E9pek elrejt\u00E9se',
    textSpacing: 'Sz\u00F6veg t\u00E1vols\u00E1g',
    pauseAnimations: 'Anim\u00E1ci\u00F3k sz\u00FCneteltet\u00E9se',
    largeCursor: 'Nagy kurzor',
    highlightHeadings: 'C\u00EDmsorok kiemel\u00E9se',
    invertColors: 'Sz\u00EDnek invertál\u00E1sa',
    readingGuide: 'Olvas\u00E1si \u00FAtvonal',
    textToSpeech: 'Sz\u00F6veg felolvas\u00E1sa',
    focusMode: 'Fókusz mód',
    lineHeight: 'Sormák',
    letterSpacing: 'Betűköz',
    wordSpacing: 'Szóköz',
    visual: 'Vizuális',
    content: 'Tartalom',
    navigation: 'Navigáció',
    deuteranopia: 'Deuteranopia (zöldvakság)',
    protanopia: 'Protanopia (pirosvaság)',
    tritanopia: 'Tritanopia (kékvakság)',
    highlightHover: 'Kiemelés lebögetéskor',
    ttsPause: 'Olvasás szüneteltetése',
    ttsResume: 'Olvasás folytatása',
    ttsSpeed: 'Olvasási sebesség',
    ttsSlower: 'Lassabb',
    ttsFaster: 'Gyorsabb',
    quickStart: 'Gyors indítás',
    presetLowVision: 'Gyenglétlátó',
    presetDyslexia: 'Diszlexia-barát',
    presetAdhd: 'ADHD / Kognitiv',
    presetMotor: 'Mozgási / Billentyűzet',
    presetMigraine: 'Biztonságos migrén esetén',
    settingsActive: 'aktív beállítások',
    resetConfirmation: 'Minden akadálymentesítési beállítás visszaállítva',
    featureEnabled: 'engedélyezve',
    featureDisabled: 'letiltva',
    tooltipMessage: 'Testreszabhatja az akadálymentesítési beállításokat',
    increaseLineHeight: "Sormagasság növelése",
    decreaseLineHeight: "Sormagasság csökkentése",
    increaseLetterSpacing: "Betűközök növelése",
    decreaseLetterSpacing: "Betűközök csökkentése",
    increaseWordSpacing: "Szóközök növelése",
    decreaseWordSpacing: "Szóközök csökkentése",
    reducedTransparency: "Csökkentett átlátszóság",
    sensoryFriendly: "Érzékszerv-barát",
    readableFont: "Olvasható betűtípus",
    saturation: "Telítettség",
    increaseSaturation: "Telítettség növelése",
    decreaseSaturation: "Telítettség csökkentése",
    brightness: "Fényerő",
    increaseBrightness: "Fényerő növelése",
    decreaseBrightness: "Fényerő csökkentése",
    suppressNotifications: "Értesítések elnémítása",
    devAuditBadge: "Fejlesztői audit",
    devAuditNoIssues: "Nem találhatók alt szöveggel kapcsolatos problémák",
    devAuditIssues: "alt szöveggel kapcsolatos problémák találhatók",
    zoomLockWarning: "⚠️ Ez az oldal letiltotta a csippentős nagyítást. Azok a felhasználók, akik a böngésző nagyítástól függenek, nehézségekbe ütközhetnek a tartalom elérésekor.",
    position: "Widget pozíció",
    topLeft: "Bal felső",
    topRight: "Jobb felső",
    bottomLeft: "Bal alsó",
    bottomRight: "Jobb alsó",
    profiles: "Profilok",
    profileNamePlaceholder: "Profil neve…",
    saveProfile: "Mentés",
    loadProfile: "Betöltés",
    deleteProfile: "Törlés",
    noProfiles: "Nincsenek mentett profilok",
    accessibilityStatementLink: "Akadálymentességi nyilatkozat",
    resetAll: 'Minden visszaallít\u00E1sa',
    closeMenu: 'Men\u00FC bez\u00E1r\u00E1sa',
    language: 'Nyelv',
    disclaimer:
      'Ez a widget nem garantálja a teljes akadálymentesít\u00E9si megfelel\u00E9st.',
  },

  kk: {
    nativeName: '\u049A\u0430\u0437\u0430\u049B\u0448\u0430',
    menuTitle: '\u049A\u043E\u043B\u0436\u0435\u0442\u0456\u043C\u0434\u0456\u043B\u0456\u043A \u043C\u04D9\u0437\u0456\u0440\u0456',
    highContrast: '\u0416\u043E\u0493\u0430\u0440\u044B \u043A\u043E\u043D\u0442\u0440\u0430\u0441\u0442',
    darkMode: '\u049A\u0430\u0440\u0430\u04A3\u0493\u044B \u0440\u0435\u0436\u0438\u043C',
    fontSize: '\u049A\u0430\u0440\u0456\u043F \u04E9\u043B\u0448\u0435\u043C\u0456',
    increaseFontSize: '\u049A\u0430\u0440\u0456\u043F \u04E9\u043B\u0448\u0435\u043C\u0456\u043D \u04B1\u043B\u0493\u0430\u0439\u0442\u0443',
    decreaseFontSize: '\u049A\u0430\u0440\u0456\u043F \u04E9\u043B\u0448\u0435\u043C\u0456\u043D \u043A\u0456\u0448\u0456\u0440\u0435\u0439\u0442\u0443',
    resetFontSize: '\u049A\u0430\u0440\u0456\u043F \u04E9\u043B\u0448\u0435\u043C\u0456\u043D \u0431\u0430\u0441\u0442\u0430\u043F\u049B\u044B \u049B\u0430\u043B\u043F\u044B\u043D\u0430 \u049B\u0430\u0439\u0442\u0430\u0440\u0443',
    dyslexiaFont: '\u0414\u0438\u0441\u043B\u0435\u043A\u0441\u0438\u044F\u0493\u0430 \u0430\u0440\u043D\u0430\u043B\u0493\u0430\u043D \u049B\u0430\u0440\u0456\u043F',
    underlineLinks: '\u0421\u0456\u043B\u0442\u0435\u043C\u0435\u043B\u0435\u0440\u0434\u0456 \u0430\u0441\u0442\u044B \u0441\u044B\u0437\u0443',
    focusOutline: '\u0424\u043E\u043A\u0443\u0441 \u0436\u0438\u0435\u0433\u0456',
    hideImages: '\u0421\u0443\u0440\u0435\u0442\u0442\u0435\u0440\u0434\u0456 \u0436\u0430\u0441\u044B\u0440\u0443',
    textSpacing: '\u041C\u04D9\u0442\u0456\u043D \u0430\u0440\u0430\u043B\u044B\u0493\u044B',
    pauseAnimations: '\u0410\u043D\u0438\u043C\u0430\u0446\u0438\u044F\u043D\u044B \u0442\u043E\u049B\u0442\u0430\u0442\u0443',
    largeCursor: '\u04AE\u043B\u043A\u0435\u043D \u043A\u0435\u0440\u0441\u043E\u0440',
    highlightHeadings: '\u0422\u0430\u049B\u044B\u0440\u044B\u043F\u0442\u0430\u0440\u0434\u044B \u0431\u04E9\u043B\u0456\u043F \u043A\u04E9\u0440\u0441\u0435\u0442\u0443',
    invertColors: '\u0422\u04AF\u0441\u0442\u0435\u0440\u0434\u0456 \u04E9\u0437\u0433\u0435\u0440\u0442\u0443',
    readingGuide: '\u041E\u049B\u0443 \u043D\u04B1\u0441\u049B\u0430\u0443\u0448\u044B\u0441\u044B',
    textToSpeech: '\u041C\u04D9\u0442\u0456\u043D\u043D\u0435\u043D \u0434\u044B\u0431\u044B\u0441\u049B\u0430',
    focusMode: 'Фокус режимі',
    lineHeight: 'Жол биіктігі',
    letterSpacing: 'Әріп аралығы',
    wordSpacing: 'Сөздер аралығы',
    visual: 'Визуалды',
    content: 'Мазмұн',
    navigation: 'Навигация',
    deuteranopia: 'Жасыл түстік',
    protanopia: 'қызыл түстік',
    tritanopia: 'Кҩк түстік',
    highlightHover: 'Тінтіуір үстінде бөлектеу',
    ttsPause: 'Оқуды тоқтату',
    ttsResume: 'Оқуды жалғастыру',
    ttsSpeed: 'Оқу жылдамдығы',
    ttsSlower: 'Баяуырақ',
    ttsFaster: 'Жылдамырақ',
    quickStart: 'Жылдам бастау',
    presetLowVision: 'Нашар көру',
    presetDyslexia: 'Дислексияға ыңғайлы',
    presetAdhd: 'СДЗБ / Танымдық',
    presetMotor: 'Моторлы / Пернетақта',
    presetMigraine: 'Мигреньге қауіпсіз',
    settingsActive: 'параметрлер белсенді',
    resetConfirmation: 'Барлық қолжетімділік параметрлері қалпына келтірілді',
    featureEnabled: 'қосылған',
    featureDisabled: 'өшірілген',
    tooltipMessage: 'Қолжетімділік параметрлеріңізді баптап',
    increaseLineHeight: "Жол биіктігін арттыру",
    decreaseLineHeight: "Жол биіктігін азайту",
    increaseLetterSpacing: "Әріп аралығын арттыру",
    decreaseLetterSpacing: "Әріп аралығын азайту",
    increaseWordSpacing: "Сөз аралығын арттыру",
    decreaseWordSpacing: "Сөз аралығын азайту",
    reducedTransparency: "Азайтылған мөлдірлік",
    sensoryFriendly: "Сезімге жайлы",
    readableFont: "Оқылатын қаріп",
    saturation: "Қанықтылық",
    increaseSaturation: "Қанықтылықты арттыру",
    decreaseSaturation: "Қанықтылықты азайту",
    brightness: "Жарықтық",
    increaseBrightness: "Жарықтықты арттыру",
    decreaseBrightness: "Жарықтықты азайту",
    suppressNotifications: "Хабарландыруларды басу",
    devAuditBadge: "Әзірлеуші аудиті",
    devAuditNoIssues: "Alt мәтін мәселелері табылмады",
    devAuditIssues: "alt мәтін мәселелері табылды",
    zoomLockWarning: "⚠️ Бұл бет қысып-масштабтауды өшірді. Шолғыш масштабына тәуелді пайдаланушылар мазмұнға қол жеткізуде қиындықтарға тап болуы мүмкін.",
    position: "Виджет орны",
    topLeft: "Жоғарғы сол",
    topRight: "Жоғарғы оң",
    bottomLeft: "Төменгі сол",
    bottomRight: "Төменгі оң",
    profiles: "Профильдер",
    profileNamePlaceholder: "Профиль атауы…",
    saveProfile: "Сақтау",
    loadProfile: "Жүктеу",
    deleteProfile: "Жою",
    noProfiles: "Сақталған профильдер жоқ",
    accessibilityStatementLink: "Қолжетімділік мәлімдемесі",
    resetAll: '\u0411\u04D9\u0440\u0456\u043D \u0431\u0430\u0441\u0442\u0430\u043F\u049B\u044B \u049B\u0430\u043B\u043F\u044B\u043D\u0430 \u049B\u0430\u0439\u0442\u0430\u0440\u0443',
    closeMenu: '\u041C\u04D9\u0437\u0456\u0440\u0434\u0456 \u0436\u0430\u0431\u0443',
    language: '\u0422\u0456\u043B',
    disclaimer:
      '\u0411\u04B1\u043B \u0432\u0438\u0434\u0436\u0435\u0442 \u049B\u043E\u043B\u0436\u0435\u0442\u0456\u043C\u0434\u0456\u043B\u0456\u043A \u0442\u0430\u043B\u0430\u043F\u0442\u0430\u0440\u044B\u043D\u044B\u04A3 \u0442\u043E\u043B\u044B\u049B \u0441\u0430\u049B\u0442\u0430\u043B\u044B\u043D\u0443\u044B\u043D \u043A\u0435\u043F\u0456\u043B\u0434\u0435\u043C\u0435\u0439\u0434\u0456.',
  },

  sr: {
    nativeName: '\u0421\u0440\u043F\u0441\u043A\u0438',
    menuTitle: '\u041C\u0435\u043D\u0438 \u043F\u0440\u0438\u0441\u0442\u0443\u043F\u0430\u0447\u043D\u043E\u0441\u0442\u0438',
    highContrast: '\u0412\u0438\u0441\u043E\u043A\u0438 \u043A\u043E\u043D\u0442\u0440\u0430\u0441\u0442',
    darkMode: '\u0422\u0430\u043C\u043D\u0438 \u0440\u0435\u0436\u0438\u043C',
    fontSize: '\u0412\u0435\u043B\u0438\u0447\u0438\u043D\u0430 \u0444\u043E\u043D\u0442\u0430',
    increaseFontSize: '\u041F\u043E\u0432\u0435\u045B\u0430\u0458 \u0444\u043E\u043D\u0442',
    decreaseFontSize: '\u0421\u043C\u0430\u045A\u0438 \u0444\u043E\u043D\u0442',
    resetFontSize: '\u0420\u0435\u0441\u0435\u0442\u0443\u0458 \u0432\u0435\u043B\u0438\u0447\u0438\u043D\u0443 \u0444\u043E\u043D\u0442\u0430',
    dyslexiaFont: '\u0424\u043E\u043D\u0442 \u043F\u0440\u0438\u043B\u0430\u0433\u043E\u0452\u0435\u043D \u0434\u0438\u0441\u043B\u0435\u043A\u0441\u0438\u0458\u0438',
    underlineLinks: '\u041F\u043E\u0434\u0432\u0443\u0447\u0438 \u0432\u0435\u0437\u0435',
    focusOutline: '\u041E\u043A\u0432\u0438\u0440 \u0444\u043E\u043A\u0443\u0441\u0430',
    hideImages: '\u0421\u0430\u043A\u0440\u0438\u0458 \u0441\u043B\u0438\u043A\u0435',
    textSpacing: '\u0420\u0430\u0437\u043C\u0430\u043A \u0442\u0435\u043A\u0441\u0442\u0430',
    pauseAnimations: '\u041F\u0430\u0443\u0437\u0438\u0440\u0430\u0458 \u0430\u043D\u0438\u043C\u0430\u0446\u0438\u0458\u0435',
    largeCursor: '\u0412\u0435\u043B\u0438\u043A\u0438 \u043A\u0443\u0440\u0441\u043E\u0440',
    highlightHeadings: '\u0418\u0441\u0442\u0430\u043A\u043D\u0438 \u043D\u0430\u0441\u043B\u043E\u0432\u0435',
    invertColors: '\u0418\u043D\u0432\u0435\u0440\u0442\u0443\u0458 \u0431\u043E\u0458\u0435',
    readingGuide: '\u0412\u043E\u0434\u0438\u0447 \u0437\u0430 \u0447\u0438\u0442\u0430\u045A\u0435',
    textToSpeech: '\u0422\u0435\u043A\u0441\u0442 \u0443 \u0433\u043E\u0432\u043E\u0440',
    focusMode: 'Режим фокуса',
    lineHeight: 'Висина реда',
    letterSpacing: 'Размак слова',
    wordSpacing: 'Размак речи',
    visual: 'Визуелно',
    content: 'Садржај',
    navigation: 'Навигација',
    deuteranopia: 'Деутеранопија',
    protanopia: 'Протанопија',
    tritanopia: 'Тританопија',
    highlightHover: 'Означи при преласку',
    ttsPause: 'Паузирај читање',
    ttsResume: 'Настави читање',
    ttsSpeed: 'Брзина читања',
    ttsSlower: 'Спорије',
    ttsFaster: 'Брже',
    quickStart: 'Брзи почетак',
    presetLowVision: 'Слаб вид',
    presetDyslexia: 'Прилагођен дислексији',
    presetAdhd: 'АДХД / Когнитивни',
    presetMotor: 'Моторни / Тастатура',
    presetMigraine: 'Безбедно при мигрени',
    settingsActive: 'активна подешавања',
    resetConfirmation: 'Све поставке приступачности су поништене',
    featureEnabled: 'омогућено',
    featureDisabled: 'онемогућено',
    tooltipMessage: 'Прилагодите поставке приступачности',
    increaseLineHeight: "Повећај висину реда",
    decreaseLineHeight: "Смањи висину реда",
    increaseLetterSpacing: "Повећај размак слова",
    decreaseLetterSpacing: "Смањи размак слова",
    increaseWordSpacing: "Повећај размак речи",
    decreaseWordSpacing: "Смањи размак речи",
    reducedTransparency: "Смањена провидност",
    sensoryFriendly: "Погодан за чула",
    readableFont: "Читљиво писмо",
    saturation: "Засићеност",
    increaseSaturation: "Повећај засићеност",
    decreaseSaturation: "Смањи засићеност",
    brightness: "Осветљеност",
    increaseBrightness: "Повећај осветљеност",
    decreaseBrightness: "Смањи осветљеност",
    suppressNotifications: "Сакриј обавештења",
    devAuditBadge: "Ревизија програмера",
    devAuditNoIssues: "Нису пронађени проблеми са алтернативним текстом",
    devAuditIssues: "пронађени проблеми са алтернативним текстом",
    zoomLockWarning: "⚠️ Ова страница је онемогућила увеличавање прстима. Корисници који зависе од увеличавања прегледача могу имати потешкоћа у приступу садржају.",
    position: "Позиција виџета",
    topLeft: "Горе лево",
    topRight: "Горе десно",
    bottomLeft: "Доле лево",
    bottomRight: "Доле десно",
    profiles: "Профили",
    profileNamePlaceholder: "Назив профила…",
    saveProfile: "Сачувај",
    loadProfile: "Учитај",
    deleteProfile: "Обриши",
    noProfiles: "Нема сачуваних профила",
    accessibilityStatementLink: "Изјава о приступачности",
    resetAll: '\u0420\u0435\u0441\u0435\u0442\u0443\u0458 \u0441\u0432\u0435',
    closeMenu: '\u0417\u0430\u0442\u0432\u043E\u0440\u0438 \u043C\u0435\u043D\u0438',
    language: '\u0408\u0435\u0437\u0438\u043A',
    disclaimer:
      '\u041E\u0432\u0430\u0458 \u0432\u0438\u0452\u0435\u0442 \u043D\u0435 \u0433\u0430\u0440\u0430\u043D\u0442\u0443\u0458\u0435 \u043F\u043E\u0442\u043F\u0443\u043D\u0443 \u0443\u0441\u043A\u043B\u0430\u0452\u0435\u043D\u043E\u0441\u0442 \u0441\u0430 \u0441\u0442\u0430\u043D\u0434\u0430\u0440\u0434\u0438\u043C\u0430 \u043F\u0440\u0438\u0441\u0442\u0443\u043F\u0430\u0447\u043D\u043E\u0441\u0442\u0438.',
  },

  no: {
    nativeName: 'Norsk',
    menuTitle: 'Tilgjengelighetsmeny',
    highContrast: 'H\u00F8y kontrast',
    darkMode: 'M\u00F8rk modus',
    fontSize: 'Skriftst\u00F8rrelse',
    increaseFontSize: '\u00D8k skriftst\u00F8rrelse',
    decreaseFontSize: 'Reduser skriftst\u00F8rrelse',
    resetFontSize: 'Tilbakestill skriftst\u00F8rrelse',
    dyslexiaFont: 'Dysleksivennlig skrifttype',
    underlineLinks: 'Understrek lenker',
    focusOutline: 'Fokusramme',
    hideImages: 'Skjul bilder',
    textSpacing: 'Tekstmellomrom',
    pauseAnimations: 'Sett animasjoner p\u00E5 pause',
    largeCursor: 'Stor mark\u00F8r',
    highlightHeadings: 'Fremhev overskrifter',
    invertColors: 'Inverter farger',
    readingGuide: 'Lesehjelp',
    textToSpeech: 'Tekst til tale',
    focusMode: 'Fokusmodus',
    lineHeight: 'Linjehøyde',
    letterSpacing: 'Bokstavavstand',
    wordSpacing: 'Ordavstand',
    visual: 'Visuell',
    content: 'Innhold',
    navigation: 'Navigering',
    deuteranopia: 'Deuteranopi (grønnblindhet)',
    protanopia: 'Protanopi (rødblindhet)',
    tritanopia: 'Tritanopi (blåblindhet)',
    highlightHover: 'Merk ved hover',
    ttsPause: 'Pause opplesing',
    ttsResume: 'Gjenoppta opplesing',
    ttsSpeed: 'Lesehastighet',
    ttsSlower: 'Langsommere',
    ttsFaster: 'Raskere',
    quickStart: 'Hurtigstart',
    presetLowVision: 'Svaksyn',
    presetDyslexia: 'Dysleksivennlig',
    presetAdhd: 'ADHD / Kognitiv',
    presetMotor: 'Motorisk / Tastatur',
    presetMigraine: 'Trygg ved migrene',
    settingsActive: 'innstillinger aktive',
    resetConfirmation: 'Alle tilgjengelighetsinnstillinger er tilbakestilt',
    featureEnabled: 'aktivert',
    featureDisabled: 'deaktivert',
    tooltipMessage: 'Tilpass tilgjengelighetsinnstillingene dine',
    increaseLineHeight: "Øk linjehøyde",
    decreaseLineHeight: "Reduser linjehøyde",
    increaseLetterSpacing: "Øk bokstavavstand",
    decreaseLetterSpacing: "Reduser bokstavavstand",
    increaseWordSpacing: "Øk ordavstand",
    decreaseWordSpacing: "Reduser ordavstand",
    reducedTransparency: "Redusert gjennomsiktighet",
    sensoryFriendly: "Sansevennlig",
    readableFont: "Lesbar skrifttype",
    saturation: "Metning",
    increaseSaturation: "Øk metning",
    decreaseSaturation: "Reduser metning",
    brightness: "Lysstyrke",
    increaseBrightness: "Øk lysstyrke",
    decreaseBrightness: "Reduser lysstyrke",
    suppressNotifications: "Undertrykk varsler",
    devAuditBadge: "Utviklerkontroll",
    devAuditNoIssues: "Ingen alt-tekstproblemer funnet",
    devAuditIssues: "alt-tekstproblemer funnet",
    zoomLockWarning: "⚠️ Denne siden har deaktivert klyp-for-å-zoome. Brukere som er avhengige av nettleserzooming, kan ha problemer med å få tilgang til innhold.",
    position: "Widgetposisjon",
    topLeft: "Øverst til venstre",
    topRight: "Øverst til høyre",
    bottomLeft: "Nederst til venstre",
    bottomRight: "Nederst til høyre",
    profiles: "Profiler",
    profileNamePlaceholder: "Profilnavn…",
    saveProfile: "Lagre",
    loadProfile: "Last inn",
    deleteProfile: "Slett",
    noProfiles: "Ingen lagrede profiler",
    accessibilityStatementLink: "Tilgjengelighetserklæring",
    resetAll: 'Tilbakestill alt',
    closeMenu: 'Lukk meny',
    language: 'Spr\u00E5k',
    disclaimer:
      'Denne widgeten garanterer ikke full overensstemmelse med tilgjengelighetskrav.',
  },
};

// ---------------------------------------------------------------------------
// RTL language codes
// ---------------------------------------------------------------------------

/**
 * Set of language codes whose scripts are written right-to-left.
 * Maintained as a Set for O(1) look-ups and easy expansion.
 *
 * @type {Set<string>}
 */
const RTL_LANGUAGES = new Set(['he', 'ar', 'fa', 'ur']);

// ---------------------------------------------------------------------------
// Public API -- named exports
// ---------------------------------------------------------------------------

/**
 * Return the translated string for the given language and key.
 *
 * Falls back to English (`en`) when the requested language or key is not
 * found, and returns an empty string as a last resort so callers never
 * receive `undefined`.
 *
 * @param {string} lang - BCP-47 language code (e.g. "en", "he").
 * @param {string} key  - Translation key (e.g. "menuTitle").
 * @returns {string} The localised string, or the English fallback, or "".
 */
export function getTranslation(lang, key) {
  if (translations[lang] && translations[lang][key] !== undefined) {
    return translations[lang][key];
  }

  // Fallback to English
  if (translations.en && translations.en[key] !== undefined) {
    return translations.en[key];
  }

  return '';
}

/**
 * Return an array of all currently registered language codes.
 *
 * @returns {string[]} e.g. ["en", "he"]
 */
export function getAvailableLanguages() {
  return Object.keys(translations);
}

/**
 * Determine whether a language code represents a right-to-left script.
 *
 * @param {string} lang - BCP-47 language code.
 * @returns {boolean} `true` when the language is RTL.
 */
export function isRTL(lang) {
  return RTL_LANGUAGES.has(lang);
}

/**
 * Return the native name of a language (e.g. "Deutsch" for "de").
 *
 * Reads the `nativeName` key from the translation registry.  Falls back to
 * the code in upper-case when no native name is registered.
 *
 * @param {string} code - BCP-47 language code.
 * @returns {string}
 */
export function getNativeName(code) {
  return (translations[code] && translations[code].nativeName) || code.toUpperCase();
}

/**
 * Register (or overwrite) translations for a language.
 *
 * Allows consumers to add new locales at runtime without modifying the source.
 * The supplied object is **merged** into any existing registry entry for that
 * code — so you can supply only the keys that differ from English and the rest
 * will fall back gracefully via `getTranslation`.
 *
 * Call this **before** `AccessibilityWidget.init()` (or before calling
 * `setLanguage()`) so the translations are available when the widget renders.
 *
 * Required translation keys (matching the built-in `en` locale):
 * `nativeName`, `menuTitle`, `highContrast`, `darkMode`, `fontSize`,
 * `increaseFontSize`, `decreaseFontSize`, `resetFontSize`, `dyslexiaFont`,
 * `underlineLinks`, `focusOutline`, `hideImages`, `textSpacing`,
 * `pauseAnimations`, `largeCursor`, `highlightHeadings`, `invertColors`,
 * `readingGuide`, `textToSpeech`, `resetAll`, `closeMenu`, `language`,
 * `disclaimer`, `accessibilityStatementLink`.
 *
 * @param {string}                 code            - BCP-47 language code (e.g. `"it"`, `"ko"`).
 * @param {Record<string, string>} newTranslations - Key/value translation pairs.
 * @throws {Error} If `code` is not a non-empty string.
 * @throws {Error} If `newTranslations` is not a plain object.
 *
 * @example
 * import { registerLanguage } from 'free-accessibility-menu/src/i18n.js';
 *
 * registerLanguage('it', {
 *   nativeName: 'Italiano',
 *   menuTitle: 'Menu Accessibilità',
 *   resetAll: 'Ripristina tutto',
 *   closeMenu: 'Chiudi menu',
 *   // ... other keys; missing ones fall back to English
 * });
 *
 * AccessibilityWidget.init({ defaultLanguage: 'it' });
 */
export function registerLanguage(code, newTranslations) {
  if (typeof code !== 'string' || code.length === 0) {
    throw new Error('Language code must be a non-empty string.');
  }

  if (
    newTranslations === null ||
    typeof newTranslations !== 'object' ||
    Array.isArray(newTranslations)
  ) {
    throw new Error('Translations must be a plain object.');
  }

  translations[code] = { ...translations[code], ...newTranslations };
}

// ---------------------------------------------------------------------------
// Default export -- the translations object itself
// ---------------------------------------------------------------------------

export default translations;
